import React, { useState, useEffect, useRef } from 'react';
import {
    AppBar,
    Box,
    Toolbar,
    Button,
    Container,
    Grid,
    Card,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CardContent,
    Typography,
    CircularProgress,
    LinearProgress,
    Checkbox,
    IconButton,
    Select,
    MenuItem,
    TextField,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Paper,
    Snackbar,
    Chip,
    Tooltip,
    Slide,
    Fade,
    Tabs,
    Menu,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from '@mui/material';
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Edit, Check, Close, Delete } from '@mui/icons-material';
import GamepadIcon from '@mui/icons-material/Gamepad';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
// CacheProvider with RTL stylis plugin is now at the root (index.js)
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SaveIcon from '@mui/icons-material/Save';
import HistoryIcon from '@mui/icons-material/History';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import StorageIcon from '@mui/icons-material/Storage';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LinkIcon from '@mui/icons-material/Link';
import ImageIcon from '@mui/icons-material/Image';
import PollIcon from '@mui/icons-material/Poll';
import VideoCameraBackIcon from '@mui/icons-material/VideoCameraBack';
import LogoutIcon from '@mui/icons-material/Logout';
import SmsIcon from '@mui/icons-material/Sms';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockResetIcon from '@mui/icons-material/LockReset';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import PersonIcon from '@mui/icons-material/Person';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import axios from 'axios';
import { GAME_THEMES } from './gameThemes';
import './index.css';
import { API_URL } from './config';

// RTL Theme for dialogs & portals
const rtlTheme = createTheme({
  direction: 'rtl',
  typography: {
    fontFamily: "'Varela Round', 'Rubik', Arial, sans-serif",
    h4: { fontWeight: 700 },
    h5: { fontWeight: 700 },
    h6: { fontWeight: 600 },
    button: { fontWeight: 600, letterSpacing: 0 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiDialog: {
      defaultProps: {
        PaperProps: { dir: 'rtl' },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: { direction: 'rtl', fontFamily: "'Varela Round', 'Rubik', Arial, sans-serif" },
      },
    },
  },
});

// Shared dialog styles
const dialogPaperSx = {
  '& .MuiDialog-paper': {
    direction: 'rtl',
    textAlign: 'right',
    borderRadius: '20px',
    minWidth: { xs: '90vw', sm: 440 },
    boxShadow: '0 24px 80px rgba(0,0,0,0.18)',
    overflow: 'hidden',
    fontFamily: "'Varela Round', 'Rubik', Arial, sans-serif",
  },
};
// When tour is active, dialogs must appear above the tour overlay (z-index 9998-10000)
const dialogTourBoostSx = {
  '&.MuiModal-root': { zIndex: 10100 },
  '& .MuiBackdrop-root': { zIndex: 10099 },
  '& .MuiDialog-paper': { zIndex: 10101 },
};
const dialogTitleSx = {
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: '#fff',
  fontWeight: 700,
  fontSize: '1.15rem',
  fontFamily: "'Varela Round', 'Rubik', Arial, sans-serif",
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  py: 2.5,
  px: 3,
  direction: 'rtl',
  textAlign: 'right',
};
const dialogActionsSx = {
  px: 3,
  py: 2,
  gap: 1,
  justifyContent: 'flex-start',
  borderTop: '1px solid #eee',
  bgcolor: '#fafafa',
  direction: 'rtl',
};

// Custom snack styles
const snackConfig = {
  success: { emoji: '✅', bg: 'linear-gradient(135deg, #00c853 0%, #69f0ae 100%)', shadow: 'rgba(0,200,83,0.35)' },
  error: { emoji: '❌', bg: 'linear-gradient(135deg, #ff1744 0%, #ff5252 100%)', shadow: 'rgba(255,23,68,0.35)' },
  warning: { emoji: '⚠️', bg: 'linear-gradient(135deg, #ff9100 0%, #ffab40 100%)', shadow: 'rgba(255,145,0,0.35)' },
  info: { emoji: 'ℹ️', bg: 'linear-gradient(135deg, #2979ff 0%, #82b1ff 100%)', shadow: 'rgba(41,121,255,0.35)' },
};
// cacheRtl is now created in src/index.js

function App() {
    const [games, setGames] = useState([]);
    const [groups, setGroups] = useState([]);
    const [questions, setQuestions] = useState([]);
    const [selectedGame, setSelectedGame] = useState(null);
    const [selectedQuestion, setSelectedQuestion] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedType, setSelectedType] = useState(null);
    const [hoveringIndex, setHoveringIndex] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [newGameName, setNewGameName] = useState('');
    const [editGroupId, setEditGroupId] = useState(null);
const [editedName, setEditedName] = useState('');

const [loadingXL, setLoadingXL] = useState(false);
const [uploadProgress, setUploadProgress] = useState(0);
const [uploadEnd, setUploadEnd] = useState(false);

const [viewMode, setViewMode] = useState('questions'); // 'settings' או 'questions'
const [players, setPlayers] = useState([]);
const [selectedPlayer, setSelectedPlayer] = useState(null);

const [previewOpen, setPreviewOpen] = useState(false);

const [mediaFile, setMediaFile] = useState(null);
const [showUploadDialog, setShowUploadDialog] = useState(false);
const [uploadType, setUploadType] = useState(null);
const [mediaUploading, setMediaUploading] = useState(false);
const [mediaUploadProgress, setMediaUploadProgress] = useState(0);
const uploadAbortRef = useRef(null);
const newGameMobileBtnRef = useRef(null);
const newGameDesktopBtnRef = useRef(null);
  const [openPlayerDialog, setOpenPlayerDialog] = useState(false);
  const [openGroupDialog, setOpenGroupDialog] = useState(false);
  const [playerName, setPlayerName] = useState("");
  const [playerPhone, setPlayerPhone] = useState("");
  const [playerGroup, setPlayerGroup] = useState("");
  const [groupName, setGroupName] = useState("");
  const [groupCategory, setGroupCategory] = useState("");
  const [selectedGroupCategory, setSelectedGroupCategory] = useState(null);
  const [newCatMode, setNewCatMode] = useState(false);
  const [extraCategories, setExtraCategories] = useState([]);
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");

  // Package / quota state
  const [quota, setQuota] = useState(null);
  // AI question-generation state
  const [aiDlg, setAiDlg] = useState(false);
  const [aiForm, setAiForm] = useState({ topic: '', count: 5, numAnswers: 4, difficulty: 'בינונית' });
  const [aiLoading, setAiLoading] = useState(false);
  const [aiPreview, setAiPreview] = useState(null); // null=טרם נוצר
  const [aiSelected, setAiSelected] = useState({});
  const [packages, setPackages] = useState([]);
  const [showPackageDialog, setShowPackageDialog] = useState(false);
  const [pkgTab, setPkgTab] = useState('game');
  const [claimingPackage, setClaimingPackage] = useState(null);

  // Profile state
  const [showProfile, setShowProfile] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileEdit, setProfileEdit] = useState(null);
  const [profileTab, setProfileTab] = useState(0);
  const [savingProfile, setSavingProfile] = useState(false);

  // Password reset state
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetStep, setResetStep] = useState(0); // 0=idle, 1=sending code, 2=enter code, 3=new password, 4=done
  const [resetIdentifier, setResetIdentifier] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [resetMaskedPhone, setResetMaskedPhone] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  // Pre-activation dialog state
  const [showPreActivateDialog, setShowPreActivateDialog] = useState(false);
  const [preActivateInfo, setPreActivateInfo] = useState(null);
  const [preActivateLoading, setPreActivateLoading] = useState(false);
  const [smsInfo, setSmsInfo] = useState(null);
  const [smsLoading, setSmsLoading] = useState(false);
  const [smsSent, setSmsSent] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);
  const [globalPlayerInfo, setGlobalPlayerInfo] = useState(null);
  const [showPlayerLimitDialog, setShowPlayerLimitDialog] = useState(false);

  // Tour state
  const [searchParams, setSearchParams] = useSearchParams();
  const [showTour, setShowTour] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [spotRect, setSpotRect] = useState(null);

  // חזרה מתשלום UPay — עמודי התשלום מפנים ל-/panel עם transactionid.
  // מפנים לדף אימות התשלום (/Panel/endPay) שמעניק את החבילה ומודיע ללקוח.
  useEffect(() => {
    const txn = searchParams.get('transactionid') || searchParams.get('transactionId') || searchParams.get('transaction_id');
    if (txn) {
      window.location.replace(`/Panel/endPay${window.location.search}`);
    }
  }, [searchParams]);

  const tourSteps = [
    { target: null, emoji: '🎉', title: 'ברוכים הבאים לקליקרים!', desc: 'נשמח ללוות אותך בסיור אינטראקטיבי קצר 🕐\n\nנעבור יחד צעד אחרי צעד:\nניצור משחק, נוסיף שאלות, ננהל שחקנים\nונלמד איך להפעיל הכל!\n\nאפשר לדלג בכל שלב — פשוט לחצו ✕', color: '#667eea', position: 'center' },
    { key: 'new-game', target: '[data-tour="new-game-float"], [data-tour="new-game"]', emoji: '🎮', title: 'כפתור יצירת משחק', desc: 'זהו הכפתור ליצירת משחק חדש 👆\n\nלחצו "הבא" ונפתח את חלון היצירה\nכדי שתוכלו לתת שם למשחק!', color: '#10b981', position: 'auto',
      action: (ctx) => { ctx.setNewGameName('המשחק שלי'); ctx.setOpenDialog(true); },
      nextLabel: 'פתח חלון יצירה ←'
    },
    { key: 'wait-create', target: null, emoji: '✏️', title: 'יצרו משחק חדש!', desc: 'הכנסנו את השם "המשחק שלי" בשבילך ✨\n\nאפשר לשנות את השם לכל דבר שתרצו.\nלמשל: "חידון יום הולדת", "טריוויה חברים"\n\nלחצו "צור משחק" בדיאלוג ואז לחצו "הבא"!', color: '#10b981', position: 'center',
      nextLabel: 'הבא ←'
    },
    { target: '[data-tour="game-list"]', emoji: '📋', title: 'המשחק נוצר בהצלחה!', desc: 'מעולה! 🎉 המשחק שלכם מופיע ברשימה.\n\nכל משחק מציג:\n🔹 שם המשחק\n🔹 מספר שאלות ושחקנים\n🔹 סטטוס פעיל/לא פעיל\n\nלחצו "הבא" ונכנס למשחק!', color: '#3b82f6', position: 'auto',
      action: (ctx) => { const g = ctx.games[0]; if (g) ctx.handleGameClick(g); },
      nextLabel: 'נכנס למשחק ←'
    },
    { target: '[data-tour="tabs"]', emoji: '📑', title: 'לשוניות ניהול המשחק', desc: 'ברגע שנכנסים למשחק, מופיעות 3 לשוניות:\n\n📝 שאלות — יצירה ועריכה של שאלות\nעם תמונות, וידאו, מוזיקה ותשובות\n\n👥 שחקנים — הוספה ידנית, ייבוא מאקסל\nאו שהשחקנים נרשמים לבד דרך קישור\n\n⚙️ הגדרות — זמן לשאלה, ניקוד, קבוצות\nופתיחה/סגירה של הרשמה', color: '#f59e0b', position: 'auto',
      action: (ctx) => { ctx.setViewMode('questions'); },
      nextLabel: 'נעבור לשאלות ←'
    },
    { target: '[data-tour="add-question"]', emoji: '❓', title: 'הוספת שאלה', desc: 'אלה כפתורי יצירת שאלה 👆\n\n4 סוגי שאלות:\n🔹 שאלה — שאלה רגילה עם תשובות\n🔹 תמונה — שאלה עם תמונה מצורפת\n🔹 סקר — ללא תשובה נכונה\n🔹 מדיה — וידאו או מוזיקה\n\nלחצו על אחד מהם כדי להוסיף שאלה!\nאחרי שתוסיפו — לחצו "הבא"', color: '#8b5cf6', position: 'auto',
      nextLabel: 'הבא ←'
    },
    { target: '[data-tour="tabs"]', emoji: '✅', title: 'עריכת שאלה', desc: 'אחרי שהוספתם שאלה:\n\n1️⃣ לחצו עליה ברשימה בצד ימין\n2️⃣ שנו את טקסט השאלה\n3️⃣ הוסיפו תשובות (מינימום 2)\n4️⃣ סמנו את התשובה הנכונה ⭐\n5️⃣ אפשר להוסיף תמונה או וידאו!\n\n💡 טיפ: אפשר לגרור שאלות\nכדי לשנות את הסדר שלהן\n\nלחצו "הבא" ונעבור לשחקנים!', color: '#8b5cf6', position: 'auto',
      action: (ctx) => { ctx.setViewMode('players'); },
      nextLabel: 'לשונית שחקנים ←'
    },
    { target: '[data-tour="add-player"]', emoji: '👥', title: 'הוספת שחקן', desc: 'זהו כפתור הוספת שחקן 👆\n\nאפשר להוסיף שחקנים ב-3 דרכים:\n➕ ידנית — שם וטלפון\n📊 ייבוא מאקסל — העלו קובץ\n🔗 הרשמה עצמית — שלחו קישור\n\nכל שחקן יכול להשתתף דרך:\n📱 אפליקציית ווב (מכשיר חכם)\n📞 שיחת טלפון (חיוג למספר)\n\nנסו ללחוץ "הוסף שחקן" ואז "הבא"!', color: '#ec4899', position: 'auto',
      nextLabel: 'הבא ←'
    },
    { target: '[data-tour="tabs"]', emoji: '⚙️', title: 'הגדרות המשחק', desc: 'עכשיו נעבור להגדרות!\n\nבהגדרות אפשר לשנות:\n⏱️ זמן תשובה — כמה שניות לכל שאלה\n🏆 ניקוד — כמה נקודות על תשובה נכונה\n🔓 הרשמה — פתוח/סגור לשחקנים חדשים\n👥 קבוצות — חלוקה לצוותים מתחרים\n📱 מכשיר חכם — אפשר/חסום כניסה מווב', color: '#06b6d4', position: 'auto',
      action: (ctx) => { ctx.setViewMode('settings'); },
      nextLabel: 'לשונית הגדרות ←'
    },
    { target: '[data-tour="activate-game"]', emoji: '🚀', title: 'הפעלת המשחק!', desc: 'זהו כפתור הפעלת המשחק 👆\n\nכשתלחצו עליו:\n1️⃣ המערכת תבדוק שיש חבילה\n2️⃣ תקצה מספר טלפון\n3️⃣ תראה לכם את כל פרטי ההתחברות\n4️⃣ תוכלו לשלוח SMS לשחקנים!\n\n📦 צריך חבילה? לחצו "חבילות"\nבעמוד הראשי — יש חינמית לניסיון 🎁', color: '#7c3aed', position: 'auto',
      action: (ctx) => { ctx.setViewMode('questions'); },
      nextLabel: 'סיום ←'
    },
    { target: null, emoji: '🎊', title: 'מוכנים להתחיל!', desc: 'מעולה! עכשיו אתם מכירים הכל 🎉\n\n📌 סיכום מה שלמדנו:\n✅ יצירת משחק\n✅ הוספת שאלות ועריכה\n✅ ניהול שחקנים\n✅ הגדרות המשחק\n✅ הפעלת המשחק\n\nעכשיו רק נשאר:\n➕ הוסיפו עוד שאלות\n👥 הוסיפו שחקנים\n📦 רכשו חבילה\n🎮 והפעילו!\n\nבהצלחה! 💪', color: '#667eea', position: 'center' },
  ];

  // Tour action handler — performs real UI actions when pressing "next"
  const handleTourNext = async () => {
    const step = tourSteps[tourStep];
    if (step?.action) {
      await step.action({
        setOpenDialog,
        setNewGameName,
        handleCloseDialog,
        handleCreateGame,
        handleGameClick,
        handleAddQuestion,
        setViewMode,
        setShowPackageDialog,
        games,
        selectedGame,
      });
      // Delay to let the action take effect and state updates propagate
      await new Promise(r => setTimeout(r, 500));
    }
    setTourStep(s => s + 1);
  };

  // Mobile state (must be before tour useEffect that uses isMobile)
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  useEffect(() => {
    if (!showTour) { setSpotRect(null); return; }
    const step = tourSteps[tourStep] || tourSteps[0];
    if (!step.target) { setSpotRect(null); return; }
    // Prevent using previous step rectangle while new step is being measured
    setSpotRect(null);
    let el = null;

    // שלב יצירת משחק: יעד מדויק לפי מצב מובייל/דסקטופ
    if (step.key === 'new-game') {
      const preferred = isMobile ? newGameMobileBtnRef.current : newGameDesktopBtnRef.current;
      const r = preferred?.getBoundingClientRect?.();
      if (preferred && r && r.width > 0 && r.height > 0) {
        el = preferred;
      }
    }

    // ברירת מחדל לכל שאר השלבים (או fallback אם ה-ref לא זמין)
    if (!el) {
      const candidates = Array.from(document.querySelectorAll(step.target));
      el = candidates.find((node) => {
        const r = node.getBoundingClientRect();
        return r.width > 0 && r.height > 0;
      }) || null;
    }

    if (el) {
      const updateRect = () => {
        const r = el.getBoundingClientRect();
        setSpotRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      };

      const pos = window.getComputedStyle(el).position;
      if (pos !== 'fixed') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        requestAnimationFrame(updateRect);
        setTimeout(updateRect, 260);
      } else {
        updateRect();
      }
    } else {
      // Fallback לשלב יצירת משחק (כדי שלא יקפוץ לצד הלא נכון)
      if (step.key === 'new-game') {
        setSpotRect({ top: window.innerHeight - 90, left: window.innerWidth - 270, width: 230, height: 52 });
      } else {
        setSpotRect(null);
      }
    }
  }, [showTour, tourStep, isMobile]);

  // Mobile state (moved above tour useEffect)
  const [mobileEditorOpen, setMobileEditorOpen] = useState(false);
  const [mobilePlayerEditorOpen, setMobilePlayerEditorOpen] = useState(false);
  const [addQuestionMenuAnchor, setAddQuestionMenuAnchor] = useState(null);

  // Snackbar notifications
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const showSnack = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  // Confirm dialog
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', text: '', onConfirm: null });
  const askConfirm = (title, text, onConfirm) => setConfirmDialog({ open: true, title, text, onConfirm });
    const navigate = useNavigate();
    const { userId: paramUserId } = useParams()
    // JWT: קבלת userId מטוקן או מה-URL (תאימות לאחור)
    const getToken = () => localStorage.getItem('token');
    const getAuthHeaders = () => ({
      'Content-Type': 'application/json',
      ...(getToken() ? { 'Authorization': `Bearer ${getToken()}` } : {}),
    });
    const [userId, setUserId] = useState(() => {
      // נסה מ-URL params קודם, אם אין — מ-localStorage
      if (paramUserId) return paramUserId;
      try {
        const u = JSON.parse(localStorage.getItem('user'));
        return u?.user_id || '';
      } catch { return ''; }
    });

    // בדיקת אימות בעת טעינה
    useEffect(() => {
      const token = getToken();
      if (!token && !paramUserId) {
        navigate('/login');
        return;
      }
      // אם יש טוקן אבל אין userId — שלוף מ-/me
      if (token && !userId) {
        fetch(`${API_URL}/me`, { headers: { 'Authorization': `Bearer ${token}` } })
          .then(r => r.json())
          .then(d => {
            if (d.status === 'success') {
              setUserId(d.user.user_id);
              localStorage.setItem('user', JSON.stringify(d.user));
            } else {
              localStorage.removeItem('token');
              localStorage.removeItem('user');
              navigate('/login');
            }
          })
          .catch(() => { navigate('/login'); });
      }
    }, []);

    // בדיקה אם להציג סיור למשתמש חדש
    useEffect(() => {
      if (searchParams.get('tour') === '1') {
        setShowTour(true);
        setTourStep(0);
        // ניקוי הפרמטר מה-URL
        searchParams.delete('tour');
        setSearchParams(searchParams, { replace: true });
      }
    }, []);

    // פונקציית התנתקות
    const handleLogout = () => {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      navigate('/login');
    };

    useEffect(() => {
      if (!userId) return;
        const fetchGames = async () => {
            try {
                const response = await fetch(`${API_URL}/get_games/${userId}`, {
                  headers: getAuthHeaders(),
                });
                const data = await response.json();
                if (data.status){
                    setGames(data.games);
                } else {
                    handleLogout();
                }
            } catch (error) {
                setError('שגיאה בטעינת המשחקים');
            } finally {
                setLoading(false);
            }
        };

        const fetchQuota = async () => {
            try {
                const r = await fetch(`${API_URL}/user_quota/${userId}`, {
                  headers: getAuthHeaders(),
                });
                const d = await r.json();
                if (d.status) setQuota(d.quota);
            } catch {}
        };

        const fetchPackages = async () => {
            try {
                const r = await fetch(`${API_URL}/packages`);
                const d = await r.json();
                if (d.status) setPackages(d.packages);
            } catch {}
        };

        fetchGames();
        fetchQuota();
        fetchPackages();

        // טעינת פרופיל (כולל רכישות) לבדיקת חבילת הדגמה
        (async () => {
          try {
            const r = await fetch(`${API_URL}/user_profile/${userId}`, { headers: getAuthHeaders() });
            const d = await r.json();
            if (d.status) { setProfileData(d); setProfileEdit({ ...d.profile }); }
          } catch {}
        })();
    }, [userId]);

  const fetchProfile = async () => {
    setProfileLoading(true);
    try {
      const r = await fetch(`${API_URL}/user_profile/${userId}`, {
        headers: getAuthHeaders(),
      });
      const d = await r.json();
      if (d.status) {
        setProfileData(d);
        setProfileEdit({ ...d.profile });
      }
    } catch {}
    setProfileLoading(false);
  };

  const handleSaveProfile = async () => {
    if (!profileEdit) return;
    setSavingProfile(true);
    try {
      const r = await fetch(`${API_URL}/update_profile/${userId}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          first_name: profileEdit.first_name,
          last_name: profileEdit.last_name,
          phone: profileEdit.phone,
          email: profileEdit.email,
        }),
      });
      const d = await r.json();
      if (d.status) {
        showSnack('הפרופיל עודכן בהצלחה');
        fetchProfile();
      } else {
        showSnack(d.error || 'שגיאה בעדכון', 'error');
      }
    } catch {
      showSnack('שגיאה בעדכון', 'error');
    }
    setSavingProfile(false);
  };

  const handleOpenProfile = () => {
    setSelectedGame(null);
    setShowProfile(true);
    setProfileTab(0);
    fetchProfile();
  };
const handleOpenDialog = () => {
  setNewGameName('');
  setOpenDialog(true);
};

const handleCloseDialog = () => {
  setOpenDialog(false);
};
const handleDeleteGame = async (gameId, gameName) => {
  askConfirm('מחיקת משחק', `האם אתה בטוח שברצונך למחוק את "${gameName}"? כל השאלות והשחקנים יימחקו.`, async () => {
    try {
      const response = await fetch(`${API_URL}/delete_game`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ id: gameId, user_id: userId }),
      });

      const data = await response.json();
      if (data.status) {
        const gamesRes = await fetch(`${API_URL}/get_games/${userId}`, {
          headers: getAuthHeaders(),
        });
        const gamesData = await gamesRes.json();
        setGames(gamesData.games);
        setSelectedGame(null);
        showSnack('המשחק נמחק בהצלחה');
      } else {
        showSnack('שגיאה במחיקת המשחק', 'error');
        setSelectedGame(null);
      }
    } catch (err) {
      console.error('שגיאה במחיקת משחק:', err);
      showSnack('שגיאה כללית', 'error');
    }
    setConfirmDialog({ open: false });
  });
};

const handleCreateGame = async () => {
  if (!newGameName.trim()) {
    showSnack('נא להזין שם למשחק', 'warning');
    return;
  }

  try {
    const response = await fetch(`${API_URL}/new_game`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ name: newGameName, user_id: userId }),
    });

    const data = await response.json();
    if (data.status) {
      handleCloseDialog();
      const gamesRes = await fetch(`${API_URL}/get_games/${userId}`, {
        headers: getAuthHeaders(),
      });
      const gamesData = await gamesRes.json();
      setGames(gamesData.games);
      setSelectedGame(null);
      showSnack('משחק נוצר בהצלחה! 🎮');
    } else {
      showSnack('שגיאה ביצירת המשחק', 'error');
      handleCloseDialog();
      setSelectedGame(null);
    }
  } catch (err) {
    console.error('שגיאה ביצירת משחק:', err);
    showSnack('שגיאה כללית', 'error');
    handleCloseDialog();
    setSelectedGame(null);
  }
};

    const handleGameClick = async (game) => {
        setSelectedGame(game);
        setQuestions([]);
        setLoading(true);

        try {
            const playersRes = await fetch(`${API_URL}/get_players/${game.id}`, {
              headers: getAuthHeaders(),
            });
            const playersData = await playersRes.json();
            setPlayers(playersData.players || []);
            const response = await fetch(`${API_URL}/get_questions/${game.id}`, {
              headers: getAuthHeaders(),
            });
            const data = await response.json();
            setQuestions(data);
            const response33 = await fetch(`${API_URL}/get_groups/${game.id}`, {
              headers: getAuthHeaders(),
            });
            const data33 = await response33.json();
            setGroups(data33.groups || []);
        } catch (error) {
            setError('שגיאה בטעינת השאלות');
        } finally {
            setLoading(false);
        }
    };
    const refresh = async () => {

        try {
            const response = await fetch(`${API_URL}/get_questions/${selectedGame.id}`, {
              headers: getAuthHeaders(),
            });
            const data = await response.json();
            setQuestions(data);
        } catch (error) {
            setError('שגיאה בטעינת השאלות');
        }
    };

    // ===== יצירת שאלות ב-AI (Gemini) =====
    const generateAI = async () => {
      if (!aiForm.topic.trim()) { showSnack('יש להזין נושא לשאלות', 'error'); return; }
      setAiLoading(true); setAiPreview(null);
      try {
        const r = await fetch(`${API_URL}/ai_generate_questions`, {
          method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(aiForm),
        });
        const d = await r.json();
        if (d.error) { showSnack(d.message || 'שגיאה ביצירת השאלות', 'error'); setAiLoading(false); return; }
        const qs = d.questions || [];
        setAiPreview(qs);
        const sel = {}; qs.forEach((_, i) => { sel[i] = true; });
        setAiSelected(sel);
        if (typeof d.aiCredits === 'number') setQuota(prev => prev ? { ...prev, aiCredits: d.aiCredits } : prev);
        showSnack(`נוצרו ${d.produced} שאלות — סמן ואשר להוספה`);
      } catch (e) { showSnack('שגיאה ביצירת השאלות', 'error'); }
      setAiLoading(false);
    };

    const addAIQuestions = async () => {
      const chosen = (aiPreview || []).filter((_, i) => aiSelected[i]);
      if (!chosen.length) { showSnack('לא נבחרו שאלות', 'error'); return; }
      try {
        const r = await fetch(`${API_URL}/ai_add_questions`, {
          method: 'POST', headers: getAuthHeaders(),
          body: JSON.stringify({ game_id: selectedGame.id, questions: chosen }),
        });
        const d = await r.json();
        if (d.error) { showSnack(d.message || 'שגיאה בהוספה', 'error'); return; }
        showSnack(`נוספו ${chosen.length} שאלות למשחק`);
        setAiDlg(false); setAiPreview(null);
        refresh();
      } catch (e) { showSnack('שגיאה בהוספת השאלות', 'error'); }
    };

    // דיאלוג יצירת שאלות ב-AI — מוגדר פעם אחת ומוצג גם במובייל וגם בדסקטופ
    const aiDialog = (
      <Dialog open={aiDlg} onClose={() => !aiLoading && setAiDlg(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 4 } }}>
        <DialogTitle sx={{ fontWeight: 900, display: 'flex', alignItems: 'center', gap: 1 }}>
          ✨ יצירת שאלות ב-AI
          <Chip size="small" label={`קרדיטים: ${quota?.aiCredits ?? 0}`} sx={{ ml: 'auto', fontWeight: 700, background: '#ede9fe', color: '#6d28d9' }} />
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <TextField label="נושא השאלות" value={aiForm.topic} onChange={e => setAiForm({ ...aiForm, topic: e.target.value })}
              sx={{ flex: '1 1 260px' }} autoFocus placeholder="למשל: היסטוריה של עם ישראל" />
            <TextField label="כמות שאלות" type="number" value={aiForm.count}
              onChange={e => setAiForm({ ...aiForm, count: Math.min(20, Math.max(1, parseInt(e.target.value) || 1)) })}
              sx={{ width: 120 }} inputProps={{ min: 1, max: 20 }} />
            <TextField label="מס' תשובות" type="number" value={aiForm.numAnswers}
              onChange={e => setAiForm({ ...aiForm, numAnswers: Math.min(4, Math.max(2, parseInt(e.target.value) || 4)) })}
              sx={{ width: 120 }} inputProps={{ min: 2, max: 4 }} />
            <TextField label="רמת קושי" select value={aiForm.difficulty}
              onChange={e => setAiForm({ ...aiForm, difficulty: e.target.value })} sx={{ width: 140 }}>
              {['קלה', 'בינונית', 'קשה'].map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
            </TextField>
            <Button variant="contained" onClick={generateAI} disabled={aiLoading}
              sx={{ borderRadius: '30px', px: 3, fontWeight: 800, background: 'linear-gradient(135deg,#a855f7,#6366f1)', alignSelf: 'center' }}>
              {aiLoading ? 'יוצר…' : '✨ צור'}
            </Button>
          </Box>

          {aiLoading && <LinearProgress sx={{ borderRadius: 2, mb: 2 }} />}

          {aiPreview && aiPreview.length > 0 && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography sx={{ fontWeight: 800 }}>תצוגה מקדימה ({aiPreview.filter((_, i) => aiSelected[i]).length}/{aiPreview.length} נבחרו)</Typography>
                <Button size="small" sx={{ ml: 'auto', fontWeight: 700 }}
                  onClick={() => { const all = aiPreview.every((_, i) => aiSelected[i]); const s = {}; aiPreview.forEach((_, i) => s[i] = !all); setAiSelected(s); }}>
                  {aiPreview.every((_, i) => aiSelected[i]) ? 'בטל הכל' : 'בחר הכל'}
                </Button>
              </Box>
              {aiPreview.map((q, i) => {
                const answers = ['a', 'b', 'c', 'd'].map(l => q[l]).filter(v => v != null);
                return (
                  <Box key={i} sx={{ border: '1px solid #eee', borderRadius: 3, p: 1.5, mb: 1, background: aiSelected[i] ? '#faf5ff' : '#fafafa' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Checkbox checked={!!aiSelected[i]} onChange={e => setAiSelected({ ...aiSelected, [i]: e.target.checked })} sx={{ p: .5 }} />
                      <Box sx={{ flex: 1 }}>
                        <TextField fullWidth variant="standard" value={q.q}
                          onChange={e => { const p = [...aiPreview]; p[i] = { ...p[i], q: e.target.value }; setAiPreview(p); }}
                          InputProps={{ sx: { fontWeight: 700 } }} />
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: .8, mt: 1 }}>
                          {answers.map((ans, ai) => (
                            <Chip key={ai} label={ans} size="small"
                              sx={{ fontWeight: 600, background: (q.correctAnswer === ai + 1) ? '#dcfce7' : '#f1f5f9', color: (q.correctAnswer === ai + 1) ? '#15803d' : '#475569', border: (q.correctAnswer === ai + 1) ? '1px solid #86efac' : 'none' }} />
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
          {aiPreview && aiPreview.length === 0 && (
            <Typography sx={{ color: '#94a3b8', textAlign: 'center', py: 2 }}>לא נוצרו שאלות. נסה שוב.</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAiDlg(false)} disabled={aiLoading}>סגור</Button>
          {aiPreview && aiPreview.length > 0 && (
            <Button variant="contained" onClick={addAIQuestions}
              sx={{ borderRadius: '30px', px: 3, fontWeight: 800, background: 'linear-gradient(135deg,#22c55e,#16a34a)' }}>
              ➕ הוסף נבחרות למשחק
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );

  const handleAdd = async (gameId) => {
    if (!playerName || !playerPhone) {
      showSnack("נא למלא שם וטלפון", "warning");
      return;
    }

    const response = await fetch(`${API_URL}/add_player`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({ game_id: gameId, name: playerName, phone: playerPhone, group_id: playerGroup ? playerGroup : "" }),
    });

    const data = await response.json();

    if (response.ok) {
      setPlayers(prev => [data.player, ...prev]);
      setOpenPlayerDialog(false);
      setPlayerName("");
      setPlayerPhone("");
      setPlayerGroup("");
      showSnack("שחקן נוסף בהצלחה!");
    } else {
      showSnack("שגיאה: " + data.error, "error");
    }
  };
  const handleAddGroup = async (gameId) => {
    if (!groupName) {
      showSnack("נא למלא שם קבוצה", "warning");
      return;
    }

    const response = await fetch(`${API_URL}/add_group`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({ game_id: gameId, name: groupName, category: groupCategory }),
    });

    const data = await response.json();

    if (response.ok) {
      setGroups(prev => [...prev, data.group]);
      setOpenGroupDialog(false);
      setGroupName("");
      setGroupCategory("");
      setNewCatMode(false);
      showSnack("קבוצה נוספה בהצלחה!");
    } else {
      showSnack("שגיאה: " + data.error, "error");
    }
  };
  const deleteGroup = async (gameId, groupId) => {
  try {
    const response = await fetch(`${API_URL}/delete_group`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ game_id: gameId, group_id: groupId }),
    });
    const data = await response.json();
    if (data.success) {
      setGroups(prevGroups => prevGroups.filter(group => group.id !== groupId));
      showSnack('קבוצה נמחקה בהצלחה');
    } else {
      showSnack('שגיאה במחיקת הקבוצה', 'error');
    }
  } catch (err) {
    showSnack('שגיאה במחיקת הקבוצה', 'error');
  }
}
  const saveEditGroup = async (gameId, group_id, newName) => {
    if (!newName) {
      showSnack("נא למלא שם קבוצה", "warning");
      return;
    }

    const response = await fetch(`${API_URL}/update_group`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({ game_id: gameId, group_id, newName }),
    });

    const data = await response.json();

    if (response.ok) {
      setGroups((prev) =>
        prev.map((g) =>
          g.id === group_id ? { ...g, name: newName } : g
        )
      );
      showSnack("שם הקבוצה עודכן בהצלחה");
    } else {
      showSnack("שגיאה: " + data.error, "error");
    }
  };

const handleImportPlayers = async (e) => {
  const file = e.target.files[0];
  const text = await file.text();
  const newPlayers = JSON.parse(text);
  for (let p of newPlayers) {
    await fetch(`${API_URL}/add_player`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ game_id: selectedGame.id, ...p }),
    });
  }
  const updated = await fetch(`${API_URL}/get_players/${selectedGame.id}`, {
    headers: getAuthHeaders(),
  });
  const updatedData = await updated.json();
  setPlayers(updatedData.players || []);
};

const handleDeletePlayer = async (player) => {
  askConfirm('מחיקת שחקן', `האם אתה בטוח שברצונך למחוק את ${player.name}?`, async () => {
    await fetch(`${API_URL}/delete_player`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ game_id: selectedGame.id, phone: player.phone }),
    });
    setPlayers(players.filter(p => p.phone !== player.phone));
    if (selectedPlayer?.phone === player.phone) setSelectedPlayer(null);
    showSnack('שחקן נמחק בהצלחה');
    setConfirmDialog({ open: false });
  });
};

const handleTogglePlayerDisabled = async (player) => {
  const newDisabled = !player.disabled;
  try {
    await fetch(`${API_URL}/toggle_player_disabled`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ game_id: selectedGame.id, phone: player.phone, disabled: newDisabled }),
    });
    setPlayers(players.map(p => p.phone === player.phone ? { ...p, disabled: newDisabled } : p));
    showSnack(newDisabled ? 'שחקן הושבת' : 'שחקן הופעל');
  } catch {
    showSnack('שגיאה בעדכון שחקן');
  }
};

    const handleDelete = async (id) => {
      askConfirm('מחיקת שאלה', 'האם אתה בטוח שברצונך למחוק את השאלה?', async () => {
        try {
            const response = await fetch(`${API_URL}/delete_question`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    id: id,
                    game_id: selectedGame.id,
                }),
            });
            setSelectedQuestion()
            const updatedQuestions = questions.filter((q) => q.id !== id);
            const newQuestions = updatedQuestions.map((question, index) => ({
                ...question,
                id: index + 1,
            }));
            setQuestions(newQuestions);
            refresh();
            showSnack('שאלה נמחקה בהצלחה');
        } catch (error) {
            showSnack('שגיאה במחיקת השאלה', 'error');
        }
        setConfirmDialog({ open: false });
      });
    };


    // פונקציה להוספת שאלה
    const addQuestionToAPI = async (newQuestion) => {
        try {
            const response = await fetch(`${API_URL}/addQ`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    game_id: selectedGame.id,  // תחליף ב-ID של המשחק
                    q: newQuestion.q,  // השאלה
                    a: newQuestion.answers[0],  // תשובה א'
                    b: newQuestion.answers[1],  // תשובה ב'
                    c: newQuestion.answers[2],  // תשובה ג'
                    type: newQuestion.type,
                    link: newQuestion.link || "",
                    correctAnswer: newQuestion.correctAnswer,
                    ...(newQuestion.type === 'g' ? { answerGroups: newQuestion.answerGroups || [] } : {})
                }),


            });

            if (response.ok) {
                const result = await response.json();
                setSelectedQuestion(result.qAdd)
                refresh()
                showSnack('שאלה נוספה בהצלחה ✅');
            } else {
                showSnack('שגיאה בהוספת שאלה', 'error');
            }
        } catch (error) {
            showSnack('שגיאה בהוספת שאלה', 'error');
        }
    };
    console.log(selectedQuestion);
    
    const addAwsToAPI = async () => {
        try {
            const response = await fetch(`${API_URL}/addAWS`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    game_id: selectedGame.id,
                    id: selectedQuestion.id
                }),
            });
    
            if (response.ok) {
                const result = await response.json();
                setSelectedQuestion((prev) => ({
                    ...prev,
                    answers: [...prev.answers, ""] // מוסיף תשובה ריקה במקום לאפס
                }));
                refresh();
            } else {
                console.error('Error adding question:', response.statusText);
            }
        } catch (error) {
            console.error('Error sending request:', error);
        }
    };
    

    //שינוי תשובה נכונה
    const handleChangeCorrectAnswer = (newCorrect) => {
        setSelectedQuestion((prev) => ({
            ...prev,
            correctAnswer: newCorrect,
        }));
        updateData("correctAnswer", newCorrect)
    };
    // שיוך קטגוריית קבוצות לשאלה — התשובות הופכות אוטומטית לקבוצות שבקטגוריה
    const applyGroupCategory = (cat) => {
        const catGroups = groups.filter(g => (g.category || "") === cat).sort((a, b) => a.id - b.id);
        const names = catGroups.map(g => g.name);
        const answerGroups = catGroups.map(g => g.id);
        setSelectedQuestion(prev => ({ ...prev, answers: names, answerGroups, groupCategory: cat }));
        setQuestions(prev => prev.map(q => q.id === selectedQuestion.id ? { ...q, answers: names, answerGroups, groupCategory: cat } : q));
        ["a", "b", "c", "d"].forEach((f, i) => updateData(f, names[i] || ""));
        updateData("answerGroups", answerGroups);
        updateData("groupCategory", cat);
    };
    // שיוך תשובה לקבוצה (שאלת קבוצה — טייפ g)
    const handleChangeAnswerGroup = (index, groupId) => {
        const arr = [...(selectedQuestion.answerGroups || [])];
        while (arr.length <= index) arr.push("");
        arr[index] = groupId;
        setSelectedQuestion((prev) => ({ ...prev, answerGroups: arr }));
        updateData("answerGroups", arr);
    };
    const handleChangeQuestionType = (newType) => {
        setSelectedQuestion((prev) => ({
            ...prev,
            type: newType,
        }));
setQuestions((prevQuestions) =>
  prevQuestions.map((q) =>
    q.id === selectedQuestion.id ? { ...q, type: newType } : q
  )
);

        updateData("type", newType)

    };

    const updateData = async (type, value) => {
        // עדכון מיידי ברשימת השאלות ב-state כדי שלא יאבד בניווט
        setQuestions((prevQuestions) =>
            prevQuestions.map((q) =>
                q.id === selectedQuestion.id ? { ...q, [type]: value } : q
            )
        );

        try {
            // שליחה ל-API לעדכון השאלה
            const response = await fetch(`${API_URL}/updateData`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    game_id: selectedGame.id,
                    id: selectedQuestion.id,
                    type: type,
                    value: value
                }),
            });

            if (response.ok) {
                const result = await response.json();
                showSnack('נשמר בהצלחה');
            } else {
                console.error('Error updating question:', response.statusText);
                showSnack('שגיאה בשמירה', 'error');
            }
        } catch (error) {
            console.error('Error sending request:', error);
            showSnack('שגיאה בשמירה', 'error');
        }
    };


    // פונקציה להעתקת שאלה חדשה
    const handleCopy = (question) => {
        const newQuestion = { ...question, id: (questions.length + 1).toString() };
        setQuestions([...questions, newQuestion]);

        // שליחה ל-API של השאלה החדשה
        addQuestionToAPI(newQuestion);
    };

    const handleDragEnd = (result) => {
        const { destination, source } = result;

        // אם לא היעד חוקי, לא עושים כלום
        if (!destination) return;

        // אם השאלה לא הוזזה (ממקם מחדש אותה במקום שלה)
        if (destination.index === source.index) return;

        const updatedQuestions = Array.from(questions); // יצירת עותק של המערך
        const [removed] = updatedQuestions.splice(source.index, 1); // מסירים את השאלה ממיקום המקורי
        updatedQuestions.splice(destination.index, 0, removed); // מכניסים אותה למיקום החדש

        // עכשיו נשלח את הסדר החדש ל-API
        updateQuestionsOrder(updatedQuestions);

    };
    const updateQuestionsOrder = async (updatedQuestions) => {
        // עדכון ה-state של השאלות עם הסדר החדש
        const updatedQuestionsWithNewOrder = updatedQuestions.map((question, index) => ({
            ...question,
            id: index + 1,  // עדכון ה-ID לכל שאלה לפי הסדר החדש
        }));

        // עדכון ה-state של השאלות (questions) עם הסדר המעודכן
        setQuestions(updatedQuestionsWithNewOrder);
        try {
            const response = await fetch(`${API_URL}/updateIDS`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    questions: updatedQuestions.map((question) => question.id),
                    game_id: selectedGame.id,
                }),
            });

            if (response.ok) {
                const result = await response.json();
                console.log('Order updated successfully:', result);
                refresh()


            } else {
                console.error('Error updating order:', response.statusText);
            }
        } catch (error) {
            console.error('Error sending request:', error);
        }
    };



    const handleDeleteAnswer = async (index) => {
        if (selectedQuestion.answers.length <= 2) {
            showSnack('חייבות להיות לפחות 2 תשובות', 'warning');
            return;
        }
        const updatedAnswers = selectedQuestion.answers.filter((_, i) => i !== index);
        // עדכון correctAnswer אם צריך
        let newCorrect = selectedQuestion.correctAnswer;
        if (newCorrect === index + 1) newCorrect = 1;
        else if (newCorrect > index + 1) newCorrect = newCorrect - 1;

        setSelectedQuestion((prev) => ({ ...prev, answers: updatedAnswers, correctAnswer: newCorrect }));
        setQuestions((prevQuestions) =>
            prevQuestions.map((q) => q.id === selectedQuestion.id ? { ...q, answers: updatedAnswers, correctAnswer: newCorrect } : q)
        );
        try {
            const response = await fetch(`${API_URL}/deleteAWS`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    game_id: selectedGame.id,
                    id: selectedQuestion.id,
                    answerIndex: index
                }),
            });
            if (response.ok) {
                showSnack('תשובה נמחקה ✅');
            } else {
                showSnack('שגיאה במחיקת תשובה', 'error');
                refresh();
            }
        } catch (error) {
            showSnack('שגיאה במחיקת תשובה', 'error');
            refresh();
        }
    };

    const handleEdit = async (field, value) => {
        if (selectedQuestion) {
            // יצירת עותק של השאלה הנבחרת
            let updatedQuestion = {
                ...selectedQuestion,
                fieldIndex: selectedQuestion.fieldIndex ? selectedQuestion.fieldIndex : 1 // אם אין fieldIndex, נותנים לו 1
            };

            // אם השדה שצריך לעדכן הוא answers, אנחנו צריכים לשנות את התשובה במערך
            if (field !== 'q') {
                // value כאן הוא המיקום במערך, נעדכן את התשובה במיקום הזה
                const updatedAnswers = [...selectedQuestion.answers]; // יצירת עותק של המערך
                updatedAnswers[field] = value; // שינוי התשובה במיקום שנבחר
                field = field + 1
                updatedQuestion = { ...updatedQuestion, answers: updatedAnswers };
            } else {
                updatedQuestion = { ...updatedQuestion, [field]: value };
            }

            // עדכון ה-state של השאלה הנבחרת
            setSelectedQuestion(updatedQuestion);

            // עדכון כל השאלות עם השאלה המעודכנת במערך
            setQuestions((prevQuestions) =>
                prevQuestions.map((q) => (q.id === updatedQuestion.id ? updatedQuestion : q))
            );

            try {
                // שליחה ל-API לעדכון השאלה
                const response = await fetch(`${API_URL}/updateQ`, {
                    method: 'POST',
                    headers: getAuthHeaders(),
                    body: JSON.stringify({
                        game_id: selectedGame.id,
                        id: updatedQuestion.id,
                        field: field,
                        value: value
                    }),
                });

                if (response.ok) {
                    const result = await response.json();
                    showSnack('נשמר בהצלחה ✅');
                } else {
                    showSnack('שגיאה בשמירה', 'error');
                }
            } catch (error) {
                showSnack('שגיאה בשמירה', 'error');
            }
        }
    };

const handleAddQuestion = (type) => {


  // רגיל או סקר
const newQuestion = {
  q: "שאלה חדשה",
  answers: ["", ""],
  type,
  correctAnswer: type === 's' ? 'Q' : type === 'g' ? 'G' : 1,
  ...(type === 'g' ? { answerGroups: ["", ""] } : {}),
  link: (type === 'p' || type === 'm') ? "https://lawyerrafi.com/placeholder.svg" : ""
};
  addQuestionToAPI(newQuestion);
};

    // ריבועים לבחירת סוג השאלה
    const questionTypes = [
        { type: 'q', icon: <PollIcon sx={{ color: '#FF5733' }} />, label: 'שאלה' },
        { type: 'p', icon: <ImageIcon sx={{ color: '#33FF57' }} />, label: 'תמונה' },
        { type: 's', icon: <PollIcon sx={{ color: '#3357FF' }} />, label: 'סקר' },
        { type: 'm', icon: <VideoCameraBackIcon sx={{ color: '#FF33A6' }} />, label: 'מדיה' },
        { type: 'g', icon: <GroupAddIcon sx={{ color: '#a855f7' }} />, label: 'שאלת קבוצה' },
    ];
const [gameSettings, setGameSettings] = useState({
  isOpen: true,
  isGroups: false,
  defaultPoints: 10, 
  answerTime: 30,
  allowSmartDevice: true
});
useEffect(() => {
  if (selectedGame && typeof selectedGame === 'object') {
    setGameSettings({
      isOpen: selectedGame.isOpen ?? true,
      isGroups: selectedGame.isGroups ?? false,
      defaultPoints: selectedGame.defaultPoints ?? 10,
      answerTime: selectedGame.answerTime ?? 30,
      allowSmartDevice: selectedGame.allowSmartDevice ?? true,
    });
  }
}, [selectedGame]);

const handleExportPlayers = async () => {
  if (!players || players.length === 0) {
    showSnack('אין שחקנים לייצא', 'warning');
    return;
  }

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('שחקנים');
  worksheet.columns = [
    { header: 'שם', key: 'name', width: 25 },
    { header: 'טלפון', key: 'phone', width: 15 },
    { header: 'נקודות', key: 'points', width: 12 },
  ];
  players.forEach((p) => {
    worksheet.addRow({ name: p.name || '', phone: p.phone || '', points: p.point || 0 });
  });

  const excelBuffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  saveAs(blob, 'players_export.xlsx');
};
const handleImportQuestions = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  const formData = new FormData();
  formData.append('file', file);
  formData.append('game_id', selectedGame.id);
  formData.append('user_id', userId);

  setLoadingXL(true);
  setUploadProgress(0);

  try {
    const response = await axios.post(`${API_URL}/importQuestions`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${getToken()}`,
      },
      onUploadProgress: (progressEvent) => {
        const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(percent);
      },
    });
setUploadEnd(true);

setTimeout(() => {
  setLoadingXL(false);
  setUploadProgress(0);
}, 2000);

  } catch (error) {
    console.error('Error importing file:', error);
    showSnack('שגיאה בייבוא הקובץ', 'error');
  }
};
const handleImportPlayers2 = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  const formData = new FormData();
  formData.append('file', file);
  formData.append('game_id', selectedGame.id);
  formData.append('user_id', userId);

  setLoadingXL(true);
  setUploadProgress(0);

  try {
    const response = await axios.post(`${API_URL}/importPlayers`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${getToken()}`,
      },
      onUploadProgress: (progressEvent) => {
        const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(percent);
      },
    });

setUploadEnd(true);

setTimeout(() => {
  setLoadingXL(false);
  setUploadProgress(0);
}, 2000);

  } catch (error) {
    console.error('Error importing file:', error);
    showSnack('שגיאה בייבוא הקובץ', 'error');
  }
};


const saveGameSettings = async () => {
  await fetch(`${API_URL}/update_game_settings`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      game_id: selectedGame.id,
      ...gameSettings,
    }),
  });
  showSnack("הגדרות נשמרו בהצלחה ✅");
};

// עדכון תבנית עיצוב למשחק (בתוך לשונית ההגדרות)
const saveGameTheme = async (themeId) => {
  try {
    await fetch(`${API_URL}/update_game_theme`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ game_id: selectedGame.id, theme: themeId }),
    });
    setSelectedGame(prev => (prev ? { ...prev, theme: themeId } : prev));
    setGames(prev => prev.map(g => (g.id === selectedGame.id ? { ...g, theme: themeId } : g)));
    showSnack("תבנית העיצוב עודכנה 🎨");
  } catch {
    showSnack("שגיאה בעדכון התבנית", "error");
  }
};

// בלוק בחירת עיצוב — משובץ בתוך לשונית ההגדרות (מובייל + דסקטופ)
const themePickerBlock = (
  <Box sx={{ mt: 2, p: 2, borderRadius: 3, background: '#faf5ff', border: '1px solid #e9d5ff' }}>
    <Typography sx={{ fontWeight: 700, color: '#7c3aed', mb: .5, fontSize: '.95rem' }}>🎨 עיצוב המשחק</Typography>
    <Typography sx={{ fontSize: '.78rem', color: '#94a3b8', mb: 1.5 }}>בחר את סגנון התצוגה של מסך המשחק — רקע, צורות, גבולות וצבעים.</Typography>
    {GAME_THEMES.map(t => {
      const active = (selectedGame?.theme || 'classic') === t.id;
      const swatch = t.id === 'classic'
        ? 'repeating-linear-gradient(45deg,#8a6d3b,#8a6d3b 10px,#6b5330 10px,#6b5330 20px)'
        : t.tokens.pageBg;
      return (
        <Box key={t.id} sx={{
          display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1.5, p: 1.25, mb: 1, borderRadius: 2.5, background: '#fff',
          border: active ? '2px solid #a855f7' : '1px solid #e2e8f0',
          boxShadow: active ? '0 3px 12px rgba(168,85,247,.2)' : '0 1px 3px rgba(0,0,0,.04)',
        }}>
          {/* עיגול צבע בלבד (ללא אייקון) */}
          <Box sx={{ width: 42, height: 42, borderRadius: '50%', flexShrink: 0, background: swatch, border: '2px solid rgba(0,0,0,.08)', boxShadow: '0 1px 4px rgba(0,0,0,.12)' }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '.92rem', color: active ? '#a855f7' : '#334155' }}>{t.name}{active ? ' ✓' : ''}</Typography>
          </Box>
          <Button size="small" variant="outlined" onClick={() => window.open(`/play/demo?theme=${t.id}`, '_blank')}
            sx={{ borderRadius: 2, fontWeight: 700, fontSize: '.75rem', borderColor: '#c4b5fd', color: '#7c3aed', flexShrink: 0 }}>👁 צפיה</Button>
          <Button size="small" variant="contained" disabled={active} onClick={() => saveGameTheme(t.id)}
            sx={{ borderRadius: 2, fontWeight: 700, fontSize: '.75rem', flexShrink: 0, background: active ? undefined : 'linear-gradient(135deg,#7c3aed,#a855f7)' }}>
            {active ? 'נבחר' : 'בחר'}
          </Button>
        </Box>
      );
    })}
  </Box>
);
// שדה בחירת קטגוריה לקבוצה — קיימת / כללי / חדשה
const categoryFieldBlock = (
  <>
    <Select fullWidth size="small" displayEmpty
      value={newCatMode ? "__new" : groupCategory}
      onChange={(e) => { const v = e.target.value; if (v === "__new") { setNewCatMode(true); setGroupCategory(""); } else { setNewCatMode(false); setGroupCategory(v); } }}
      sx={{ borderRadius: 2, mb: newCatMode ? 1.5 : 0 }}>
      <MenuItem value="">כללי (ללא קטגוריה)</MenuItem>
      {[...new Set(groups.map(g => g.category).filter(Boolean))].map(cat => <MenuItem key={cat} value={cat}>{cat}</MenuItem>)}
      <MenuItem value="__new">➕ קטגוריה חדשה</MenuItem>
    </Select>
    {newCatMode && (
      <TextField autoFocus fullWidth size="small" label="שם קטגוריה חדשה" placeholder="עיר / כיתה / גילאים..."
        value={groupCategory} onChange={(e) => setGroupCategory(e.target.value)} />
    )}
  </>
);
const saveOpenGroups = async (status) => {
  await fetch(`${API_URL}/update_game_settings`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      game_id: selectedGame.id,
      isGroups: status
    }),
  });
  setGameSettings({ ...gameSettings, isGroups: status == "true" ? true : false })
  showSnack("קבוצות " + (status == "true" ? "הופעלו" : "הושבתו"));
};
    return (
        <ThemeProvider theme={rtlTheme}>
            <div dir="rtl" style={{ fontFamily: "'Varela Round', 'Rubik', Arial, sans-serif" }}>
                <AppBar position="fixed" sx={{ backgroundColor: '#8298f3' }}>
                    <Toolbar sx={{ minHeight: { xs: 56, sm: 64 }, px: { xs: 1, sm: 2 } }}>
                        <Typography variant="h6" color="inherit" sx={{ flexGrow: 1, cursor: 'pointer', '&:hover': { opacity: .85 }, fontSize: { xs: '1rem', sm: '1.25rem' } }}
                          onClick={() => { setSelectedGame(null); setShowProfile(false); }}>
                            🎮 משחקים ושאלות
                        </Typography>
                        <Tooltip title="כדורגל בטלפון">
                          <IconButton onClick={() => navigate('/football')} sx={{ color: '#fff', mr: { xs: 0, sm: 1 }, fontSize: '1.35rem' }}>
                            ⚽
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="התנתקות">
                          <IconButton onClick={handleLogout} sx={{ color: '#fff', mr: { xs: 0, sm: 1 } }}>
                            <LogoutIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="הפרופיל שלי">
                          <IconButton onClick={handleOpenProfile} sx={{ color: '#fff', mr: { xs: 0, sm: 1 } }}>
                            <AccountCircleIcon />
                          </IconButton>
                        </Tooltip>
                    </Toolbar>
                </AppBar>

<Box
  sx={{
    p: { xs: 1, sm: 2 },
    backgroundColor: '#f5f5f5',
    height: { xs: 'calc(100vh - 56px)', sm: 'calc(100vh - 64px)' },
    marginTop: { xs: '56px', sm: '64px' },
    overflowY: 'auto'
  }}
>

                    {loading ? (
                        <Box sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '100%',
                          gap: 3,
                        }}>
                          <Typography sx={{
                            fontSize: '2.5rem',
                            fontWeight: 800,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            letterSpacing: 1,
                          }}>
                            קליקרים
                          </Typography>
                          <CircularProgress size={48} thickness={4} sx={{ color: 'linear-gradient(135deg, #667eea 0%, #667eea 100%)' }} />
                        </Box>
                    ) : error ? (
                        <Box sx={{ minHeight: '70vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, px: 2, textAlign: 'center' }}>
                          <Box sx={{ fontSize: '3.6rem' }}>⚠️</Box>
                          <Typography sx={{ fontWeight: 800, fontSize: '1.35rem', color: '#334155' }}>{error}</Typography>
                          <Typography sx={{ color: '#94a3b8', fontSize: '.9rem', maxWidth: 360 }}>
                            אירעה תקלה בטעינת הנתונים. בדוק את החיבור לאינטרנט ונסה שוב.
                          </Typography>
                          <Button variant="contained" onClick={() => window.location.reload()}
                            sx={{ mt: 1, borderRadius: 3, fontWeight: 700, px: 4, py: 1.2, fontSize: '1rem',
                              background: 'linear-gradient(135deg,#667eea 0%,#764ba2 100%)', boxShadow: '0 6px 20px rgba(102,126,234,.4)' }}>
                            🔄 טען מחדש
                          </Button>
                        </Box>

                    ) : showProfile ? (
                      /* ===== דף פרופיל אישי ===== */
                      <Container maxWidth="md">
                        <Box sx={{ mb: 3, mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                          <Typography variant="h4" sx={{ fontWeight: 800, color: '#2d2d2d', fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                            👤 הפרופיל שלי
                          </Typography>
                          <Button onClick={() => setShowProfile(false)} sx={{ borderRadius: 3, fontWeight: 600, color: '#667eea', fontSize: { xs: '.8rem', sm: '.875rem' } }}>
                            🎮 חזרה למשחקים
                          </Button>
                        </Box>

                        {profileLoading ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                            <CircularProgress size={48} sx={{ color: '#667eea' }} />
                          </Box>
                        ) : profileData ? (
                          <>
                            {/* Summary cards */}
                            <Grid container spacing={2.5} sx={{ mb: 3 }}>
                              {[
                                { icon: '🎮', label: 'משחקים', val: profileData.stats?.gamesCount || 0, color: '#667eea', bg: '#f0f3ff' },
                                { icon: '✅', label: 'הפעלות', val: profileData.quota?.gamesAllowed === -1 ? '♾️' : `${profileData.stats?.activatedGames || 0} / ${profileData.quota?.gamesAllowed || 0}`, color: '#10b981', bg: '#ecfdf5' },
                                { icon: '👥', label: 'שחקנים', val: profileData.stats?.totalPlayers || 0, color: '#3b82f6', bg: '#eff6ff' },
                                { icon: '💾', label: 'אחסון', val: profileData.quota?.storageMB === -1 ? '∞' : `${Math.round((profileData.quota?.storageUsedBytes || 0) / 1024 / 1024)} / ${profileData.quota?.storageMB || 500} MB`, color: '#f59e0b', bg: '#fffbeb' },
                                { icon: '📩', label: 'הודעות SMS', val: profileData.smsBalance === -1 ? '∞' : profileData.smsBalance || 0, color: '#7c3aed', bg: '#faf5ff' },
                              ].map((s, i) => (
                                <Grid item xs={6} sm={4} md key={i}>
                                  <Card sx={{ borderRadius: 4, boxShadow: '0 2px 12px rgba(0,0,0,.04)', textAlign: 'center', overflow: 'visible' }}>
                                    <CardContent sx={{ p: 2.5 }}>
                                      <Typography sx={{ fontSize: '1.8rem', mb: .5 }}>{s.icon}</Typography>
                                      <Typography sx={{ fontWeight: 800, fontSize: '1.4rem', color: s.color }}>{s.val}</Typography>
                                      <Typography sx={{ fontSize: '.78rem', color: '#94a3b8' }}>{s.label}</Typography>
                                    </CardContent>
                                  </Card>
                                </Grid>
                              ))}
                            </Grid>

                            {/* Tabs */}
                            <Paper sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,.04)' }}>
                              <Box sx={{ borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'center', overflowX: { xs: 'auto', sm: 'visible' }, '&::-webkit-scrollbar': { display: 'none' } }}>
                                {['👤 פרטים אישיים', '🛒 רכישות', '📋 היסטוריית חבילות'].map((label, i) => (
                                  <Button
                                    key={i}
                                    onClick={() => setProfileTab(i)}
                                    sx={{
                                      flex: { xs: 'none', sm: 1 }, py: { xs: 1.5, sm: 2 }, borderRadius: 0, fontWeight: 700, fontSize: { xs: '.75rem', sm: '.9rem' },
                                      borderBottom: profileTab === i ? '3px solid #667eea' : '3px solid transparent',
                                      color: profileTab === i ? '#667eea' : '#94a3b8',
                                      background: profileTab === i ? '#f0f3ff' : 'transparent',
                                      whiteSpace: 'nowrap', minWidth: { xs: 'auto', sm: 0 }, px: { xs: 2, sm: 1 },
                                    }}
                                  >
                                    {label}
                                  </Button>
                                ))}
                              </Box>

                              <Box sx={{ p: 3 }}>
                                {/* Tab 0 — Personal info */}
                                {profileTab === 0 && profileEdit && (
                                  <Box>
                                    <Grid container spacing={2.5}>
                                      <Grid item xs={12} sm={6}>
                                        <TextField fullWidth label="שם פרטי" value={profileEdit.first_name || ''}
                                          onChange={e => setProfileEdit({ ...profileEdit, first_name: e.target.value })}
                                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }} />
                                      </Grid>
                                      <Grid item xs={12} sm={6}>
                                        <TextField fullWidth label="שם משפחה" value={profileEdit.last_name || ''}
                                          onChange={e => setProfileEdit({ ...profileEdit, last_name: e.target.value })}
                                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }} />
                                      </Grid>
                                      <Grid item xs={12} sm={6}>
                                        <TextField fullWidth label="טלפון" value={profileEdit.phone || ''}
                                          onChange={e => setProfileEdit({ ...profileEdit, phone: e.target.value })}
                                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }} />
                                      </Grid>
                                      <Grid item xs={12} sm={6}>
                                        <TextField fullWidth label="אימייל" value={profileEdit.email || ''}
                                          onChange={e => setProfileEdit({ ...profileEdit, email: e.target.value })}
                                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }} />
                                      </Grid>
                                    </Grid>

                                    <Box sx={{ mt: 3, p: 2.5, borderRadius: 3, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                      <Typography sx={{ fontWeight: 700, mb: 1, color: '#475569' }}>📊 מידע נוסף</Typography>
                                      <Grid container spacing={1.5}>
                                        <Grid item xs={6}>
                                          <Typography sx={{ fontSize: '.82rem', color: '#94a3b8' }}>תאריך הצטרפות</Typography>
                                          <Typography sx={{ fontWeight: 600, color: '#1e293b' }}>{profileData.profile?.createdAt || '—'}</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                          <Typography sx={{ fontSize: '.82rem', color: '#94a3b8' }}>מזהה משתמש</Typography>
                                          <Typography sx={{ fontWeight: 600, color: '#1e293b', fontSize: '.85rem', wordBreak: 'break-all' }}>{userId}</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                          <Typography sx={{ fontSize: '.82rem', color: '#94a3b8' }}>מקס שחקנים למשחק</Typography>
                                          <Typography sx={{ fontWeight: 600, color: '#1e293b' }}>{profileData.quota?.maxPlayersPerGame || 12}</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                          <Typography sx={{ fontSize: '.82rem', color: '#94a3b8' }}>סוג חבילה</Typography>
                                          <Typography sx={{ fontWeight: 600, color: '#667eea' }}>
                                            {profileData.quota?.gamesAllowed === -1 ? '♾️ ללא הגבלה' : `${profileData.quota?.gamesAllowed || 0} הפעלות`}
                                          </Typography>
                                        </Grid>
                                      </Grid>
                                    </Box>

                                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', flexDirection: { xs: 'column', sm: 'row' } }}>
                                      <Button
                                        variant="outlined"
                                        startIcon={<LockResetIcon />}
                                        onClick={() => { setShowResetDialog(true); setResetStep(0); setResetError(''); setResetIdentifier(profileEdit?.phone || profileEdit?.email || ''); }}
                                        sx={{ borderRadius: 3, px: 3, py: 1.2, fontWeight: 700, borderColor: '#c2410c', color: '#c2410c', borderWidth: 2, width: { xs: '100%', sm: 'auto' }, fontSize: { xs: '.8rem', sm: '.875rem' } }}>
                                        🔐 איפוס סיסמה
                                      </Button>
                                      <Button
                                        variant="outlined"
                                        onClick={() => setShowPackageDialog(true)}
                                        sx={{ borderRadius: 3, px: 3, py: 1.2, fontWeight: 700, borderColor: '#667eea', color: '#667eea', borderWidth: 2, width: { xs: '100%', sm: 'auto' }, fontSize: { xs: '.8rem', sm: '.875rem' } }}
                                      >
                                        🛒 רכישת / שדרוג חבילה
                                      </Button>
                                      <Button
                                        variant="contained"
                                        onClick={handleSaveProfile}
                                        disabled={savingProfile}
                                        startIcon={savingProfile ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                                        sx={{
                                          borderRadius: 3, px: 4, py: 1.2, fontWeight: 700, width: { xs: '100%', sm: 'auto' }, fontSize: { xs: '.8rem', sm: '.875rem' },
                                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                          boxShadow: '0 4px 16px rgba(102,126,234,.3)',
                                        }}
                                      >
                                        {savingProfile ? 'שומר...' : 'שמור שינויים'}
                                      </Button>
                                    </Box>
                                  </Box>
                                )}

                                {/* Tab 1 — Purchases */}
                                {profileTab === 1 && (
                                  <Box>
                                    {profileData.purchases?.length > 0 ? (
                                      <TableContainer>
                                        <Table>
                                          <TableHead>
                                            <TableRow sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                                              <TableCell sx={{ color: '#fff', fontWeight: 700, border: 0 }}>תאריך</TableCell>
                                              <TableCell sx={{ color: '#fff', fontWeight: 700, border: 0 }}>סכום</TableCell>
                                              <TableCell sx={{ color: '#fff', fontWeight: 700, border: 0 }}>תיאור</TableCell>
                                              <TableCell sx={{ color: '#fff', fontWeight: 700, border: 0 }}>מזהה עסקה</TableCell>
                                              <TableCell sx={{ color: '#fff', fontWeight: 700, border: 0 }}>סטטוס</TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {profileData.purchases.map((p, i) => (
                                              <TableRow key={i} sx={{ bgcolor: i % 2 === 0 ? '#fff' : '#fafbff', '& td': { border: 0 } }}>
                                                <TableCell sx={{ fontWeight: 500 }}>{p.createdAt || '—'}</TableCell>
                                                <TableCell sx={{ fontWeight: 700, color: '#10b981' }}>₪{p.amount || '—'}</TableCell>
                                                <TableCell>{p.productDescription || p.packageId || '—'}</TableCell>
                                                <TableCell sx={{ fontFamily: 'monospace', fontSize: '.82rem', color: '#94a3b8' }}>{p.transactionId || '—'}</TableCell>
                                                <TableCell>
                                                  <Chip
                                                    label={p.processed ? 'עובד ✅' : p.errordescription === 'SUCCESS' ? 'הצליח ⏳' : 'ממתין'}
                                                    size="small"
                                                    sx={{
                                                      fontWeight: 600, borderRadius: 2,
                                                      bgcolor: p.processed ? '#ecfdf5' : '#fffbeb',
                                                      color: p.processed ? '#10b981' : '#f59e0b',
                                                    }}
                                                  />
                                                </TableCell>
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                        </Table>
                                      </TableContainer>
                                    ) : (
                                      <Box sx={{ textAlign: 'center', py: 6 }}>
                                        <Typography sx={{ fontSize: '3rem', mb: 1 }}>🛒</Typography>
                                        <Typography sx={{ color: '#94a3b8', fontWeight: 600 }}>אין רכישות</Typography>
                                        <Button
                                          variant="outlined"
                                          onClick={() => { setShowProfile(false); setShowPackageDialog(true); }}
                                          sx={{ mt: 2, borderRadius: 3, fontWeight: 700, borderColor: '#667eea', color: '#667eea' }}
                                        >
                                          🛒 רכישת חבילה
                                        </Button>
                                      </Box>
                                    )}
                                  </Box>
                                )}

                                {/* Tab 2 — Package grant history */}
                                {profileTab === 2 && (
                                  <Box>
                                    {profileData.purchaseHistory?.length > 0 ? (
                                      <TableContainer>
                                        <Table>
                                          <TableHead>
                                            <TableRow sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                                              <TableCell sx={{ color: '#fff', fontWeight: 700, border: 0 }}>תאריך</TableCell>
                                              <TableCell sx={{ color: '#fff', fontWeight: 700, border: 0 }}>חבילה</TableCell>
                                              <TableCell sx={{ color: '#fff', fontWeight: 700, border: 0 }}>סכום</TableCell>
                                              <TableCell sx={{ color: '#fff', fontWeight: 700, border: 0 }}>הוענקה ע״י</TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {profileData.purchaseHistory.map((ph, i) => (
                                              <TableRow key={i} sx={{ bgcolor: i % 2 === 0 ? '#fff' : '#fafbff', '& td': { border: 0 } }}>
                                                <TableCell sx={{ fontWeight: 500 }}>{ph.date || '—'}</TableCell>
                                                <TableCell sx={{ fontWeight: 700, color: '#667eea' }}>{ph.packageName || '—'}</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>{ph.amount || '—'}</TableCell>
                                                <TableCell sx={{ color: '#94a3b8' }}>{ph.grantedBy || 'מערכת'}</TableCell>
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                        </Table>
                                      </TableContainer>
                                    ) : (
                                      <Box sx={{ textAlign: 'center', py: 6 }}>
                                        <Typography sx={{ fontSize: '3rem', mb: 1 }}>📋</Typography>
                                        <Typography sx={{ color: '#94a3b8', fontWeight: 600 }}>אין היסטוריית חבילות</Typography>
                                      </Box>
                                    )}
                                  </Box>
                                )}
                              </Box>
                            </Paper>
                          </>
                        ) : (
                          <Box sx={{ textAlign: 'center', py: 8 }}>
                            <Typography sx={{ color: '#94a3b8' }}>שגיאה בטעינת הפרופיל</Typography>
                          </Box>
                        )}
                      </Container>

                    ) : selectedGame ? (
                        <>
                        {/* ===================== כותרת מובייל ===================== */}
                        {isMobile && (
                          <Box sx={{ pb: 8 }}>
                          {/* כותרת משחק מובייל */}
                          <Box sx={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            borderRadius: 3, p: 2, mb: 1.5, color: '#fff',
                            display: 'flex', alignItems: 'center', gap: 1.5,
                          }}>
                            <IconButton onClick={() => { setSelectedGame(null); setMobileEditorOpen(false); setMobilePlayerEditorOpen(false); }} sx={{ color: '#fff', p: .5 }}>
                              <ArrowBackIcon />
                            </IconButton>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {selectedGame?.name}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, mt: .3 }}>
                                <Typography sx={{ fontSize: '.7rem', opacity: .8 }}>❓ {questions.length} שאלות</Typography>
                                <Typography sx={{ fontSize: '.7rem', opacity: .8 }}>
                                  👥 {players.length}/{quota?.maxPlayersPerGame || '∞'} שחקנים
                                </Typography>
                              </Box>
                            </Box>
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<PlayArrowIcon sx={{ fontSize: '1rem !important' }} />}
                              onClick={async () => {
                                if (quota && quota.gamesAllowed !== -1) {
                                  const activationsUsed = quota.activationsUsed || 0;
                                  if (activationsUsed >= quota.gamesAllowed) { setShowPackageDialog(true); return; }
                                }
                                // בדיקת מגבלת שחקנים לפני פתיחת דיאלוג הפעלה
                                try {
                                  const gpR = await fetch(`${API_URL}/global_player_count/${userId}`, { headers: getAuthHeaders() });
                                  const gpData = await gpR.json();
                                  if (gpData.status) {
                                    setGlobalPlayerInfo(gpData);
                                    if (gpData.maxPlayers > 0 && gpData.maxPlayers !== 9999 && gpData.totalPlayers > gpData.maxPlayers) {
                                      setShowPlayerLimitDialog(true);
                                      return;
                                    }
                                  }
                                } catch (e) { console.error('global_player_count error:', e); }
                                setPreActivateLoading(true);
                                setShowPreActivateDialog(true);
                                setSmsInfo(null); setSmsSent(false);
                                try {
                                  const [r, smsR] = await Promise.all([
                                    fetch(`${API_URL}/get_info/${selectedGame.id}`),
                                    fetch(`${API_URL}/sms_info/${selectedGame.id}`, { headers: getAuthHeaders() }),
                                  ]);
                                  const info = await r.json();
                                  const smsData = await smsR.json();
                                  if (smsData.status) setSmsInfo(smsData);
                                  const activationsUsed = quota?.activationsUsed || 0;
                                  const remaining = quota?.gamesAllowed === -1 ? -1 : Math.max(0, (quota?.gamesAllowed || 0) - activationsUsed);
                                  setPreActivateInfo({
                                    phoneNumber: info.number || selectedGame.startNumber || 'ממתין להקצאה...',
                                    gameName: selectedGame.name,
                                    gameId: selectedGame.id,
                                    mobileLink: `${window.location.origin}/m/${selectedGame.id}`,
                                    remaining,
                                    allowSmartDevice: selectedGame.allowSmartDevice ?? true,
                                  });
                                } catch {
                                  setPreActivateInfo({
                                    phoneNumber: selectedGame.startNumber || 'לא ידוע',
                                    gameName: selectedGame.name,
                                    gameId: selectedGame.id,
                                    mobileLink: `${window.location.origin}/m/${selectedGame.id}`,
                                    remaining: -1,
                                    allowSmartDevice: selectedGame.allowSmartDevice ?? true,
                                  });
                                }
                                setPreActivateLoading(false);
                              }}
                              sx={{
                                borderRadius: 2, px: 2, py: .8,
                                fontWeight: 700, fontSize: '.78rem',
                                background: 'rgba(255,255,255,.2)',
                                backdropFilter: 'blur(10px)',
                                boxShadow: 'none',
                                '&:hover': { background: 'rgba(255,255,255,.3)' },
                                flexShrink: 0,
                              }}
                            >
                              🚀 הפעלה
                            </Button>
                          </Box>

                          {/* טאבים מובייל */}
                          <Box sx={{
                            display: 'flex', gap: .5, mb: 1.5,
                            background: '#f1f5f9', borderRadius: 2.5, p: .5,
                          }}>
                            {[
                              { key: 'questions', label: '📝 שאלות' },
                              { key: 'players', label: '👥 שחקנים' },
                              { key: 'groups', label: '🏷️ קבוצות' },
                              { key: 'settings', label: '⚙️ הגדרות' },
                            ].map(t => (
                              <Button
                                key={t.key}
                                onClick={() => { setViewMode(t.key); setMobileEditorOpen(false); setMobilePlayerEditorOpen(false); }}
                                sx={{
                                  flex: 1, borderRadius: 2, py: 1,
                                  fontSize: '.78rem', fontWeight: 700,
                                  background: viewMode === t.key ? '#fff' : 'transparent',
                                  color: viewMode === t.key ? '#667eea' : '#94a3b8',
                                  boxShadow: viewMode === t.key ? '0 2px 8px rgba(0,0,0,.08)' : 'none',
                                  minWidth: 0,
                                }}
                              >
                                {t.label}
                              </Button>
                            ))}
                          </Box>

                          {/* עורך שאלות מובייל — שכבה עליונה */}
                          {mobileEditorOpen && viewMode === 'questions' && selectedQuestion && (
                            <Slide direction="right" in={mobileEditorOpen} mountOnEnter unmountOnExit>
                              <Box sx={{
                                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                                bgcolor: '#f5f5f5', zIndex: 1200, overflowY: 'auto',
                                pt: '56px',
                              }}>
                                <Box sx={{
                                  position: 'fixed', top: 0, left: 0, right: 0, height: 56, zIndex: 1201,
                                  background: '#fff', borderBottom: '1px solid #e2e8f0',
                                  display: 'flex', alignItems: 'center', px: 1.5, gap: 1,
                                }}>
                                  <IconButton onClick={() => setMobileEditorOpen(false)} sx={{ color: '#667eea' }}>
                                    <ArrowBackIcon />
                                  </IconButton>
                                  <Typography sx={{ fontWeight: 700, fontSize: '.95rem', color: '#1e293b', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {selectedQuestion?.q || 'עריכת שאלה'}
                                  </Typography>
                                </Box>
                                <Box sx={{ p: 1.5 }}>
                                  <Box sx={{ backgroundColor: '#ffffff', p: 1.5, pb: 0, borderRadius: 2 }}>
                                    <Grid container spacing={1}>
                                      {questionTypes.map((type) => (
                                        <Grid item xs={3} key={type.type}>
                                          <Paper
                                            sx={{ p: 1, borderRadius: 2, textAlign: 'center', cursor: 'pointer', boxShadow: selectedType === type.type ? 3 : 1, position: 'relative' }}
                                            onClick={() => handleAddQuestion(type.type)}
                                          >
                                            <Box sx={{ position: 'absolute', top: -6, left: -6, backgroundColor: '#fff', borderRadius: '50%', width: 20, height: 20, display: 'flex', justifyContent: 'center', alignItems: 'center', boxShadow: 1 }}
                                              onClick={(e) => { e.stopPropagation(); handleAddQuestion(type.type); }}>
                                              <Typography sx={{ fontSize: 14, fontWeight: 'bold', color: '#2196f3' }}>+</Typography>
                                            </Box>
                                            {type.icon}
                                            <Typography sx={{ color: 'black', fontSize: '.6rem' }}>{type.label}</Typography>
                                          </Paper>
                                        </Grid>
                                      ))}
                                    </Grid>

                                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <Typography sx={{ fontSize: '.9rem', fontWeight: 700 }}>עריכת שאלה</Typography>
                                      <Select
                                        sx={{ minWidth: 90, '& .MuiSelect-select': { textAlign: 'right', py: .8 } }}
                                        size="small"
                                        value={selectedQuestion?.type || ''}
                                        onChange={(e) => handleChangeQuestionType(e.target.value)}
                                      >
                                        {questionTypes.map((type) => (
                                          <MenuItem key={type.type} value={type.type}>{type.label}</MenuItem>
                                        ))}
                                      </Select>
                                    </Box>

                                    {/* שאלה */}
                                    <Card sx={{ borderRadius: '20px', mb: 1, mt: 1 }}>
                                      <CardContent
                                        contentEditable suppressContentEditableWarning
                                        onBlur={(e) => handleEdit('q', e.target.innerText)}
                                        dangerouslySetInnerHTML={{ __html: selectedQuestion.q }}
                                        style={{ minHeight: '36px', padding: '8px 12px', border: 'none', boxShadow: 'none', fontSize: '16px', lineHeight: '1.4', whiteSpace: 'pre-wrap', outline: 'none', color: 'rgba(0,0,0,.8)' }}
                                      />
                                    </Card>

                                    {/* תשובות */}
                                    <Grid container spacing={1}>
                                      {selectedQuestion?.answers?.map((answer, index) => (
                                        <Grid item xs={12} key={index} sx={{ position: 'relative' }}>
                                          <Card sx={{ px: 1.5, borderRadius: '20px', display: 'flex', alignItems: 'center', bgcolor: 'transparent' }}>
                                            <Typography sx={{ fontSize: '16px', fontWeight: 'bold', mr: 1 }}>{index + 1}</Typography>
                                            <CardContent
                                              contentEditable suppressContentEditableWarning
                                              onBlur={(e) => handleEdit(index, e.target.innerText)}
                                              dangerouslySetInnerHTML={{ __html: answer }}
                                              style={{ flexGrow: 1, minHeight: '36px', padding: '8px', border: 'none', boxShadow: 'none', fontSize: '15px', lineHeight: '1.4', whiteSpace: 'pre-wrap', outline: 'none', color: 'rgba(0,0,0,0.8)' }}
                                            />
                                            <Box sx={{ display: 'flex', gap: .5 }}>
                                              <IconButton size="small" onClick={() => handleDeleteAnswer(index)} sx={{ color: '#e57373' }}>
                                                <DeleteIcon fontSize="small" />
                                              </IconButton>
                                              <IconButton size="small" onClick={() => updateData("currect", index)} sx={{ color: selectedQuestion.currect === index ? '#4caf50' : '#ccc' }}>
                                                <CheckCircleIcon fontSize="small" />
                                              </IconButton>
                                            </Box>
                                          </Card>
                                        </Grid>
                                      ))}
                                    </Grid>

                                    {/* כפתורי פעולה */}
                                    <Box sx={{ display: 'flex', gap: 1, mt: 1.5, mb: 2, flexWrap: 'wrap' }}>
                                      <Button size="small" variant="outlined" onClick={() => addAwsToAPI()} sx={{ borderRadius: '20px', fontSize: '.75rem' }}>+ תשובה</Button>
                                      <Button size="small" variant="outlined" onClick={() => { updateData("link", selectedQuestion.link); }} sx={{ borderRadius: '20px', fontSize: '.75rem' }}>שמור קישור</Button>
                                    </Box>
                                  </Box>
                                </Box>
                              </Box>
                            </Slide>
                          )}

                          {/* עורך שחקנים מובייל — שכבה עליונה */}
                          {mobilePlayerEditorOpen && viewMode === 'players' && selectedPlayer && (
                            <Slide direction="right" in={mobilePlayerEditorOpen} mountOnEnter unmountOnExit>
                              <Box sx={{
                                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                                bgcolor: '#f5f5f5', zIndex: 1200, overflowY: 'auto',
                                pt: '56px',
                              }}>
                                <Box sx={{
                                  position: 'fixed', top: 0, left: 0, right: 0, height: 56, zIndex: 1201,
                                  background: '#fff', borderBottom: '1px solid #e2e8f0',
                                  display: 'flex', alignItems: 'center', px: 1.5, gap: 1,
                                }}>
                                  <IconButton onClick={() => setMobilePlayerEditorOpen(false)} sx={{ color: '#667eea' }}>
                                    <ArrowBackIcon />
                                  </IconButton>
                                  <Typography sx={{ fontWeight: 700, fontSize: '.95rem', color: '#1e293b', flex: 1 }}>
                                    עריכת שחקן
                                  </Typography>
                                </Box>
                                <Box sx={{ p: 2 }}>
                                  <Box sx={{ backgroundColor: '#fff', p: 2, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,.06)' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5, pb: 2, borderBottom: '1px solid #f1f5f9' }}>
                                      <Box sx={{ width: 44, height: 44, borderRadius: '50%', background: 'linear-gradient(135deg, #667eea, #764ba2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem' }}>{(selectedPlayer.name || '?')[0]}</Typography>
                                      </Box>
                                      <Box>
                                        <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>{selectedPlayer.name || 'ללא שם'}</Typography>
                                        <Typography sx={{ fontSize: '.75rem', color: '#94a3b8' }}>{selectedPlayer.phone}</Typography>
                                      </Box>
                                    </Box>
                                    <Grid container spacing={2}>
                                      <Grid item xs={12}>
                                        <TextField label="שם" fullWidth size="small" value={selectedPlayer.name || ''}
                                          onChange={(e) => setSelectedPlayer(prev => ({ ...prev, name: e.target.value }))} />
                                      </Grid>
                                      <Grid item xs={12}>
                                        <TextField label="טלפון" fullWidth size="small" value={selectedPlayer.phone || ''}
                                          onChange={(e) => setSelectedPlayer(prev => ({ ...prev, phone: e.target.value }))} />
                                      </Grid>
                                        <Grid item xs={12}>
                                          <TextField select label="קבוצה" fullWidth size="small" value={selectedPlayer.group_id ?? ''}
                                            onChange={(e) => setSelectedPlayer(prev => ({ ...prev, group_id: e.target.value === '' ? null : +e.target.value }))}>
                                            <MenuItem value="">ללא קבוצה</MenuItem>
                                            {Array.isArray(groups) && groups.map(g => <MenuItem key={g.id} value={g.id}>{g.name || `קבוצה ${g.id}`}</MenuItem>)}
                                          </TextField>
                                        </Grid>
                                      <Grid item xs={12}>
                                        <TextField label="נקודות" type="number" fullWidth size="small" value={selectedPlayer.point || 0}
                                          onChange={(e) => setSelectedPlayer(prev => ({ ...prev, point: +e.target.value }))} />
                                      </Grid>
                                      <Grid item xs={12}>
                                        <Button variant="contained" fullWidth
                                          onClick={async () => {
                                            await fetch(`${API_URL}/update_player`, {
                                              method: 'POST',
                                              headers: getAuthHeaders(),
                                              body: JSON.stringify({ game_id: selectedGame.id, ...selectedPlayer }),
                                            });
                                            setPlayers(prev => prev.map(p => p.id === selectedPlayer.id ? { ...p, ...selectedPlayer } : p));
                                            showSnack('שחקן עודכן בהצלחה!');
                                            setMobilePlayerEditorOpen(false);
                                          }}
                                          sx={{ borderRadius: '20px', py: 1.2, fontWeight: 700, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                                          💾 שמור שחקן
                                        </Button>
                                      </Grid>
                                    </Grid>
                                  </Box>
                                </Box>
                              </Box>
                            </Slide>
                          )}

                          {/* תוכן הטאב הנוכחי במובייל */}
                          <Box sx={{ bgcolor: '#fff', borderRadius: 3, p: 1.5 }}>

{viewMode === 'settings' ? (
  <Box sx={{ backgroundColor: '#f0f0f0', p: 2, borderRadius: 2 }}>
    <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem' }}>הגדרות המשחק</Typography>
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField label="ניקוד לשאלה" type="number" fullWidth size="small"
          value={gameSettings.defaultPoints}
          onChange={(e) => setGameSettings({ ...gameSettings, defaultPoints: +e.target.value })} />
      </Grid>
      <Grid item xs={12}>
        <TextField label="זמן תגובה (שניות)" type="number" fullWidth size="small"
          value={gameSettings.answerTime}
          onChange={(e) => setGameSettings({ ...gameSettings, answerTime: +e.target.value })} />
      </Grid>
      <Grid item xs={12}>
        <Select fullWidth size="small" value={gameSettings.isOpen}
          onChange={(e) => setGameSettings({ ...gameSettings, isOpen: e.target.value })}>
          <MenuItem value="true">פתוח לכולם</MenuItem>
          <MenuItem value="false">רק למאושרים</MenuItem>
        </Select>
      </Grid>
      <Grid item xs={12}>
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          p: 1.5, borderRadius: 2,
          background: gameSettings.allowSmartDevice ? 'linear-gradient(135deg, #eff6ff, #dbeafe)' : '#f8fafc',
          border: `1px solid ${gameSettings.allowSmartDevice ? '#93c5fd' : '#e2e8f0'}`,
          cursor: 'pointer',
        }}
        onClick={() => setGameSettings({ ...gameSettings, allowSmartDevice: !gameSettings.allowSmartDevice })}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: '1.2rem' }}>📱</Typography>
            <Typography sx={{ fontWeight: 700, fontSize: '.8rem', color: gameSettings.allowSmartDevice ? '#1d4ed8' : '#94a3b8' }}>מכשיר חכם</Typography>
          </Box>
          <Box sx={{
            width: 40, height: 22, borderRadius: 11, position: 'relative',
            background: gameSettings.allowSmartDevice ? '#2563eb' : '#cbd5e1',
            transition: 'all .2s', flexShrink: 0,
          }}>
            <Box sx={{ width: 16, height: 16, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,.2)',
              position: 'absolute', top: 3,
              left: gameSettings.allowSmartDevice ? 21 : 3, transition: 'all .2s' }} />
          </Box>
        </Box>
      </Grid>
      <Grid item xs={12}>
        {themePickerBlock}
        <Button variant="contained" fullWidth onClick={saveGameSettings} sx={{ borderRadius: '20px', mt: 2 }}>שמור הגדרות</Button>
      </Grid>
    </Grid>
  </Box>
) : viewMode === 'groups' ? (
  <Box sx={{ backgroundColor: '#f0f0f0', p: 2, borderRadius: 2 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
      <Typography variant="h6" sx={{ fontSize: '1rem' }}>ניהול קבוצות</Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button size="small" variant="contained" onClick={() => saveOpenGroups(gameSettings.isGroups ? "false" : "true")} sx={{ borderRadius: '20px' }}>{gameSettings.isGroups ? "השבת" : "הפעל"}</Button>
        <Button size="small" variant="outlined" onClick={() => setOpenGroupDialog(true)} sx={{ borderRadius: '20px' }}>➕ הוסף</Button>
      </Box>
    </Box>
    {groups.length === 0 ? (
      <Typography sx={{ textAlign: 'center', color: '#94a3b8', py: 3 }}>אין קבוצות עדיין</Typography>
    ) : groups.map(g => (
      <Box key={g.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.25, mb: 1, borderRadius: 2, background: '#fff', border: '1px solid #e2e8f0' }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '.9rem' }}>{g.name}</Typography>
          {g.category && <Typography sx={{ fontSize: '.7rem', color: '#a855f7', fontWeight: 600 }}>{g.category}</Typography>}
        </Box>
        <IconButton size="small" color="error" onClick={() => deleteGroup(selectedGame.id, g.id)}><DeleteIcon fontSize="small" /></IconButton>
      </Box>
    ))}
    <Dialog open={openGroupDialog} onClose={() => setOpenGroupDialog(false)} fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}><GroupAddIcon /> הוספת קבוצה</DialogTitle>
      <DialogContent sx={{ pt: '16px !important', direction: 'rtl', textAlign: 'right' }}>
        <TextField autoFocus label="שם הקבוצה" value={groupName} onChange={(e) => setGroupName(e.target.value)} fullWidth sx={{ mb: 2, mt: 1 }} />
        <Typography sx={{ fontSize: '.8rem', color: '#94a3b8', mb: .5 }}>קטגוריה</Typography>
        {categoryFieldBlock}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={() => handleAddGroup(selectedGame.id)} variant="contained" sx={{ borderRadius: 2 }}>הוסף</Button>
        <Button onClick={() => setOpenGroupDialog(false)} sx={{ color: '#888' }}>ביטול</Button>
      </DialogActions>
    </Dialog>
  </Box>
) : viewMode === 'players' ? (
  <>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, px: .5 }}>
      <Typography sx={{ fontWeight: 700, fontSize: '.95rem' }}>
        שחקנים ({players.length})
      </Typography>
      {quota?.maxPlayersPerGame > 0 && quota?.maxPlayersPerGame !== 9999 && (
        <Chip
          label={`מקס: ${quota.maxPlayersPerGame}`}
          size="small"
          sx={{
            fontSize: '.7rem', fontWeight: 700, height: 22,
            bgcolor: players.length > quota.maxPlayersPerGame ? '#fef2f2' : '#ecfdf5',
            color: players.length > quota.maxPlayersPerGame ? '#dc2626' : '#16a34a',
          }}
        />
      )}
    </Box>
    {quota?.maxPlayersPerGame > 0 && quota?.maxPlayersPerGame !== 9999 && players.length > quota.maxPlayersPerGame && (
      <Box sx={{ mb: 1.5, p: 1.5, borderRadius: 2, background: '#fef2f2', border: '1px solid #fecaca' }}>
        <Typography sx={{ fontSize: '.78rem', color: '#dc2626', fontWeight: 600 }}>
          ⚠️ יש {players.length} שחקנים שמורים אבל החבילה מאפשרת עד {quota.maxPlayersPerGame}.
          מחק שחקנים או שדרג חבילה.
        </Typography>
      </Box>
    )}
    <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
      <Button size="small" variant="contained" onClick={() => setOpenPlayerDialog(true)}
        sx={{ borderRadius: '20px', fontSize: '.78rem', flex: 1, py: .8, fontWeight: 700, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        👤 הוסף שחקן
      </Button>
    </Box>
    {players.length === 0 ? (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography sx={{ fontSize: '2.5rem', mb: .5 }}>👥</Typography>
        <Typography sx={{ color: '#94a3b8', fontWeight: 600, fontSize: '.85rem' }}>אין שחקנים עדיין</Typography>
      </Box>
    ) : (
      <List dense>
        {players.map((player) => (
          <ListItem key={player.phone} button onClick={() => { setSelectedPlayer(player); setMobilePlayerEditorOpen(true); }}
            sx={{ borderRadius: 2, mb: .5, bgcolor: selectedPlayer?.phone === player.phone ? '#f0f3ff' : 'transparent', opacity: player.disabled ? 0.5 : 1 }}>
            <ListItemText
              primary={<Typography sx={{ fontWeight: 600, fontSize: '.85rem', textDecoration: player.disabled ? 'line-through' : 'none', color: player.disabled ? '#94a3b8' : 'inherit' }}>{player.name || "ללא שם"}{player.disabled ? ' (מושבת)' : ''}</Typography>}
              secondary={<Typography sx={{ fontSize: '.72rem', color: '#94a3b8' }}>{player.phone}</Typography>}
            />
            <Tooltip title={player.disabled ? 'הפעל שחקן' : 'השבת שחקן'}>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleTogglePlayerDisabled(player); }}
                sx={{ color: player.disabled ? '#10b981' : '#f59e0b', mr: .5 }}>
                {player.disabled ? <PersonIcon fontSize="small" /> : <PersonOffIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDeletePlayer(player); }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </ListItem>
        ))}
      </List>
    )}
  </>
) : (
  /* שאלות במובייל */
  <>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, px: .5 }}>
      <Typography sx={{ fontWeight: 700, fontSize: '.95rem' }}>שאלות ({questions.length})</Typography>
      {loadingXL ? (
        <Button size="small" disabled sx={{ borderRadius: '20px', fontSize: '.72rem' }}>
          {uploadEnd ? "עלה ✅" : `${uploadProgress}%`} <CircularProgress size={14} sx={{ ml: .5 }} />
        </Button>
      ) : (
        <Button size="small" variant="outlined" component="label" sx={{ borderRadius: '20px', fontSize: '.72rem' }}>
          📥 ייבוא <input type="file" accept=".xlsx" hidden onChange={handleImportQuestions} />
        </Button>
      )}
    </Box>
    {/* כפתור הוספת שאלה — פלוס שפותח תפריט */}
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
      <Button
        variant="contained"
        onClick={(e) => setAddQuestionMenuAnchor(e.currentTarget)}
        sx={{
          borderRadius: '50%', minWidth: 48, width: 48, height: 48, p: 0,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          boxShadow: '0 4px 16px rgba(102, 126, 234, 0.4)',
          fontSize: '1.5rem', fontWeight: 700,
          '&:hover': { boxShadow: '0 6px 24px rgba(102, 126, 234, 0.55)' },
        }}
      >
        +
      </Button>
      {/* יצירת שאלות ב-AI */}
      <Button
        variant="contained"
        onClick={() => { setAiPreview(null); setAiDlg(true); }}
        sx={{
          borderRadius: '30px', px: 2.5, height: 48, fontWeight: 800, fontSize: '.9rem', whiteSpace: 'nowrap',
          background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)',
          boxShadow: '0 4px 16px rgba(168, 85, 247, 0.4)',
          '&:hover': { boxShadow: '0 6px 24px rgba(168, 85, 247, 0.55)' },
        }}
      >
        ✨ צור שאלות ב-AI
      </Button>
      <Menu
        anchorEl={addQuestionMenuAnchor}
        open={Boolean(addQuestionMenuAnchor)}
        onClose={() => setAddQuestionMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{ sx: { borderRadius: 3, mt: 1, minWidth: 180, boxShadow: '0 8px 30px rgba(0,0,0,.12)' } }}
      >
        {questionTypes.map((type) => (
          <MenuItem
            key={type.type}
            onClick={() => { handleAddQuestion(type.type); setAddQuestionMenuAnchor(null); }}
            sx={{ gap: 1.5, py: 1.2, fontWeight: 600, fontSize: '.9rem' }}
          >
            {type.icon}
            {type.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>

    {questions.length === 0 ? (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography sx={{ fontSize: '2.5rem', mb: .5 }}>❓</Typography>
        <Typography sx={{ color: '#94a3b8', fontWeight: 600, fontSize: '.85rem' }}>אין שאלות עדיין</Typography>
      </Box>
    ) : (
      <List dense>
        {questions.map((question, index) => (
          <ListItem
            key={question.id}
            button
            onClick={() => { setSelectedQuestion(question); setMobileEditorOpen(true); }}
            sx={{
              borderRadius: 2, mb: .5, py: 1,
              bgcolor: selectedQuestion?.id === question.id ? '#f0f3ff' : 'transparent',
              border: '1px solid #f1f5f9',
            }}
          >
            <Box sx={{
              width: 28, height: 28, borderRadius: 1.5,
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: '.75rem', fontWeight: 700, mr: 1.5, flexShrink: 0,
            }}>{index + 1}</Box>
            <ListItemText
              primary={<Typography sx={{ fontWeight: 600, fontSize: '.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{question.q}</Typography>}
              secondary={<Typography sx={{ fontSize: '.68rem', color: '#94a3b8' }}>{question.answers?.length || 0} תשובות</Typography>}
            />
            <Box sx={{ display: 'flex', gap: .3 }}>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleCopy(question); }} sx={{ color: '#94a3b8' }}>
                <ContentCopyIcon sx={{ fontSize: '1rem' }} />
              </IconButton>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDelete(question.id); }} sx={{ color: '#e57373' }}>
                <DeleteIcon sx={{ fontSize: '1rem' }} />
              </IconButton>
            </Box>
          </ListItem>
        ))}
      </List>
    )}

  </>
)}
                          </Box>
                          </Box>
                        )}

                        {/* ===================== תצוגת דסקטופ ===================== */}
                        {!isMobile && (
                        <Container maxWidth={false} sx={{ display: 'flex', gap: 2, px: 3 }}>

<Box sx={{ flex: 1, backgroundColor: '#f9f9f9', padding: 2, borderRadius: 2 }}>
<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333' }}>
     {selectedGame?.name || 'משחק נבחר'}
  </Typography>
  <Button
    onClick={() => setSelectedGame(null)}
    sx={{ borderRadius: '30px', px: 3, color: '#667eea', fontWeight: 600 }}
    startIcon={<span style={{ fontSize: '1.1rem' }}>→</span>}
  >
    חזרה למשחקים
  </Button>
</Box>

  {/* כפתורי ניווט */}
<Box data-tour="tabs" sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'nowrap', gap: 1, mb: 2 }}>
  <Button variant={viewMode === 'questions' ? 'contained' : 'outlined'} onClick={() => setViewMode('questions')} sx={{ borderRadius: '30px', px: 2.5, flexShrink: 0, whiteSpace: 'nowrap' }}>שאלות</Button>
  <Button variant={viewMode === 'players' ? 'contained' : 'outlined'} onClick={() => setViewMode('players')} sx={{ borderRadius: '30px', px: 2.5, flexShrink: 0, whiteSpace: 'nowrap' }}>שחקנים</Button>
  <Button variant={viewMode === 'groups' ? 'contained' : 'outlined'} onClick={() => setViewMode('groups')} sx={{ borderRadius: '30px', px: 2.5, flexShrink: 0, whiteSpace: 'nowrap' }}>קבוצות</Button>
  <Button variant={viewMode === 'settings' ? 'contained' : 'outlined'} onClick={() => setViewMode('settings')} sx={{ borderRadius: '30px', px: 2.5, flexShrink: 0, whiteSpace: 'nowrap' }}>הגדרות</Button>
</Box>

{viewMode === 'settings' ? (
  <Box sx={{ backgroundColor: '#f0f0f0', p: 2, borderRadius: 2 }}>
    <Typography variant="h6" gutterBottom>הגדרות המשחק</Typography>
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <TextField label="ניקוד לשאלה" type="number" fullWidth
          value={gameSettings.defaultPoints}
          onChange={(e) => setGameSettings({ ...gameSettings, defaultPoints: +e.target.value })} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField label="זמן תגובה (שניות)" type="number" fullWidth
          value={gameSettings.answerTime}
          onChange={(e) => setGameSettings({ ...gameSettings, answerTime: +e.target.value })} />
      </Grid>
      <Grid item xs={12}>
        <Select fullWidth value={gameSettings.isOpen}
          onChange={(e) => setGameSettings({ ...gameSettings, isOpen: e.target.value })}>
          <MenuItem value="true">פתוח לכולם</MenuItem>
          <MenuItem value="false">רק למאושרים</MenuItem>
        </Select>
      </Grid>
      <Grid item xs={12}>
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          p: 2, borderRadius: 3,
          background: gameSettings.allowSmartDevice ? 'linear-gradient(135deg, #eff6ff, #dbeafe)' : '#f8fafc',
          border: `1px solid ${gameSettings.allowSmartDevice ? '#93c5fd' : '#e2e8f0'}`,
          cursor: 'pointer', transition: 'all .2s',
        }}
        onClick={() => setGameSettings({ ...gameSettings, allowSmartDevice: !gameSettings.allowSmartDevice })}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography sx={{ fontSize: '1.5rem' }}>📱</Typography>
            <Box>
              <Typography sx={{ fontWeight: 700, color: gameSettings.allowSmartDevice ? '#1d4ed8' : '#94a3b8', fontSize: '.9rem' }}>
                הפעלה דרך מכשיר חכם
              </Typography>
              <Typography sx={{ fontSize: '.75rem', color: '#94a3b8' }}>
                אפשר לשחקנים להצטרף דרך קישור במכשיר חכם (אפליקציה)
              </Typography>
            </Box>
          </Box>
          <Box sx={{
            width: 44, height: 24, borderRadius: 12, position: 'relative',
            background: gameSettings.allowSmartDevice ? '#2563eb' : '#cbd5e1',
            transition: 'all .2s', flexShrink: 0,
          }}>
            <Box sx={{
              width: 18, height: 18, borderRadius: '50%', background: '#fff',
              boxShadow: '0 1px 3px rgba(0,0,0,.2)', transition: 'all .2s',
              position: 'absolute', top: 3,
              left: gameSettings.allowSmartDevice ? 23 : 3,
            }} />
          </Box>
        </Box>
      </Grid>
      <Grid item xs={12} textAlign="right">
        <Box sx={{ mt: 1 }}><Button variant="contained" color="primary" onClick={saveGameSettings} sx={{ borderRadius: '30px', px: 4 }}>שמור הגדרות</Button></Box>
      </Grid>
    </Grid>
  </Box>
) : viewMode === 'groups' ? (
  <Box>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 800 }}>קטגוריות</Typography>
      <Button size="small" variant="contained" onClick={() => { setNewCategoryName(''); setOpenCategoryDialog(true); }} sx={{ borderRadius: '20px', background: 'linear-gradient(135deg,#7c3aed,#a855f7)' }}>➕ הוסף קטגוריה</Button>
    </Box>
    {[...new Set([...groups.map(g => g.category || ''), ...extraCategories])].length === 0 ? (
      <Typography sx={{ color: '#94a3b8', textAlign: 'center', py: 3 }}>אין קטגוריות עדיין — הוסף קטגוריה</Typography>
    ) : [...new Set([...groups.map(g => g.category || ''), ...extraCategories])].map(cat => {
      const count = groups.filter(g => (g.category || '') === cat).length;
      const active = selectedGroupCategory === cat;
      return (
        <Box key={cat || '__none'} onClick={() => setSelectedGroupCategory(cat)} sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, mb: 1, borderRadius: 2.5, cursor: 'pointer',
          border: active ? '2px solid #a855f7' : '1px solid #e2e8f0', background: active ? '#faf5ff' : '#fff',
          boxShadow: active ? '0 3px 12px rgba(168,85,247,.2)' : '0 1px 3px rgba(0,0,0,.04)', transition: 'all .15s',
        }}>
          <Typography sx={{ fontWeight: 700, color: active ? '#7c3aed' : '#334155' }}>{cat || 'כללי'}</Typography>
          <Box sx={{ px: 1.2, borderRadius: 10, background: active ? '#a855f7' : '#ede9fe', color: active ? '#fff' : '#7c3aed', fontSize: '.72rem', fontWeight: 800 }}>{count}</Box>
        </Box>
      );
    })}
    <Dialog open={openCategoryDialog} onClose={() => setOpenCategoryDialog(false)} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 800 }}>🏷️ הוספת קטגוריה</DialogTitle>
      <DialogContent sx={{ pt: '16px !important', direction: 'rtl', textAlign: 'right' }}>
        <TextField autoFocus fullWidth label="שם הקטגוריה" placeholder="עיר / כיתה / גילאים..." value={newCategoryName}
          onChange={(e) => setNewCategoryName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && newCategoryName.trim()) { const n = newCategoryName.trim(); setExtraCategories(p => [...new Set([...p, n])]); setSelectedGroupCategory(n); setOpenCategoryDialog(false); setNewCategoryName(''); } }}
          sx={{ mt: 1 }} />
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button variant="contained" disabled={!newCategoryName.trim()} onClick={() => { const n = newCategoryName.trim(); setExtraCategories(p => [...new Set([...p, n])]); setSelectedGroupCategory(n); setOpenCategoryDialog(false); setNewCategoryName(''); }} sx={{ borderRadius: 2, background: 'linear-gradient(135deg,#7c3aed,#a855f7)' }}>הוסף</Button>
        <Button onClick={() => setOpenCategoryDialog(false)} sx={{ color: '#888' }}>ביטול</Button>
      </DialogActions>
    </Dialog>
  </Box>
) : viewMode === 'players' ? (
  <>
    <Typography variant="h5" gutterBottom>רשימת שחקנים</Typography>
    {players.length === 0 ? (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography sx={{ fontSize: '3.5rem', mb: 1 }}>👥</Typography>
        <Typography variant="h6" sx={{ color: '#94a3b8', fontWeight: 700, mb: 1 }}>אין שחקנים עדיין</Typography>
        <Typography sx={{ color: '#cbd5e1', fontSize: '.9rem' }}>שחקנים יופיעו כאן אחרי הפעלת המשחק</Typography>
      </Box>
    ) : (
    <List>
      {players.map((player) => (
        <ListItem key={player.phone} sx={{ borderRadius: '30px', opacity: player.disabled ? 0.5 : 1 }} button
          selected={selectedPlayer?.phone === player.phone}
          onClick={() => setSelectedPlayer(player)}>
          <ListItemText
            primary={<span style={{ textDecoration: player.disabled ? 'line-through' : 'none', color: player.disabled ? '#94a3b8' : 'inherit' }}>{player.name || "ללא שם"}{player.disabled ? ' (מושבת)' : ''}</span>}
            secondary={`טלפון: ${player.phone}`}
          />
          <ListItemSecondaryAction>
            <Tooltip title={player.disabled ? 'הפעל שחקן' : 'השבת שחקן'}>
              <IconButton edge="end" onClick={() => handleTogglePlayerDisabled(player)}
                sx={{ color: player.disabled ? '#10b981' : '#f59e0b', mr: .5 }}>
                {player.disabled ? <PersonIcon /> : <PersonOffIcon />}
              </IconButton>
            </Tooltip>
            <IconButton edge="end" color="error" onClick={() => handleDeletePlayer(player)}><DeleteIcon /></IconButton>
          </ListItemSecondaryAction>
        </ListItem>
      ))}
    </List>
    )}
  </>
) : (
  <>
    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
      <Typography variant="h5" gutterBottom>רשימת שאלות</Typography>
      {loadingXL ? (
        <Button variant="outlined" disabled sx={{ borderRadius: '30px', px: 3 }}>
          {uploadEnd ? "עלה בהצלחה" : `מעלה... ${uploadProgress}%`}
          <CircularProgress size={20} sx={{ ml: 1 }} />
        </Button>
      ) : (
        <Button variant="outlined" component="label" sx={{ borderRadius: '30px', px: 3 }}>
          ייבוא שאלות
          <input type="file" accept=".xlsx" hidden onChange={handleImportQuestions} />
        </Button>
      )}
    </Box>
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId="questions-desktop">
        {(provided) => (
          <List {...provided.droppableProps} ref={provided.innerRef}>
            {questions.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography sx={{ fontSize: '3rem', mb: 1 }}>❓</Typography>
                <Typography variant="h6" sx={{ color: '#94a3b8', fontWeight: 700, mb: .5 }}>אין שאלות עדיין</Typography>
                <Typography sx={{ color: '#cbd5e1', fontSize: '.85rem', mb: 2 }}>הוסף שאלות בצד ימין או ייבא מקובץ Excel</Typography>
              </Box>
            ) : questions.map((question, index) => (
              <Draggable key={String(question.id)} draggableId={String(question.id)} index={index}>
                {(provided) => (
                  <ListItem ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}
                    button selected={selectedQuestion?.id === question.id}
                    onClick={() => setSelectedQuestion(question)}
                    sx={{ borderRadius: '30px', backgroundColor: selectedQuestion?.id === question.id ? '#e0e0e0' : 'inherit' }}>
                    <ListItemText primary={question.q} />
                    <ListItemSecondaryAction>
                      <IconButton edge="end" color="error" onClick={() => handleDelete(question.id)}><DeleteIcon /></IconButton>
                      <IconButton edge="end" color="primary" onClick={() => handleCopy(question)}><ContentCopyIcon /></IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </List>
        )}
      </Droppable>
    </DragDropContext>
  </>
)}

</Box>

<Box sx={{ flex: 2, backgroundColor: '#ffffff', padding: 2, pb: 0, borderRadius: 2 }}>
{viewMode === 'questions' ? (
             <Box>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                                  <Button
                                    variant="contained"
                                    onClick={() => { setAiPreview(null); setAiDlg(true); }}
                                    sx={{ borderRadius: '30px', px: 3, fontWeight: 800, background: 'linear-gradient(135deg,#a855f7,#6366f1)', boxShadow: '0 4px 16px rgba(168,85,247,.4)', '&:hover': { boxShadow: '0 6px 24px rgba(168,85,247,.55)' } }}
                                  >
                                    ✨ צור שאלות ב-AI
                                  </Button>
                                </Box>
                                <Grid data-tour="add-question" container spacing={2}>
                                    {questionTypes.map((type) => (
                                        <Grid item xs={6} sm={3} key={type.type}>
                                            <Paper
                                                sx={{
                                                    padding: 2,
                                                    backgroundColor: "#fff",
                                                    borderRadius: 2,
                                                    textAlign: 'center',
                                                    cursor: 'pointer',
                                                    boxShadow: selectedType === type.type ? 3 : 1,
                                                    transition: 'all 0.3s ease',
                                                    '&:hover': { boxShadow: 3 },
                                                    position: 'relative',  // נדרש כדי למקם את הפלוס בתור child יחסית לריבוע
                                                }}
                                                onClick={() => handleAddQuestion(type.type)}
                                            >
                                                {/* סימן פלוס בפינה */}
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        top: -10,
                                                        left: -10,
                                                        backgroundColor: '#fff',
                                                        borderRadius: '50%',
                                                        width: 30,
                                                        height: 30,
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        boxShadow: 2,
                                                        cursor: 'pointer',
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation(); // מונע שהלחיצה תעבור לריבוע עצמו
                                                        handleAddQuestion(type.type);
                                                    }}
                                                >
                                                    <Typography sx={{ fontSize: 24, fontWeight: 'bold', color: '#2196f3' }}>+</Typography>
                                                </Box>

                                                {/* אייקון וסוג השאלה */}
                                                {type.icon}
                                                <Typography variant="body1" sx={{ color: "black" }}>
                                                    {type.label}
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                    ))}
                                </Grid>

                                <Grid sx={{ mt: 0.8 }} container spacing={2} alignItems="center">
                                    {/* עריכת שאלה */}
                                    <Grid item xs={6}>
                                        <Typography variant="h5" gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' } }}>
                                            עריכת שאלה
                                        </Typography>
                                    </Grid>

                                    {/* סוג השאלה */}
                                    <Grid item xs={6} display="flex" justifyContent="flex-end">
                                        <Select
                                            sx={{ minWidth: 120, textAlign: 'right', '& .MuiSelect-select': { textAlign: 'right' } }}
                                            size="small"
                                            disabled={selectedQuestion ? false : true}
                                            value={selectedQuestion?.type || 'בחר שאילה'}
                                            onChange={(e) => handleChangeQuestionType(e.target.value)}

                                        >
                                            {questionTypes.map((type) => (
                                                <MenuItem key={type.type} value={type.type}>
                                                    {type.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </Grid>

                                </Grid>

{selectedQuestion ? (
<CardContent sx={{ cursor: '' }}>  
  
  {/* זמן וניקוד פר-שאלה (עוקף את הגדרות המשחק) */}
  <Box dir="" sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', direction: '' }}>
    <TextField type="number" size="small" label="⏱️ זמן מענה (שניות)" placeholder={`ברירת מחדל: ${gameSettings.answerTime || 20}`}
      value={selectedQuestion.answerTime || ''} onChange={(e) => { const v = e.target.value ? +e.target.value : null; setSelectedQuestion(prev => ({ ...prev, answerTime: v })); updateData('answerTime', v); }}
      sx={{ flex: 1, minWidth: 160 }} InputLabelProps={{ shrink: true }} />
      <TextField type="number" size="small" label="🏆 ניקוד לשאלה" placeholder={(selectedQuestion.type !== 's' && selectedQuestion.type !== 'S' && selectedQuestion.type !== 'g') ? `ברירת מחדל: ${gameSettings.defaultPoints || 100}` : ""}
        value={selectedQuestion.points || ''} onChange={(e) => { const v = e.target.value ? +e.target.value : null; setSelectedQuestion(prev => ({ ...prev, points: v })); updateData('points', v); }}
        sx={{ flex: 1, minWidth: 160 }} InputLabelProps={{ shrink: true }} disabled={selectedQuestion.type === 's' || selectedQuestion.type === 'S' || selectedQuestion.type === 'g'} />
    <Button variant="outlined" onClick={() => {
      const payload = encodeURIComponent(JSON.stringify({
        q: (selectedQuestion.q || '').replace(/<[^>]*>/g, ''),
        answers: selectedQuestion.answers || [],
        type: selectedQuestion.type,
        correctAnswer: selectedQuestion.correctAnswer,
        answerTime: selectedQuestion.answerTime,
      }));
      window.open(`/play/demo?theme=${selectedGame?.theme || 'classic'}&q=${payload}`, '_blank');
    }} sx={{ borderRadius: 2, fontWeight: 700, borderColor: '#a855f7', color: '#a855f7', flexShrink: 0 }}>
      👁 תצוגה מקדימה
    </Button>
  </Box>
  {/* עריכת שאילות */}
  <Grid container spacing={2}>
    <Grid item xs={12} sm={12} md={12} key={selectedQuestion.id}>
      <Card sx={{ borderRadius: '50px', marginBottom: '20px' }}>
        <CardContent
          contentEditable
          suppressContentEditableWarning
          onBlur={(e) => handleEdit('q', e.target.innerText)}
          dangerouslySetInnerHTML={{ __html: selectedQuestion.q }}
          style={{
            minHeight: '40px',
            padding: '10px',
            border: 'none',
            boxShadow: 'none',
            fontFamily: 'Roboto, sans-serif',
            fontSize: '18px',
            lineHeight: '1.5',
            whiteSpace: 'pre-wrap',
            outline: 'none',
            marginRight: '12px',
            color: 'rgba(0, 0, 0, .8)',
          }}
        />
      </Card>
    </Grid>
  </Grid>

  {selectedQuestion.type === 'g' && (
    <Box sx={{ mb: 3, p: 2, borderRadius: 3, background: '#faf5ff', border: '1px solid #e9d5ff' }}>
      <Typography sx={{ fontWeight: 700, color: '#7c3aed', mb: .5, fontSize: '.95rem' }}>🏷️ קטגוריית קבוצות לשאלה</Typography>
      <Typography sx={{ fontSize: '.78rem', color: '#94a3b8', mb: 1.5 }}>בחר קטגוריה — התשובות יהפכו אוטומטית לקבוצות שבקטגוריה, וכל מי שיבחר ישויך לאותה קבוצה.</Typography>
      {[...new Set(groups.map(g => g.category).filter(Boolean))].length === 0 ? (
        <Typography sx={{ fontSize: '.82rem', color: '#ef4444', fontWeight: 600 }}>אין קטגוריות עדיין — צור קבוצות עם קטגוריה דרך כפתור "קבוצות".</Typography>
      ) : (
        <Select size="small" fullWidth displayEmpty value={selectedQuestion.groupCategory || ""} onChange={(e) => applyGroupCategory(e.target.value)} sx={{ borderRadius: 3, bgcolor: '#fff', fontWeight: 700 }}>
          <MenuItem value=""><em>בחר קטגוריה</em></MenuItem>
          {[...new Set(groups.map(g => g.category).filter(Boolean))].map(cat => (
            <MenuItem key={cat} value={cat}>{cat} ({groups.filter(g => g.category === cat).length} קבוצות)</MenuItem>
          ))}
        </Select>
      )}
    </Box>
  )}

  <Grid container spacing={2}>
    {selectedQuestion.type === 'g' ? (
      ((selectedQuestion.answers || []).filter(Boolean).length > 0)
        ? (selectedQuestion.answers || []).filter(Boolean).map((ans, index) => (
            <Grid item xs={12} sm={6} md={6} key={index}>
              <Card sx={{ borderRadius: '18px', display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, border: '2px solid #a855f7', background: '#faf5ff' }}>
                <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: '#a855f7', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, flexShrink: 0 }}>{index + 1}</Box>
                <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: '#4c1d95' }}>{ans}</Typography>
                <Typography sx={{ ml: 'auto', fontSize: '.68rem', color: '#a855f7', fontWeight: 700 }}>קבוצה</Typography>
              </Card>
            </Grid>
          ))
        : <Grid item xs={12}><Typography sx={{ textAlign: 'center', color: '#94a3b8', py: 2 }}>בחר קטגוריה למעלה — התשובות יהיו הקבוצות שבקטגוריה</Typography></Grid>
    ) : selectedQuestion?.answers.map((answer, index) => (
      <Grid item xs={12} sm={6} md={6} key={index} className="card-container" sx={{ position: 'relative' }}>
        <Card
          sx={{
            paddingRight: '15px',
            paddingLeft: '15px',
            borderRadius: '50px',
            display: 'flex',
            alignItems: 'center',
            backgroundColor: 'transparent',
          }}
          className="card-hover" // מגדיר את הקלאס כדי לשלוט בריחוף
        >
          {/* מספר התשובה */}
          <Typography
            sx={{
              fontFamily: 'Merriweather, serif',
              fontSize: '20px',
              fontWeight: 'bold',
              color: 'primary',
            }}
          >
            {index + 1}
          </Typography>

          {/* התשובה */}
          <CardContent
            contentEditable
            suppressContentEditableWarning
            onBlur={(e) => handleEdit(index, e.target.innerText)}
            dangerouslySetInnerHTML={{ __html: answer }}
            style={{
              flexGrow: 1,
              minHeight: '40px',
              padding: '10px',
              border: 'none',
              boxShadow: 'none',
              fontFamily: 'Roboto, sans-serif',
              fontSize: '18px',
              lineHeight: '1.5',
              whiteSpace: 'pre-wrap',
              outline: 'none',
              marginRight: '10px',
              color: 'rgba(0, 0, 0, 0.8)',
            }}
          />
          {/* כפתור מחיקה בצד שמאל */}
          <IconButton edge="end" color="error" onClick={() => handleDeleteAnswer(index)}>
            <DeleteIcon />
          </IconButton>
        </Card>

        {/* שאלת קבוצה: בורר קבוצה לכל תשובה. אחרת: אליפסת "תשובה נכונה" בריחוף */}
        {selectedQuestion.type === 'g' ? (
          <Box sx={{ position: 'absolute', bottom: '-16px', left: '50%', transform: 'translateX(-50%)', zIndex: 5, width: '85%' }}>
            <Select
              size="small"
              displayEmpty
              value={(selectedQuestion.answerGroups && selectedQuestion.answerGroups[index]) || ""}
              onChange={(e) => handleChangeAnswerGroup(index, e.target.value)}
              sx={{ width: '100%', borderRadius: '50px', bgcolor: '#fff', fontSize: '.8rem', fontWeight: 700, boxShadow: '0 2px 8px rgba(168,85,247,.25)', '& fieldset': { borderColor: '#a855f7' } }}
            >
              <MenuItem value=""><em>בחר קבוצה לתשובה זו</em></MenuItem>
              {groups.map((g) => (
                <MenuItem key={g.id} value={g.id}>{g.category ? `${g.category} › ${g.name}` : g.name}</MenuItem>
              ))}
            </Select>
          </Box>
        ) : (selectedQuestion.type === 's' || selectedQuestion.type === 'S') ? null : (
          <Box
            sx={{
              cursor: 'pointer',
              position: 'absolute',
              bottom: '-10px', // מציב את האליפסה בדיוק בתחתית של הריבוע
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: selectedQuestion.correctAnswer !== index + 1 ? '#fff' : '#4caf50',
              borderRadius: '50px',
              padding: '1px 15px',
              fontSize: '14px',
              color: selectedQuestion.correctAnswer !== index + 1 ? '#4caf50' : '#fff',
              fontWeight: 'bold',
              textAlign: 'center',
              visibility: selectedQuestion.correctAnswer !== index + 1 ? 'hidden' : 'visible',
              opacity: selectedQuestion.correctAnswer !== index + 1 ? 0 : 1,
              transition: 'visibility 0.3s, opacity 0.3s',
              border: '1px solid #4caf50',
              zIndex: 5,
            }}
            className="ellipse-hover"
            onClick={() => handleChangeCorrectAnswer(index + 1)}
          >
            תשובה נכונה
          </Box>
        )}
      </Grid>
    ))}
          {/* כאן מוסיף את ה-CSS */}
          <style jsx>{`
        .card-container:hover .ellipse-hover {
          visibility: visible;
          opacity: 1;
        }
      `}</style>

  </Grid>
 {/* כפתור הוספת תשובה — לא בשאלת קבוצה (שם התשובות הן הקבוצות) */}
  {selectedQuestion.type !== 'g' && (
  <Grid container justifyContent="end" sx={{ marginTop: '30px' }}>
  <div
    style={{
      padding: '10px 20px',
      backgroundColor: '#4caf50',
      color: 'white',
      borderRadius: '50px',
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: 'bold',
    }}
    onClick={addAwsToAPI}
  >
    הוספת תשובה
  </div>
</Grid>
  )}
{(selectedQuestion.type === 'p' || selectedQuestion.type === 'm') && (
<Box sx={{ mt: 3 }}>
  <Typography variant="subtitle1" gutterBottom>
    {selectedQuestion.type === 'p' ? 'תמונה' : 'סרטון'} לשאלה
  </Typography>

  <Grid container spacing={2} alignItems="center">
    {/* צד שמאל – תצוגת מדיה עם לחיצה לפתיחה */}
    <Grid item xs={12} sm={5}>
      <Box
        sx={{
          cursor: mediaUploading ? 'default' : 'pointer',
          width: '100%',
          maxWidth: { xs: '100%', sm: 250 },
          minHeight: { xs: 150, sm: 200 },
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid #ccc',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        onClick={() => !mediaUploading && setPreviewOpen(true)}
      >
        {mediaUploading ? (
          <Box sx={{
            width: '100%',
            height: '100%',
            minHeight: 200,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            bgcolor: '#f9f5ff',
            position: 'relative',
          }}>
            <IconButton
              size="small"
              onClick={() => {
                if (uploadAbortRef.current) {
                  uploadAbortRef.current.abort();
                  uploadAbortRef.current = null;
                }
                setMediaUploading(false);
                setMediaUploadProgress(0);
                showSnack('ההעלאה בוטלה', 'warning');
              }}
              sx={{ position: 'absolute', top: 8, left: 8, bgcolor: 'rgba(0,0,0,0.05)', '&:hover': { bgcolor: 'rgba(0,0,0,0.1)' } }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              <CircularProgress
                variant="determinate"
                value={mediaUploadProgress}
                size={70}
                thickness={4}
                sx={{ color: '#764ba2' }}
              />
              <Box sx={{ position: 'absolute', top: 0, left: 0, bottom: 0, right: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="caption" sx={{ fontWeight: 800, fontSize: '0.95rem', color: '#764ba2' }}>
                  {mediaUploadProgress}%
                </Typography>
              </Box>
            </Box>
            <Typography variant="body2" sx={{ color: '#764ba2', fontWeight: 600, fontSize: '0.95rem' }}>
              מעלה...
            </Typography>
          </Box>
        ) : selectedQuestion.type === 'p' ? (
          <img
            src={selectedQuestion.link || "https://lawyerrafi.com/placeholder.svg"}
            alt="תמונה"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <video style={{ width: '100%', display: 'block' }}>
            <source src={selectedQuestion.link || "https://lawyerrafi.com/placeholder.svg"} />
            לא ניתן להציג מדיה
          </video>
        )}
      </Box>
    </Grid>

    {/* צד ימין – שדה קישור, העלאה, שמירה */}
    <Grid item xs={12} sm={7}>
      <TextField
        fullWidth
        label="קישור לקובץ"
        value={selectedQuestion.link || ''}
        onChange={(e) => {
          const newLink = e.target.value;
          setSelectedQuestion((prev) => ({ ...prev, link: newLink }));
        }}
        variant="outlined"
        sx={{ mb: 2 }}
      />

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Button variant="outlined" component="label" disabled={mediaUploading}>
          {mediaUploading ? 'מעלה...' : 'העלאת קובץ'}
            <input
              type="file"
              accept={selectedQuestion.type === 'p' ? 'image/*' : 'video/*'}
              hidden
              onChange={async (e) => {
                const file = e.target.files[0];
                if (!file) return;

                const formData = new FormData();
                formData.append("file", file);
                formData.append("user_id", userId);

                setMediaUploading(true);
                setMediaUploadProgress(0);

                const abortController = new AbortController();
                uploadAbortRef.current = abortController;

                try {
                  const res = await axios.post(`${API_URL}/upload_file`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${getToken()}` },
                    signal: abortController.signal,
                    onUploadProgress: (progressEvent) => {
                      const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                      setMediaUploadProgress(percent);
                    },
                  });

                  const data = res.data;
                  if (data?.error === 'storage_limit') {
                    showSnack(data.message || 'חרגת ממכסת האחסון', 'warning');
                  } else if (data?.link) {
                    setSelectedQuestion((prev) => ({
                      ...prev,
                      link: data.link
                    }));
                    // שמירה אוטומטית של הקישור בשרת
                    await fetch(`${API_URL}/updateData`, {
                      method: 'POST',
                      headers: getAuthHeaders(),
                      body: JSON.stringify({
                        game_id: selectedGame.id,
                        id: selectedQuestion.id,
                        type: 'link',
                        value: data.link
                      }),
                    });
                    setQuestions((prev) => prev.map((q) => q.id === selectedQuestion.id ? { ...q, link: data.link } : q));
                    showSnack("הקובץ הועלה ונשמר בהצלחה");
                  } else {
                    showSnack("שגיאה בהעלאת קובץ", "error");
                  }
                } catch (err) {
                  if (axios.isCancel(err)) return;
                  console.error(err);
                  showSnack("שגיאה בהעלאת קובץ", "error");
                } finally {
                  uploadAbortRef.current = null;
                  setMediaUploading(false);
                  setMediaUploadProgress(0);
                }
              }}
            />
          </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={() => updateData("link", selectedQuestion.link)}
        >
          שמור קישור
        </Button>
      </Box>
    </Grid>
  </Grid>

  {/* דיאלוג תצוגה גדולה */}
  <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="lg" sx={{ '& .MuiDialog-paper': { direction: 'rtl', borderRadius: '20px', overflow: 'hidden' } }}>
    <DialogContent>
      {selectedQuestion.type === 'p' ? (
        <img
          src={selectedQuestion.link || "https://lawyerrafi.com/placeholder.svg"}
          alt="תמונה"
          style={{ maxWidth: '100%', height: 'auto', borderRadius: 8 }}
        />
      ) : (
        <video controls style={{ width: '100%' }}>
          <source src={selectedQuestion.link || "https://lawyerrafi.com/placeholder.svg"} />
          לא ניתן להציג מדיה
        </video>
      )}
    </DialogContent>
  </Dialog>
</Box>

)}
 

</CardContent>
) : (
  <Typography variant="body1" color="textSecondary">בחר שאלה לעריכה</Typography>
)}
</Box>                                

) : viewMode === 'players' ? (
  <Box>
<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
  <Typography variant="h5" sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' } }}>עריכת שחקן</Typography>

  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button
    data-tour="add-player"
    variant="outlined"
    component="label"
    sx={{ borderRadius: '30px', px: { xs: 2, sm: 3 }, fontSize: { xs: '.8rem', sm: '.875rem' } }}
    onClick={() => setOpenPlayerDialog(true)}
  >
        הוסף שחקן
      </Button>

{loadingXL ? (
  <Button variant="outlined" disabled sx={{ borderRadius: '30px', px: 3 }}>
    {uploadEnd
      ? "עלה בהצלחה"
      : `מעלה... ${uploadProgress}%`
    }
    <CircularProgress size={20} sx={{ ml: 1 }} />
  </Button>
) : (
  <Button
    variant="outlined"
    component="label"
    sx={{ borderRadius: '30px', px: 3 }}
  >
    ייבוא שחקנים
    <input type="file" accept=".xlsx" hidden onChange={handleImportPlayers2} />
  </Button>
)}




    <Button
      variant="outlined"
      onClick={handleExportPlayers}
      sx={{ borderRadius: '30px', px: 2 }}
    >
      ייצוא
    </Button>
  </Box>
</Box>


    {/* תוכן עריכת שחקן */}
    {selectedPlayer ? (
      <Box sx={{ padding: 2, backgroundColor: '#f0f0f0', borderRadius: 2 }}>
<Grid container spacing={2}>
  {/* שם */}
  <Grid item xs={12} sm={6}>
    <TextField
      label="שם"
      fullWidth
      value={selectedPlayer.name || ""}
      onChange={(e) =>
        setSelectedPlayer((prev) => ({ ...prev, name: e.target.value }))
      }
    />
  </Grid>

  {/* טלפון */}
  <Grid item xs={12} sm={6}>
    <TextField
      label="טלפון"
      fullWidth
      value={selectedPlayer.phone || ""}
      onChange={(e) =>
        setSelectedPlayer((prev) => ({ ...prev, phone: e.target.value }))
      }
    />
  </Grid>
  <Grid item xs={12} sm={6}>
    <TextField
      select
      label="קבוצה"
      fullWidth
      value={selectedPlayer.group_id ?? ""}
      onChange={(e) =>
        setSelectedPlayer((prev) => ({
          ...prev,
          group_id: e.target.value === '' ? null : +e.target.value,
        }))
      }
    >
        <MenuItem key={0} value="">
          ללא קבוצה 
        </MenuItem>
      {Array.isArray(groups) && groups.map((group) => (
        <MenuItem key={group.id} value={group.id}>
          {group.name || `קבוצה ${group.id}`}
        </MenuItem>
      ))}
    </TextField>
  </Grid>


  {/* נקודות */}
  <Grid item xs={12} sm={6}>
    <TextField
      label="נקודות"
      type="number"
      fullWidth
      value={selectedPlayer.point || 0}
      onChange={(e) =>
        setSelectedPlayer((prev) => ({
          ...prev,
          point: +e.target.value,
        }))
      }
    />
  </Grid>

  {/* כפתור שמירה */}
  <Grid item xs={12}>
    <Button
      variant="contained"
      onClick={async () => {
        await fetch(`${API_URL}/update_player`, {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            game_id: selectedGame.id,
            ...selectedPlayer,
          }),
        });

        setPlayers((prev) =>
          prev.map((p) =>
            p.id === selectedPlayer.id
              ? { ...p, ...selectedPlayer }
              : p
          )
        );
      }}
    >
      שמור שחקן
    </Button>
  </Grid>
</Grid>

      </Box>
    ) : (
      <Typography>בחר שחקן לעריכה</Typography>
    )}
  </Box>
) : viewMode === 'groups' ? (
  <Box>
<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
  <Typography variant="h5">{selectedGroupCategory === null ? '🏷️ בחר קטגוריה מימין' : `קבוצות: ${selectedGroupCategory || 'כללי'}`}</Typography>

  <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => saveOpenGroups(gameSettings.isGroups ? "false" : "true")}
          sx={{ borderRadius: '30px', px: 3, background: gameSettings.isGroups ? undefined : 'linear-gradient(135deg,#667eea,#764ba2)' }}
        >
          {gameSettings.isGroups ? "השבת קבוצות" : "הפעל קבוצות"}
        </Button>
        <Button
    variant="outlined"
    component="label"
    disabled={selectedGroupCategory === null}
    sx={{ borderRadius: '30px', px: 3 }}
    onClick={() => { setGroupCategory(selectedGroupCategory || ''); setNewCatMode(false); setOpenGroupDialog(true); }}
  >
        ➕ הוסף קבוצה
      </Button>
    <Dialog open={openGroupDialog} onClose={() => setOpenGroupDialog(false)} sx={{ ...dialogPaperSx, ...(showTour ? dialogTourBoostSx : {}) }} TransitionComponent={Slide} TransitionProps={{ direction: 'up' }}>
        <DialogTitle sx={dialogTitleSx}>
          <GroupAddIcon /> הוספת קבוצה חדשה
        </DialogTitle>
        <DialogContent sx={{ pt: '24px !important', px: 3, pb: 2, direction: 'rtl', textAlign: 'right' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            הזן שם לקבוצה. אפשר לשייך לקטגוריה (למשל: עיר, כיתה, גילאים) כדי לקבץ קבוצות יחד.
          </Typography>
          <TextField
            autoFocus
            label="שם הקבוצה"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddGroup(selectedGame.id)}
            fullWidth
            variant="outlined"
            sx={{ mb: 2 }}
          />
          <Typography variant="body2" sx={{ mb: .75, fontWeight: 600, color: '#475569' }}>קטגוריה</Typography>
          {categoryFieldBlock}
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button onClick={() => handleAddGroup(selectedGame.id)} variant="contained" sx={{ borderRadius: 2, px: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            ➕ הוסף קבוצה
          </Button>
          <Button onClick={() => setOpenGroupDialog(false)} sx={{ borderRadius: 2, color: '#888' }}>ביטול</Button>
        </DialogActions>
      </Dialog>
  </Box>
</Box>
<Grid container spacing={2}>

<Grid item xs={6} sm={12}>
  {selectedGroupCategory !== null ? (
    <Box
      sx={{
        p: 2,
        bgcolor: '#f5f6f8',
        borderRadius: 3,
      }}
    >
      <List sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {groups.filter(g => (g.category || '') === selectedGroupCategory).map((group) => {
          const isEditing = editGroupId === group.id;

          return (
            <ListItem
              key={group.id}
              sx={{
                bgcolor: '#fff',
                borderRadius: 2,
                px: 2,
                py: 1.5,
                border: '1px solid #e0e0e0',
                boxShadow: '0 3px 8px rgba(0,0,0,0.08)',
                transition: '0.2s',
                '&:hover': {
                  borderColor: 'primary.main',
                  boxShadow: '0 6px 14px rgba(0,0,0,0.12)',
                },
              }}
            >
              {!isEditing ? (
                <>
                  {/* תצוגה */}
                  <Typography
                    sx={{
                      flex: 1,
                      fontSize: '1rem',
                      fontWeight: 600,
                      color: '#222',
                    }}
                  >
                    {group.name || 'ללא שם'}
                  </Typography>

                  <IconButton
                    size="small"
                    onClick={() => {
                      setEditGroupId(group.id);
                      setEditedName(group.name ?? '');
                    }}
                  >
                    <Edit color="primary" />
                  </IconButton>

                  <IconButton
                    size="small"
                    onClick={() => deleteGroup(selectedGame.id, group.id)}
                  >
                    <Delete sx={{ color: '#d32f2f' }} />
                  </IconButton>
                </>
              ) : (
                <>
                  {/* עריכה */}
                  <TextField
                    variant="standard"
                    fullWidth
                    autoFocus
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    color="primary"
                  />
                          <IconButton
                            color="success"
                            onClick={() => {
                              saveEditGroup(selectedGame.id, group.id, editedName);
                              setEditGroupId(null);
                            }}
                          >
                            <Check />
                          </IconButton>

                          <IconButton
                            color="error"
                            onClick={() => {
                              setEditGroupId(null);
                              setEditedName('');
                            }}
                          >
                            <Close />
                          </IconButton>
                </>
              )}
            </ListItem>
          );
        })}
      </List>
    </Box>
  ) : (
    <Box sx={{ textAlign: 'center', py: 8 }}>
      <Typography sx={{ fontSize: '3rem', mb: .5 }}>🏷️</Typography>
      <Typography sx={{ color: '#94a3b8', fontWeight: 700 }}>בחר קטגוריה מהרשימה מימין</Typography>
      <Typography sx={{ color: '#cbd5e1', fontSize: '.85rem' }}>הקבוצות שבקטגוריה יוצגו כאן</Typography>
    </Box>
  )}
</Grid>



</Grid>
  </Box>
) : viewMode === 'settings' ? (
                                    <Box dir="rtl" sx={{ p: 1, direction: 'rtl', textAlign: 'right' }}>
                                      {themePickerBlock}
                                    </Box>
                                ) : (

                                    <Box sx={{ textAlign: 'center', py: 8 }}>
                                      <Typography sx={{ fontSize: '3rem', mb: 1 }}>✏️</Typography>
                                      <Typography variant="h6" sx={{ color: '#94a3b8', fontWeight: 700, mb: .5 }}>
                                        {questions.length === 0 ? 'אין שאלות עדיין' : 'בחר שאלה לעריכה'}
                                      </Typography>
                                      <Typography sx={{ color: '#cbd5e1', fontSize: '.85rem' }}>
                                        {questions.length === 0 ? 'הוסף שאלות מהפאנל השמאלי כדי להתחיל' : 'לחץ על שאלה מהרשימה כדי לערוך אותה'}
                                      </Typography>
                                    </Box>
                                )}
                            </Box>

{/* ===== פאנל מידע + הפעלה (שמאל) ===== */}
<Box sx={{ width: { xs: '100%', md: 220 }, minWidth: { md: 220 }, display: { xs: 'none', md: 'flex' }, flexDirection: 'column', gap: 2, position: { md: 'sticky' }, top: 80, alignSelf: 'flex-start' }}>
  {/* כרטיס מידע */}
  <Box sx={{ borderRadius: 4, overflow: 'hidden', background: '#fff', boxShadow: '0 2px 16px rgba(0,0,0,.06)', border: '1px solid #e2e8f0' }}>
    <Box sx={{ p: 2, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff', textAlign: 'center' }}>
      <Typography sx={{ fontWeight: 800, fontSize: '1rem', mb: .3 }}>🎮 {selectedGame?.name}</Typography>
      <Typography sx={{ fontSize: '.7rem', opacity: .8 }}>ID: {selectedGame?.id?.slice(0, 10)}</Typography>
    </Box>
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', py: .8, borderBottom: '1px solid #f1f5f9' }}>
        <Typography sx={{ fontSize: '.8rem', color: '#94a3b8' }}>❓ שאלות</Typography>
        <Typography sx={{ fontSize: '.8rem', fontWeight: 700, color: '#667eea' }}>{questions.length}</Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', py: .8, borderBottom: '1px solid #f1f5f9' }}>
        <Typography sx={{ fontSize: '.8rem', color: '#94a3b8' }}>👥 שחקנים</Typography>
        <Typography sx={{ fontSize: '.8rem', fontWeight: 700, color: '#3b82f6' }}>{players.length}</Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', py: .8, borderBottom: '1px solid #f1f5f9' }}>
        <Typography sx={{ fontSize: '.8rem', color: '#94a3b8' }}>⏱️ זמן תשובה</Typography>
        <Typography sx={{ fontSize: '.8rem', fontWeight: 700, color: '#f59e0b' }}>{gameSettings.answerTime} שנ׳</Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', py: .8, borderBottom: '1px solid #f1f5f9' }}>
        <Typography sx={{ fontSize: '.8rem', color: '#94a3b8' }}>🏆 ניקוד</Typography>
        <Typography sx={{ fontSize: '.8rem', fontWeight: 700, color: '#10b981' }}>{gameSettings.defaultPoints}</Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', py: .8, borderBottom: '1px solid #f1f5f9' }}>
        <Typography sx={{ fontSize: '.8rem', color: '#94a3b8' }}>🔓 סטטוס</Typography>
        <Chip label={gameSettings.isOpen ? 'פתוח' : 'סגור'} size="small" sx={{ height: 20, fontSize: '.65rem', fontWeight: 700, bgcolor: gameSettings.isOpen ? '#ecfdf5' : '#fef2f2', color: gameSettings.isOpen ? '#16a34a' : '#dc2626' }} />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', py: .8 }}>
        <Typography sx={{ fontSize: '.8rem', color: '#94a3b8' }}>📱 מכשיר חכם</Typography>
        <Chip label={gameSettings.allowSmartDevice ? 'מופעל' : 'כבוי'} size="small" sx={{ height: 20, fontSize: '.65rem', fontWeight: 700, bgcolor: gameSettings.allowSmartDevice ? '#eff6ff' : '#f1f5f9', color: gameSettings.allowSmartDevice ? '#2563eb' : '#94a3b8' }} />
      </Box>
    </Box>
  </Box>

  {/* כפתור הפעלה */}
  <Button
    data-tour="activate-game"
    variant="contained" fullWidth
    startIcon={<PlayArrowIcon />}
    onClick={async () => {
      if (quota && quota.gamesAllowed !== -1) {
        const activationsUsed = quota.activationsUsed || 0;
        if (activationsUsed >= quota.gamesAllowed) { setShowPackageDialog(true); return; }
      }
      // בדיקת מגבלת שחקנים לפני פתיחת דיאלוג הפעלה
      try {
        const gpR = await fetch(`${API_URL}/global_player_count/${userId}`, { headers: getAuthHeaders() });
        const gpData = await gpR.json();
        if (gpData.status) {
          setGlobalPlayerInfo(gpData);
          if (gpData.maxPlayers > 0 && gpData.maxPlayers !== 9999 && gpData.totalPlayers > gpData.maxPlayers) {
            setShowPlayerLimitDialog(true);
            return;
          }
        }
      } catch (e) { console.error('global_player_count error:', e); }
      setPreActivateLoading(true);
      setShowPreActivateDialog(true);
      setSmsInfo(null); setSmsSent(false);
      try {
        const [r, smsR] = await Promise.all([
          fetch(`${API_URL}/get_info/${selectedGame.id}`),
          fetch(`${API_URL}/sms_info/${selectedGame.id}`, { headers: getAuthHeaders() }),
        ]);
        const info = await r.json();
        const smsData = await smsR.json();
        if (smsData.status) setSmsInfo(smsData);
        const activationsUsed = quota?.activationsUsed || 0;
        const remaining = quota?.gamesAllowed === -1 ? -1 : Math.max(0, (quota?.gamesAllowed || 0) - activationsUsed);
        setPreActivateInfo({
          phoneNumber: info.number || selectedGame.startNumber || 'ממתין להקצאה...',
          gameName: selectedGame.name,
          gameId: selectedGame.id,
          mobileLink: `${window.location.origin}/m/${selectedGame.id}`,
          remaining,
          allowSmartDevice: selectedGame.allowSmartDevice ?? true,
        });
      } catch {
        setPreActivateInfo({
          phoneNumber: selectedGame.startNumber || 'לא ידוע',
          gameName: selectedGame.name,
          gameId: selectedGame.id,
          mobileLink: `${window.location.origin}/m/${selectedGame.id}`,
          remaining: -1,
          allowSmartDevice: selectedGame.allowSmartDevice ?? true,
        });
      }
      setPreActivateLoading(false);
    }}
    sx={{
      borderRadius: 3, py: 1.5,
      fontWeight: 800, fontSize: '.95rem',
      background: 'linear-gradient(135deg, #4caf50, #2e7d32)',
      boxShadow: '0 4px 16px rgba(76,175,80,.35)',
      '&:hover': { background: 'linear-gradient(135deg, #43a047, #1b5e20)', boxShadow: '0 6px 24px rgba(76,175,80,.5)' },
    }}
  >
    🚀 הפעלת משחק
  </Button>
</Box>
<Box data-tour="activate-placeholder" sx={{ display: 'none' }} />

                        </Container>
                        )}
                        </>
                    ) : (
                        <Container maxWidth="lg">
                            {/* כותרת */}
                            <Box sx={{ mb: 3, mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                              <Box>
                                <Typography variant="h4" sx={{ fontWeight: 800, color: '#2d2d2d', mb: 0.5, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                                    🎮 המשחקים שלי
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#999' }}>
                                    {games.length} משחקים
                                </Typography>
                              </Box>
                              {quota && (
                                <Button
                                  variant="outlined"
                                  data-tour="packages"
                                  onClick={() => setShowPackageDialog(true)}
                                  sx={{ borderRadius: 3, fontWeight: 600, borderColor: '#667eea', color: '#667eea', fontSize: { xs: '.75rem', sm: '.875rem' }, px: { xs: 1.5, sm: 2 } }}
                                >
                                  🛒 {quota.gamesAllowed === -1
                                    ? '♾️ ללא הגבלה'
                                    : quota.gamesAllowed - (quota.activationsUsed || 0) > 0
                                      ? `${quota.gamesAllowed - (quota.activationsUsed || 0)} הפעלות זמינות`
                                      : 'רכישת חבילה'}
                                </Button>
                              )}
                            </Box>

                            {/* כפתור יצירת משחק ממורכז במובייל */}
                            {isMobile && (
                              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 2.5 }}>
                                <Button
                                  ref={newGameMobileBtnRef}
                                  variant="contained"
                                  onClick={handleOpenDialog}
                                  sx={{
                                    borderRadius: '50px', px: 4, py: 1.3, fontWeight: 700, fontSize: '.95rem',
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    boxShadow: '0 6px 24px rgba(102, 126, 234, 0.45)',
                                    '&:hover': { boxShadow: '0 10px 32px rgba(102, 126, 234, 0.55)' },
                                  }}
                                  startIcon={<GamepadIcon />}
                                  data-tour="new-game"
                                >
                                  🎮 יצירת משחק חדש
                                </Button>
                                <Tooltip title="הדרכה" arrow>
                                  <IconButton
                                    onClick={() => { setShowTour(true); setTourStep(0); }}
                                    sx={{
                                      width: 40, height: 40,
                                      bgcolor: '#ffffff',
                                      border: '2px solid #667eea',
                                      color: '#667eea',
                                      boxShadow: '0 4px 14px rgba(102,126,234,.22)',
                                      '&:hover': { bgcolor: '#f3f6ff' },
                                    }}
                                  >
                                    <HelpOutlineIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            )}

                            {games.length === 0 ? (
                              <Box sx={{ textAlign: 'center', py: 10 }}>
                                <Typography sx={{ fontSize: '4rem', mb: 2 }}>🎯</Typography>
                                <Typography variant="h5" sx={{ color: '#888', mb: 2 }}>אין לך משחקים עדיין</Typography>
                                <Typography variant="body1" sx={{ color: '#aaa' }}>לחץ על הכפתור למעלה כדי ליצור את המשחק הראשון שלך</Typography>
                              </Box>
                            ) : isMobile ? (
                              /* ===== כרטיסי משחקים במובייל ===== */
                              <Box data-tour="game-list" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {games.map((game) => (
                                  <Paper
                                    key={game.id}
                                    onClick={() => handleGameClick(game)}
                                    sx={{
                                      p: 2, borderRadius: 3, cursor: 'pointer',
                                      border: '1px solid #e8ecf4',
                                      transition: 'all .2s',
                                      '&:active': { transform: 'scale(.98)' },
                                      display: 'flex', alignItems: 'center', gap: 2,
                                    }}
                                  >
                                    <Box sx={{
                                      width: 44, height: 44, borderRadius: 2.5,
                                      background: 'linear-gradient(135deg, #667eea, #764ba2)',
                                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      fontSize: '1.3rem', flexShrink: 0,
                                    }}>🎮</Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Typography sx={{ fontWeight: 700, fontSize: '.95rem', color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{game.name}</Typography>
                                      <Box sx={{ display: 'flex', gap: 1, mt: .5, alignItems: 'center' }}>
                                        <Chip label={`${game.qCount || 0} שאלות`} size="small" sx={{ height: 22, fontSize: '.7rem', fontWeight: 600, bgcolor: '#f0f4ff', color: '#4a6cf7' }} />
                                        <Chip
                                          label={game.isOpen ? 'פתוח' : 'סגור'}
                                          size="small"
                                          sx={{ height: 22, fontSize: '.7rem', fontWeight: 600, bgcolor: game.isOpen ? '#ecfdf5' : '#fef2f2', color: game.isOpen ? '#16a34a' : '#dc2626' }}
                                        />
                                      </Box>
                                    </Box>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => { e.stopPropagation(); handleDeleteGame(game.id, game.name); }}
                                      sx={{ color: '#cbd5e1', '&:hover': { color: '#e53935' } }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Paper>
                                ))}
                              </Box>
                            ) : (
                            <TableContainer component={Paper} sx={{ borderRadius: '16px', boxShadow: '0 2px 20px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
                              <Table>
                                <TableHead>
                                  <TableRow sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                                    <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.75rem', sm: '0.9rem' }, border: 0, py: { xs: 1.5, sm: 2 } }}>שם משחק</TableCell>
                                    <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.75rem', sm: '0.9rem' }, border: 0, py: { xs: 1.5, sm: 2 } }}>שאלות</TableCell>
                                    <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.75rem', sm: '0.9rem' }, border: 0, py: { xs: 1.5, sm: 2 } }}>סטטוס</TableCell>
                                    <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.9rem', border: 0, py: 2, display: { xs: 'none', sm: 'table-cell' } }}>זמן תשובה</TableCell>
                                    <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.9rem', border: 0, py: 2, display: { xs: 'none', sm: 'table-cell' } }}>ניקוד</TableCell>
                                    <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.9rem', border: 0, py: 2, width: 60 }}></TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {games.map((game, index) => (
                                    <TableRow
                                      key={game.id}
                                      onClick={() => handleGameClick(game)}
                                      sx={{
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        bgcolor: index % 2 === 0 ? '#fff' : '#fafbff',
                                        '&:hover': {
                                          bgcolor: '#f0f3ff',
                                          transform: 'scale(1.005)',
                                          boxShadow: '0 4px 15px rgba(102,126,234,0.1)',
                                        },
                                        '& td': { border: 0, py: 2 },
                                      }}
                                    >
                                      <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                          <Box>
                                            <Typography sx={{ fontWeight: 700, color: '#2d2d2d', fontSize: { xs: '0.82rem', sm: '0.95rem' } }}>{game.name}</Typography>
                                            <Typography sx={{ color: '#bbb', fontSize: '0.7rem', display: { xs: 'none', sm: 'block' } }}>ID: {game.id?.slice(0, 10)}</Typography>
                                          </Box>
                                        </Box>
                                      </TableCell>
                                      <TableCell>
                                        <Chip label={`${game.qCount || 0}`} size="small" sx={{ bgcolor: '#f0f4ff', color: '#4a6cf7', fontWeight: 700, borderRadius: '8px', minWidth: 36 }} />
                                      </TableCell>
                                      <TableCell>
                                        <Chip
                                          label={game.isOpen ? 'פתוח' : 'סגור'}
                                          size="small"
                                          sx={{
                                            bgcolor: game.isOpen ? '#e8f5e9' : '#fbe9e7',
                                            color: game.isOpen ? '#2e7d32' : '#c62828',
                                            fontWeight: 600, borderRadius: '8px', minWidth: 50,
                                          }}
                                        />
                                      </TableCell>
                                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                        <Typography sx={{ color: '#555', fontWeight: 500 }}>{game.answerTime ? `${game.answerTime} שניות` : '—'}</Typography>
                                      </TableCell>
                                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                        <Typography sx={{ color: '#555', fontWeight: 500 }}>{game.defaultPoints || '—'}</Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Tooltip title="מחק משחק">
                                          <IconButton
                                            size="small"
                                            onClick={(e) => { e.stopPropagation(); handleDeleteGame(game.id, game.name); }}
                                            sx={{ color: '#ccc', '&:hover': { color: '#e53935', bgcolor: '#fff0f0' } }}
                                          >
                                            <DeleteIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                            )}
                        </Container>
                    )}
                                                <Box sx={{
    position: 'fixed',
    bottom: { xs: 16, sm: 24 },
    right: { xs: 16, sm: 24 },
    display: isMobile ? 'none' : 'flex',
    alignItems: 'center',
    gap: 1,
    zIndex: 1000,
  }}>
    <Box
      ref={newGameDesktopBtnRef}
      data-tour="new-game-float"
      sx={{
        padding: { xs: '10px 18px', sm: '14px 28px' },
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        borderRadius: '50px',
        cursor: 'pointer',
        fontSize: { xs: '13px', sm: '15px' },
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        boxShadow: '0 6px 24px rgba(102, 126, 234, 0.45)',
        transition: 'all 0.3s ease',
        '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 10px 32px rgba(102, 126, 234, 0.55)' },
      }}
      onClick={handleOpenDialog}
    >
      🎮 {isMobile ? '+' : 'יצירת משחק חדש'}
    </Box>

    <Tooltip title="הדרכה" arrow>
      <IconButton
        onClick={() => { setShowTour(true); setTourStep(0); }}
        sx={{
          width: 42, height: 42,
          bgcolor: '#ffffff',
          border: '2px solid #667eea',
          color: '#667eea',
          boxShadow: '0 6px 22px rgba(102,126,234,.28)',
          '&:hover': { bgcolor: '#f3f6ff' },
        }}
      >
        <HelpOutlineIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  </Box>

<Dialog open={openDialog} onClose={handleCloseDialog} sx={{ ...dialogPaperSx, ...(showTour ? dialogTourBoostSx : {}) }} TransitionComponent={Slide} TransitionProps={{ direction: 'up' }}>
  <DialogTitle sx={dialogTitleSx}>
    <GamepadIcon /> יצירת משחק חדש
  </DialogTitle>
  <DialogContent sx={{ pt: '24px !important', px: 3, pb: 2, direction: 'rtl', textAlign: 'right' }}>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      הזן שם למשחק החדש שלך
    </Typography>
    <TextField
      autoFocus
      label="שם המשחק"
      type="text"
      fullWidth
      variant="outlined"
      value={newGameName}
      onChange={(e) => setNewGameName(e.target.value)}
      onKeyDown={(e) => e.key === 'Enter' && handleCreateGame()}
    />
  </DialogContent>
  <DialogActions sx={dialogActionsSx}>
    <Button onClick={handleCreateGame} variant="contained" sx={{ borderRadius: 2, px: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      🎮 צור משחק
    </Button>
    <Button onClick={handleCloseDialog} sx={{ borderRadius: 2, color: '#888' }}>
      ביטול
    </Button>
  </DialogActions>
</Dialog>
<Dialog open={showUploadDialog} onClose={() => setShowUploadDialog(false)} sx={{ ...dialogPaperSx, ...(showTour ? dialogTourBoostSx : {}) }} TransitionComponent={Slide} TransitionProps={{ direction: 'up' }}>
  <DialogTitle sx={dialogTitleSx}>
    <CloudUploadIcon /> העלאת {uploadType === 'p' ? 'תמונה' : 'וידאו'}
  </DialogTitle>
  <DialogContent sx={{ pt: '24px !important', px: 3, pb: 2, direction: 'rtl', textAlign: 'right' }}>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      בחר קובץ {uploadType === 'p' ? 'תמונה' : 'וידאו'} להעלאה
    </Typography>
    <Button variant="outlined" component="label" fullWidth sx={{ borderRadius: 2, py: 3, borderStyle: 'dashed', borderWidth: 2 }}>
      <CloudUploadIcon sx={{ mr: 1 }} /> {mediaFile ? mediaFile.name : 'לחץ לבחירת קובץ'}
      <input type="file" accept={uploadType === 'p' ? 'image/*' : 'video/*'} hidden onChange={(e) => setMediaFile(e.target.files[0])} />
    </Button>
  </DialogContent>
  <DialogActions sx={dialogActionsSx}>
    <Button
      onClick={async () => {
        if (!mediaFile) return showSnack("לא נבחר קובץ", "warning");
        const formData = new FormData();
        formData.append("file", mediaFile);
        formData.append("user_id", userId);

        const res = await fetch(`${API_URL}/upload_file`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${getToken()}` },
          body: formData
        });

        const data = await res.json();
        if (data?.error === 'storage_limit') return showSnack(data.message || 'חרגת ממכסת האחסון', 'warning');
        if (!data?.link) return showSnack("שגיאה בהעלאה", "error");

        addQuestionToAPI({
          q: "שאלה עם קובץ",
          answers: ["", ""],
          type: uploadType,
          correctAnswer: 1,
          link: data.link
        });

        setShowUploadDialog(false);
        setMediaFile(null);
      }}
      variant="contained"
      sx={{ borderRadius: 2, px: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
    >
      📤 העלה ושלח
    </Button>
    <Button onClick={() => setShowUploadDialog(false)} sx={{ borderRadius: 2, color: '#888' }}>ביטול</Button>
  </DialogActions>
</Dialog>

{/* דיאלוג יצירת שאלות ב-AI — גלובלי (מובייל + דסקטופ) */}
{aiDialog}

{/* דיאלוג הוספת שחקן */}
<Dialog open={openPlayerDialog} onClose={() => setOpenPlayerDialog(false)} sx={{ ...dialogPaperSx, ...(showTour ? dialogTourBoostSx : {}) }} TransitionComponent={Slide} TransitionProps={{ direction: 'up' }}>
  <DialogTitle sx={dialogTitleSx}>
    <PersonAddIcon /> הוספת שחקן חדש
  </DialogTitle>
  <DialogContent sx={{ pt: '24px !important', px: 3, pb: 2, display: 'flex', flexDirection: 'column', gap: 2, direction: 'rtl', textAlign: 'right' }}>
    <TextField
      label="שם השחקן"
      value={playerName}
      onChange={(e) => setPlayerName(e.target.value)}
      fullWidth
      variant="outlined"
    />
    <TextField
      label="מספר טלפון"
      value={playerPhone}
      onChange={(e) => setPlayerPhone(e.target.value)}
      fullWidth
      variant="outlined"
    />
    <TextField
      select
      label="קבוצה"
      fullWidth
      value={playerGroup}
      onChange={(e) => setPlayerGroup(e.target.value)}
    >
      <MenuItem key={0} value="">
        ללא קבוצה
      </MenuItem>
      {groups.map((g) => (
        <MenuItem key={g.id} value={g.id}>
          {g.name || `קבוצה ${g.id}`}
        </MenuItem>
      ))}
    </TextField>
  </DialogContent>
  <DialogActions sx={dialogActionsSx}>
    <Button onClick={() => handleAdd(selectedGame.id)} variant="contained" sx={{ borderRadius: 2, px: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      ➕ הוסף שחקן
    </Button>
    <Button onClick={() => setOpenPlayerDialog(false)} sx={{ borderRadius: 2, color: '#888' }}>ביטול</Button>
  </DialogActions>
</Dialog>


{/* Snackbar התראות */}
<Snackbar
  open={snackbar.open}
  autoHideDuration={3000}
  onClose={() => setSnackbar({ ...snackbar, open: false })}
  anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
  TransitionComponent={Slide}
  TransitionProps={{ direction: 'up' }}
  sx={{ mb: 2 }}
>
  <Box
    dir="rtl"
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      minWidth: 340,
      px: 2.5,
      py: 1.8,
      borderRadius: '16px',
      color: '#fff',
      fontWeight: 600,
      fontSize: '0.95rem',
      background: (snackConfig[snackbar.severity] || snackConfig.info).bg,
      boxShadow: `0 12px 40px ${(snackConfig[snackbar.severity] || snackConfig.info).shadow}, 0 4px 12px rgba(0,0,0,0.1)`,
      backdropFilter: 'blur(12px)',
      fontFamily: "'Varela Round', 'Rubik', Arial, sans-serif",
    }}
  >
    <Box sx={{ fontSize: '1.5rem', lineHeight: 1, flexShrink: 0 }}>
      {(snackConfig[snackbar.severity] || snackConfig.info).emoji}
    </Box>
    <Typography sx={{ flex: 1, fontWeight: 600, fontSize: '0.95rem', color: '#fff' }}>
      {snackbar.message}
    </Typography>
    <IconButton
      size="small"
      onClick={() => setSnackbar({ ...snackbar, open: false })}
      sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.15)' } }}
    >
      <CloseIcon fontSize="small" />
    </IconButton>
  </Box>
</Snackbar>

{/* דיאלוג אישור */}
<Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false })} sx={{ ...dialogPaperSx, ...(showTour ? dialogTourBoostSx : {}) }} TransitionComponent={Slide} TransitionProps={{ direction: 'up' }}>
  <DialogTitle sx={{ ...dialogTitleSx, background: 'linear-gradient(135deg, #e53935 0%, #ff7043 100%)' }}>
    <WarningAmberIcon /> {confirmDialog.title}
  </DialogTitle>
  <DialogContent sx={{ pt: '24px !important', px: 3, pb: 2, direction: 'rtl', textAlign: 'right' }}>
    <Typography sx={{ fontSize: '1rem', color: '#444', lineHeight: 1.8 }}>{confirmDialog.text}</Typography>
  </DialogContent>
  <DialogActions sx={dialogActionsSx}>
    <Button
      onClick={confirmDialog.onConfirm}
      variant="contained"
      color="error"
      sx={{ borderRadius: 2, px: 4, fontWeight: 600 }}
    >
      🗑️ מחק
    </Button>
    <Button onClick={() => setConfirmDialog({ open: false })} sx={{ borderRadius: 2, color: '#888', fontWeight: 500 }}>ביטול</Button>
  </DialogActions>
</Dialog>

{/* ===== דיאלוג איפוס סיסמה ===== */}
<Dialog open={showResetDialog} onClose={() => { setShowResetDialog(false); setResetStep(0); }} maxWidth="xs" fullWidth sx={{ ...dialogPaperSx, ...(showTour ? dialogTourBoostSx : {}) }} TransitionComponent={Slide} TransitionProps={{ direction: 'up' }}>
  <DialogTitle sx={{ ...dialogTitleSx, background: 'linear-gradient(135deg, #ea580c, #c2410c)' }}>
    🔐 איפוס סיסמה
  </DialogTitle>
  <DialogContent sx={{ pt: '24px !important', px: 3, pb: 3, direction: 'rtl', textAlign: 'right' }}>
    {resetStep === 0 && (
      <Box>
        <Typography sx={{ fontSize: '.9rem', color: '#78716c', mb: 2, textAlign: 'center' }}>
          קוד אימות יישלח ב-SMS למספר הטלפון שלך
        </Typography>
        <Button
          variant="contained" fullWidth
          disabled={resetLoading}
          startIcon={resetLoading ? <CircularProgress size={16} color="inherit" /> : <SmsIcon />}
          onClick={async () => {
            setResetLoading(true); setResetError('');
            try {
              const r = await fetch(`${API_URL}/send-reset-code`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ identifier: resetIdentifier }),
              });
              const d = await r.json();
              if (d.status) { setResetMaskedPhone(d.maskedPhone); setResetStep(1); }
              else setResetError(d.error || 'שגיאה');
            } catch { setResetError('שגיאת רשת'); }
            setResetLoading(false);
          }}
          sx={{ borderRadius: 3, fontWeight: 700, py: 1.3, fontSize: '.95rem', background: 'linear-gradient(135deg, #ea580c, #c2410c)' }}>
          📲 שלח קוד אימות
        </Button>
        {resetError && <Typography sx={{ color: '#dc2626', fontSize: '.85rem', mt: 1.5, fontWeight: 600, textAlign: 'center' }}>⚠️ {resetError}</Typography>}
      </Box>
    )}
    {resetStep === 1 && (
      <Box>
        <Typography sx={{ fontSize: '.9rem', color: '#78716c', mb: 2, textAlign: 'center' }}>
          קוד נשלח למספר <strong>{resetMaskedPhone}</strong>
        </Typography>
        <TextField fullWidth label="הקלד קוד אימות (6 ספרות)" value={resetCode}
          onChange={e => setResetCode(e.target.value)}
          inputProps={{ maxLength: 6, style: { letterSpacing: 10, textAlign: 'center', fontWeight: 800, fontSize: '1.5rem' } }}
          sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 3 } }} />
        <Button variant="contained" fullWidth disabled={resetCode.length !== 6}
          onClick={() => { setResetStep(2); setResetError(''); }}
          sx={{ borderRadius: 3, fontWeight: 700, py: 1.3, background: 'linear-gradient(135deg, #ea580c, #c2410c)' }}>
          ✅ אימות קוד
        </Button>
        {resetError && <Typography sx={{ color: '#dc2626', fontSize: '.85rem', mt: 1.5, fontWeight: 600, textAlign: 'center' }}>⚠️ {resetError}</Typography>}
      </Box>
    )}
    {resetStep === 2 && (
      <Box>
        <Typography sx={{ fontSize: '.9rem', color: '#78716c', mb: 2, textAlign: 'center' }}>
          הקלד סיסמה חדשה (מינימום 6 תווים)
        </Typography>
        <TextField fullWidth label="סיסמה חדשה" type="password" value={resetNewPassword}
          onChange={e => setResetNewPassword(e.target.value)}
          sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 3 } }} />
        <Button variant="contained" fullWidth disabled={resetLoading || resetNewPassword.length < 6}
          startIcon={resetLoading ? <CircularProgress size={16} color="inherit" /> : <LockResetIcon />}
          onClick={async () => {
            setResetLoading(true); setResetError('');
            try {
              const r = await fetch(`${API_URL}/reset-password`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ identifier: resetIdentifier, code: resetCode, newPassword: resetNewPassword }),
              });
              const d = await r.json();
              if (d.status) {
                setResetStep(3);
                showSnack('הסיסמה שונתה בהצלחה!');
                setTimeout(() => { setShowResetDialog(false); setResetStep(0); setResetCode(''); setResetNewPassword(''); }, 2500);
              } else setResetError(d.error || 'שגיאה');
            } catch { setResetError('שגיאת רשת'); }
            setResetLoading(false);
          }}
          sx={{ borderRadius: 3, fontWeight: 700, py: 1.3, background: 'linear-gradient(135deg, #16a34a, #15803d)' }}>
          🔑 שנה סיסמה
        </Button>
        {resetError && <Typography sx={{ color: '#dc2626', fontSize: '.85rem', mt: 1.5, fontWeight: 600, textAlign: 'center' }}>⚠️ {resetError}</Typography>}
      </Box>
    )}
    {resetStep === 3 && (
      <Box sx={{ textAlign: 'center', py: 3 }}>
        <Typography sx={{ fontSize: '3rem', mb: 1 }}>✅</Typography>
        <Typography sx={{ fontWeight: 700, color: '#16a34a', fontSize: '1.1rem' }}>הסיסמה שונתה בהצלחה!</Typography>
      </Box>
    )}
  </DialogContent>
  <DialogActions sx={dialogActionsSx}>
    <Button onClick={() => { setShowResetDialog(false); setResetStep(0); }} sx={{ borderRadius: 2, color: '#888' }}>סגור</Button>
  </DialogActions>
</Dialog>

{/* ===== דיאלוג חבילות ===== */}
<Dialog open={showPackageDialog} onClose={() => setShowPackageDialog(false)} maxWidth="sm" fullWidth sx={{ ...dialogPaperSx, ...(showTour ? dialogTourBoostSx : {}) }} TransitionComponent={Slide} TransitionProps={{ direction: 'up' }}>
  <DialogTitle sx={{ ...dialogTitleSx, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
    🛒 רכישת חבילה
  </DialogTitle>
  <DialogContent sx={{ pt: '16px !important', px: 3, pb: 2, direction: 'rtl', textAlign: 'right' }}>
    {/* החבילה הנוכחית */}
    {quota && (
      <Box sx={{ mb: 2 }}>
        {/* כרטיס חבילה נוכחית */}
        {quota.currentPackageName ? (
          <Box sx={{ p: 2, borderRadius: 3, background: 'linear-gradient(135deg, #eff6ff, #dbeafe)', border: '1.5px solid #93c5fd', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Box sx={{ width: 28, height: 28, borderRadius: '8px', background: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography sx={{ fontSize: '.85rem' }}>📦</Typography>
              </Box>
              <Typography sx={{ fontWeight: 800, fontSize: '.9rem', color: '#1e40af' }}>החבילה שלך: {quota.currentPackageName}</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: '.78rem', color: '#1d4ed8' }}>
                🎮 <strong>{quota.gamesAllowed === -1 ? '♾️' : `${quota.gamesAllowed - (quota.activationsUsed || 0)} נותרו מתוך ${quota.gamesAllowed}`}</strong>
              </Typography>
              <Typography sx={{ fontSize: '.78rem', color: '#1d4ed8' }}>
                👥 <strong>עד {quota.maxPlayersPerGame || 0} שחקנים</strong>
              </Typography>
              {globalPlayerInfo && <Typography sx={{ fontSize: '.78rem', color: '#1d4ed8' }}>
                📊 <strong>{globalPlayerInfo.totalPlayers} שחקנים שמורים</strong>
              </Typography>}
            </Box>
          </Box>
        ) : (
          <Box sx={{ p: 2, borderRadius: 3, background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)', border: '1px solid #e2e8f0', mb: 1, textAlign: 'center' }}>
            <Typography sx={{ fontSize: '.85rem', color: '#64748b', fontWeight: 600 }}>📦 אין לך חבילה פעילה — בחרו חבילה כדי להתחיל!</Typography>
          </Box>
        )}

        {/* אזהרה — רכישה מחליפה */}
        {pkgTab === 'game' && (quota.gamesAllowed > 0 || quota.gamesAllowed === -1) && (
          <Box sx={{ p: 1.5, borderRadius: 2.5, background: '#fffbeb', border: '1px solid #fde68a' }}>
            <Typography sx={{ fontSize: '.72rem', color: '#92400e', textAlign: 'center' }}>
              ⚠️ רכישת חבילת משחקים חדשה <strong>תחליף</strong> את החבילה הנוכחית ומספר ההפעלות יתאפס
            </Typography>
          </Box>
        )}
      </Box>
    )}

    {/* באנר חבילת הדגמה — רק ללקוחות חדשים */}
    {(() => {
      const demoPackages = packages.filter(p => p.isDemo);
      const isNewCustomer = profileData && (profileData.purchases || []).length === 0 && (profileData.quota?.activationsUsed || quota?.activationsUsed || 0) === 0;
      if (demoPackages.length > 0 && isNewCustomer) {
        return demoPackages.map(dp => (
          <Box key={dp.id} sx={{
            mb: 2, p: 2.5, borderRadius: 3,
            background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
            border: '2px solid #f59e0b',
          }}>
            <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#92400e', mb: .5 }}>
              🎯 רוצה לראות איך המשחק עובד?
            </Typography>
            <Typography sx={{ fontSize: '.85rem', color: '#78350f', mb: 1.5 }}>
              קבל עכשיו חבילת הדגמה — {dp.games} משחקים עד {dp.maxPlayers} שחקנים, בחינם!
            </Typography>
            <Button
              variant="contained"
              disabled={claimingPackage === dp.id}
              onClick={async () => {
                setClaimingPackage(dp.id);
                try {
                  const r = await fetch(`${API_URL}/claim_free_package`, {
                    method: 'POST',
                    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                    body: JSON.stringify({ packageId: dp.id }),
                  });
                  const d = await r.json();
                  if (d.status) {
                    alert(d.message);
                    setShowPackageDialog(false);
                    // רענון נתונים
                    const qr = await fetch(`${API_URL}/user_quota/${userId}`, { headers: getAuthHeaders() });
                    const qd = await qr.json();
                    if (qd.status) setQuota(qd.quota);
                  } else {
                    alert(d.message || 'שגיאה');
                  }
                } catch { alert('שגיאה בקבלת החבילה'); }
                setClaimingPackage(null);
              }}
              sx={{
                borderRadius: 2, px: 4, fontWeight: 700, fontSize: '.95rem',
                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                '&:hover': { background: 'linear-gradient(135deg, #d97706, #b45309)' },
              }}
            >
              {claimingPackage === dp.id ? '⏳ מקבל...' : '🎁 קבל חבילת הדגמה'}
            </Button>
          </Box>
        ));
      }
      return null;
    })()}

    {/* טאבים */}
    <Tabs value={pkgTab} onChange={(_, v) => setPkgTab(v)} variant="fullWidth"
      sx={{
        mb: 2, minHeight: 40,
        '& .MuiTab-root': { fontWeight: 700, fontSize: '.78rem', minHeight: 40, py: 0 },
        '& .MuiTabs-indicator': { height: 3, borderRadius: 2 },
      }}>
      <Tab value="game" label="🎮 משחקים" />
      <Tab value="storage" label="💾 אחסון" />
      <Tab value="sms" label="📩 הודעות" />
    </Tabs>

    {packages.filter(p => (p.type || 'game') === pkgTab && !p.isDemo).map(pkg => {
      const isFree = !pkg.price || pkg.price <= 0;
      const isLimited = pkg.maxPerCustomer && pkg.maxPerCustomer > 0;
      const pkgType = pkg.type || 'game';

      // === חסימת חבילות חלשות/זהות ===
      const isCurrentPkg = quota?.currentPackageId === pkg.id;
      const currentRemaining = quota ? (quota.gamesAllowed === -1 ? Infinity : Math.max(0, (quota.gamesAllowed || 0) - (quota.activationsUsed || 0))) : 0;
      const isWeaker = pkgType === 'game' && quota && !pkg.unlimited && pkg.games !== -1 &&
        (quota.gamesAllowed === -1 || (pkg.games < currentRemaining && currentRemaining > 0));
      const isBlocked = isCurrentPkg || isWeaker;

      return (
      <Box key={pkg.id} sx={{
        p: 2.5, mb: 2, borderRadius: 3,
        border: isCurrentPkg ? '2px solid #3b82f6' : isBlocked ? '2px solid #e2e8f0' : '2px solid #e0e0e0',
        transition: 'all .2s',
        opacity: isBlocked ? .55 : 1,
        position: 'relative',
        background: isCurrentPkg ? '#eff6ff' : 'transparent',
        '&:hover': isBlocked ? {} : { borderColor: '#667eea', boxShadow: '0 4px 16px rgba(102,126,234,.15)' }
      }}>
        {/* תג חבילה נוכחית */}
        {isCurrentPkg && (
          <Box sx={{ position: 'absolute', top: -10, right: 16, px: 1.5, py: .3, borderRadius: 50, background: '#3b82f6', color: '#fff', fontSize: '.65rem', fontWeight: 800, boxShadow: '0 2px 8px rgba(59,130,246,.3)' }}>
            ✓ החבילה שלך
          </Box>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>
              {pkg.unlimited ? '♾️ ' : ''}{pkg.name}
            </Typography>
            <Typography sx={{ fontSize: '.82rem', color: '#888', mt: .5 }}>
              {pkgTab === 'game' && (
                <>{pkg.unlimited || pkg.games === -1 ? 'ללא הגבלה' : `${pkg.games} משחק${pkg.games > 1 ? 'ים' : ''}`} • עד {pkg.maxPlayers} שחקנים</>
              )}
              {pkgTab === 'storage' && (
                <>{pkg.unlimited || pkg.storageMB === -1 ? 'ללא הגבלה' : pkg.storageMB >= 1024 ? `${(pkg.storageMB / 1024).toFixed(1)} GB` : `${pkg.storageMB} MB`} אחסון נוסף</>
              )}
              {pkgTab === 'sms' && (
                <>{pkg.unlimited || pkg.smsCredits === -1 ? 'ללא הגבלה' : `${pkg.smsCredits || 0}`} הודעות SMS</>
              )}
            </Typography>
            {isFree ? (
              <Typography sx={{ fontSize: '.9rem', color: '#10b981', fontWeight: 700, mt: .5 }}>🎁 חינם</Typography>
            ) : pkg.price > 0 ? (
              <Typography sx={{ fontSize: '.9rem', color: '#667eea', fontWeight: 700, mt: .5 }}>₪{pkg.price}</Typography>
            ) : null}
            {isLimited && (
              <Typography sx={{ fontSize: '.72rem', color: '#999', mt: .3 }}>
                מוגבל ל-{pkg.maxPerCustomer} מימוש{pkg.maxPerCustomer > 1 ? 'ים' : ''} ללקוח
              </Typography>
            )}
            {isWeaker && !isCurrentPkg && (
              <Typography sx={{ fontSize: '.72rem', color: '#dc2626', fontWeight: 600, mt: .3 }}>
                ❌ חבילה חלשה מהנוכחית — נותרו לך {currentRemaining === Infinity ? '♾️' : currentRemaining} הפעלות
              </Typography>
            )}
          </Box>
          {isBlocked ? (
            <Chip label={isCurrentPkg ? 'חבילה נוכחית' : 'חלשה מדי'} size="small" sx={{ fontWeight: 700, bgcolor: isCurrentPkg ? '#dbeafe' : '#fee2e2', color: isCurrentPkg ? '#2563eb' : '#dc2626', fontSize: '.72rem' }} />
          ) : isFree ? (
            <Button
              variant="contained"
              disabled={claimingPackage === pkg.id}
              onClick={async () => {
                setClaimingPackage(pkg.id);
                try {
                  const r = await fetch(`${API_URL}/claim_free_package`, {
                    method: 'POST',
                    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                    body: JSON.stringify({ packageId: pkg.id }),
                  });
                  const d = await r.json();
                  if (d.status) {
                    alert(d.message);
                    setShowPackageDialog(false);
                    const qr = await fetch(`${API_URL}/user_quota/${userId}`, { headers: getAuthHeaders() });
                    const qd = await qr.json();
                    if (qd.status) setQuota(qd.quota);
                  } else {
                    alert(d.message || 'שגיאה');
                  }
                } catch { alert('שגיאה בקבלת החבילה'); }
                setClaimingPackage(null);
              }}
              sx={{ borderRadius: 2, px: 3, fontWeight: 700, background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}
            >
              {claimingPackage === pkg.id ? '⏳' : '🎁 קבל'}
            </Button>
          ) : pkg.payLink ? (
            <Button
              variant="contained"
              onClick={() => {
                const hasExistingGamePkg = pkgType === 'game' && quota && (quota.gamesAllowed > 0 || quota.gamesAllowed === -1);
                if (hasExistingGamePkg) {
                  const currentPkgDesc = quota.gamesAllowed === -1 ? '♾️ ללא הגבלה' : `${quota.gamesAllowed} הפעלות, עד ${quota.maxPlayersPerGame || '?'} שחקנים`;
                  if (!window.confirm(`⚠️ שים לב!\n\nכרגע יש לך חבילה: ${quota.currentPackageName || currentPkgDesc}\n\nרכישת \"${pkg.name}\" תחליף את החבילה הנוכחית.\nמספר ההפעלות יתאפס.\n\nלהמשיך?`)) return;
                }
                localStorage.setItem('pending_payment_user', userId);
                localStorage.setItem('pending_payment_package', pkg.id);
                window.open(pkg.payLink, '_blank');
              }}
              sx={{ borderRadius: 2, px: 3, fontWeight: 700, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
            >
              💳 רכישה
            </Button>
          ) : (
            <Chip label="בקרוב" sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }} />
          )}
        </Box>
      </Box>
      );
    })}

    {packages.filter(p => (p.type || 'game') === pkgTab && !p.isDemo).length === 0 && (
      <Typography sx={{ textAlign: 'center', color: '#999', py: 4 }}>
        אין חבילות זמינות בקטגוריה זו
      </Typography>
    )}
  </DialogContent>
  <DialogActions sx={dialogActionsSx}>
    <Button onClick={() => setShowPackageDialog(false)} sx={{ borderRadius: 2, color: '#888' }}>סגור</Button>
  </DialogActions>
</Dialog>

{/* ===== דיאלוג לפני הפעלת משחק ===== */}
<Dialog open={showPreActivateDialog} onClose={() => setShowPreActivateDialog(false)} maxWidth="xs" fullWidth
  PaperProps={{
    sx: {
      direction: 'rtl', borderRadius: '24px', overflow: 'hidden',
      boxShadow: '0 32px 100px rgba(0,0,0,.22)',
      fontFamily: "'Varela Round', 'Rubik', Arial, sans-serif",
      background: '#fff',
    }
  }}
  TransitionComponent={Slide} TransitionProps={{ direction: 'up' }}>

  {preActivateLoading ? (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 10 }}>
      <Box sx={{
        width: 80, height: 80, borderRadius: '50%',
        background: 'linear-gradient(135deg, #4caf50, #81c784)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        mb: 3, animation: 'pulse 1.5s ease-in-out infinite',
        '@keyframes pulse': { '0%,100%': { transform: 'scale(1)' }, '50%': { transform: 'scale(1.08)' } },
      }}>
        <CircularProgress size={36} sx={{ color: '#fff' }} />
      </Box>
      <Typography sx={{ color: '#64748b', fontWeight: 600 }}>טוען פרטי משחק...</Typography>
    </Box>
  ) : preActivateInfo && (
    <>
      {/* Header */}
      <Box sx={{
        background: 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)',
        pt: 4, pb: 5, px: 3, textAlign: 'center', position: 'relative',
        '&::after': {
          content: '""', position: 'absolute', bottom: -20, left: 0, right: 0,
          height: 40, background: '#fff', borderRadius: '50% 50% 0 0',
        }
      }}>
        <Box sx={{
          width: 64, height: 64, borderRadius: '50%', mx: 'auto', mb: 2,
          background: 'rgba(255,255,255,.2)', backdropFilter: 'blur(10px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: '2px solid rgba(255,255,255,.3)',
        }}>
          <Typography sx={{ fontSize: '2rem' }}>🎮</Typography>
        </Box>
        <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '1.3rem', mb: .5 }}>
          {preActivateInfo.gameName}
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,.75)', fontSize: '.82rem', fontWeight: 500 }}>
          הכנה להפעלת המשחק
        </Typography>
      </Box>

      {/* Content */}
      <Box sx={{ px: 3, pb: 2, pt: 0, mt: -1, position: 'relative', zIndex: 2 }}>

        {/* מספר טלפון */}
        <Box sx={{
          p: 2, borderRadius: 3, mb: 2,
          background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
          border: '1px solid #bbf7d0',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Box sx={{ width: 28, height: 28, borderRadius: '8px', background: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography sx={{ fontSize: '.85rem' }}>📞</Typography>
            </Box>
            <Typography sx={{ fontSize: '.78rem', color: '#15803d', fontWeight: 700, letterSpacing: .3 }}>מספר טלפון למשחק</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ fontWeight: 900, fontSize: '1.5rem', color: '#14532d', direction: 'ltr', letterSpacing: 2, fontFamily: 'monospace' }}>
              {preActivateInfo.phoneNumber}
            </Typography>
            <Tooltip title="העתק מספר" arrow>
              <IconButton size="small"
                onClick={() => { navigator.clipboard.writeText(String(preActivateInfo.phoneNumber)); showSnack('מספר הטלפון הועתק'); }}
                sx={{ background: '#16a34a22', color: '#16a34a', '&:hover': { background: '#16a34a33' } }}>
                <ContentCopyIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* === קישורים === */}
        <Box
          onClick={() => {
            if (!preActivateInfo.allowSmartDevice) return;
            setExpandedSection(expandedSection === 'links' ? null : 'links');
          }}
          sx={{
            borderRadius: 3, mb: 1, overflow: 'hidden',
            cursor: preActivateInfo.allowSmartDevice ? 'pointer' : 'default',
            border: !preActivateInfo.allowSmartDevice ? '1px solid #e2e8f0' : expandedSection === 'links' ? '1px solid #bfdbfe' : '1px solid #e2e8f0',
            transition: 'all .2s',
            opacity: preActivateInfo.allowSmartDevice ? 1 : .5,
          }}>
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            px: 2, py: 1.3,
            background: !preActivateInfo.allowSmartDevice ? '#f1f5f9' : expandedSection === 'links' ? 'linear-gradient(135deg, #eff6ff, #dbeafe)' : '#f8fafc',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 26, height: 26, borderRadius: '7px', background: preActivateInfo.allowSmartDevice ? '#2563eb' : '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography sx={{ fontSize: '.8rem' }}>📱</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: '.8rem', color: preActivateInfo.allowSmartDevice ? '#1d4ed8' : '#94a3b8', fontWeight: 700 }}>קישורים לשחקנים</Typography>
                {!preActivateInfo.allowSmartDevice && (
                  <Typography sx={{ fontSize: '.65rem', color: '#94a3b8' }}>לא מופעל — ניתן להפעיל בהגדרות המשחק</Typography>
                )}
              </Box>
            </Box>
            {preActivateInfo.allowSmartDevice && (
              <ExpandMoreIcon sx={{ color: '#94a3b8', fontSize: 20, transition: 'transform .25s', transform: expandedSection === 'links' ? 'rotate(180deg)' : 'none' }} />
            )}
          </Box>
          {expandedSection === 'links' && (
            <Box sx={{ px: 2, pb: 2, pt: 1, background: 'linear-gradient(135deg, #eff6ff, #dbeafe)' }} onClick={e => e.stopPropagation()}>
              {/* קישור ישיר למשחק */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.2, borderRadius: 2, background: '#fff', border: '1px solid #e2e8f0', mb: 1 }}>
                <Box sx={{ flex: 1, overflow: 'hidden' }}>
                  <Typography sx={{ fontSize: '.62rem', color: '#94a3b8', fontWeight: 600, mb: .2 }}>🔗 קישור ישיר למשחק</Typography>
                  <Typography sx={{ fontSize: '.78rem', color: '#1e40af', fontWeight: 700, wordBreak: 'break-all', direction: 'ltr', textAlign: 'left' }}>
                    {preActivateInfo.mobileLink}
                  </Typography>
                </Box>
                <Tooltip title="העתק קישור" arrow>
                  <IconButton size="small"
                    onClick={() => { navigator.clipboard.writeText(preActivateInfo.mobileLink); showSnack('הקישור הועתק'); }}
                    sx={{ ml: .5, background: '#2563eb22', color: '#2563eb', '&:hover': { background: '#2563eb33' } }}>
                    <ContentCopyIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              </Box>
              {/* דף כניסה */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.2, borderRadius: 2, background: '#fff', border: '1px solid #e2e8f0' }}>
                <Box sx={{ flex: 1, overflow: 'hidden' }}>
                  <Typography sx={{ fontSize: '.62rem', color: '#94a3b8', fontWeight: 600, mb: .2 }}>📲 דף כניסה (עם הורדת אפליקציה)</Typography>
                  <Typography sx={{ fontSize: '.78rem', color: '#1e40af', fontWeight: 700, direction: 'ltr', textAlign: 'left' }}>
                    {`${window.location.origin}/m`}
                  </Typography>
                </Box>
                <Tooltip title="העתק קישור" arrow>
                  <IconButton size="small"
                    onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/m`); showSnack('קישור דף הכניסה הועתק'); }}
                    sx={{ ml: .5, background: '#2563eb22', color: '#2563eb', '&:hover': { background: '#2563eb33' } }}>
                    <ContentCopyIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          )}
        </Box>

        {/* === SMS === */}
        <Box
          onClick={() => setExpandedSection(expandedSection === 'sms' ? null : 'sms')}
          sx={{
            borderRadius: 3, mb: 2, overflow: 'hidden', cursor: 'pointer',
            border: expandedSection === 'sms' ? '1px solid #e9d5ff' : '1px solid #e2e8f0',
            transition: 'all .2s',
          }}>
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            px: 2, py: 1.3,
            background: expandedSection === 'sms' ? 'linear-gradient(135deg, #faf5ff, #f3e8ff)' : '#f8fafc',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 26, height: 26, borderRadius: '7px', background: '#7c3aed', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <SmsIcon sx={{ fontSize: 14, color: '#fff' }} />
              </Box>
              <Typography sx={{ fontSize: '.8rem', color: '#6d28d9', fontWeight: 700 }}>שליחת SMS לשחקנים</Typography>
              {smsInfo && (smsInfo.smsBalance > 0 || smsInfo.smsBalance === -1) && (
                <Chip label={smsInfo.smsBalance === -1 ? 'ללא הגבלה ♾️' : `יתרה: ${smsInfo.smsBalance}`} size="small" sx={{ fontSize: '.6rem', fontWeight: 700, height: 20, background: '#f3e8ff', color: '#7c3aed' }} />
              )}
            </Box>
            <ExpandMoreIcon sx={{ color: '#94a3b8', fontSize: 20, transition: 'transform .25s', transform: expandedSection === 'sms' ? 'rotate(180deg)' : 'none' }} />
          </Box>
          {expandedSection === 'sms' && (
            <Box sx={{ px: 2, pb: 2, pt: 1, background: 'linear-gradient(135deg, #faf5ff, #f3e8ff)' }} onClick={e => e.stopPropagation()}>
              {smsInfo ? (
                <Box>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                    <Box sx={{ flex: 1, p: .7, borderRadius: 2, background: '#fff', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                      <Typography sx={{ fontSize: '.55rem', color: '#94a3b8', fontWeight: 600 }}>שחקנים</Typography>
                      <Typography sx={{ fontWeight: 800, color: '#6d28d9', fontSize: '.9rem' }}>{smsInfo.totalPlayers}</Typography>
                    </Box>
                    <Box sx={{ flex: 1, p: .7, borderRadius: 2, background: '#fff', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                      <Typography sx={{ fontSize: '.55rem', color: '#94a3b8', fontWeight: 600 }}>יקבלו SMS</Typography>
                      <Typography sx={{ fontWeight: 800, color: '#16a34a', fontSize: '.9rem' }}>{smsInfo.validPlayers}</Typography>
                    </Box>
                    {smsInfo.kosherSkipped > 0 && (
                      <Box sx={{ flex: 1, p: .7, borderRadius: 2, background: '#fff', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '.55rem', color: '#94a3b8', fontWeight: 600 }}>כשר</Typography>
                        <Typography sx={{ fontWeight: 800, color: '#d97706', fontSize: '.9rem' }}>{smsInfo.kosherSkipped}</Typography>
                      </Box>
                    )}
                    <Box sx={{ flex: 1, p: .7, borderRadius: 2, background: '#fff', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                      <Typography sx={{ fontSize: '.55rem', color: '#94a3b8', fontWeight: 600 }}>יתרה</Typography>
                      <Typography sx={{ fontWeight: 800, color: (smsInfo.smsBalance > 0 || smsInfo.smsBalance === -1) ? '#6d28d9' : '#dc2626', fontSize: '.9rem' }}>{smsInfo.smsBalance === -1 ? '∞' : smsInfo.smsBalance}</Typography>
                    </Box>
                  </Box>

                  {smsSent ? (
                    <Box sx={{ p: 1, borderRadius: 2, background: '#f0fdf4', border: '1px solid #bbf7d0', textAlign: 'center' }}>
                      <Typography sx={{ fontWeight: 700, color: '#15803d', fontSize: '.8rem' }}>✅ הודעות נשלחו בהצלחה!</Typography>
                    </Box>
                  ) : smsInfo.validPlayers === 0 ? (
                    <Box sx={{ p: 1, borderRadius: 2, background: '#fefce8', border: '1px solid #fde68a', textAlign: 'center' }}>
                      <Typography sx={{ fontWeight: 600, color: '#d97706', fontSize: '.75rem' }}>אין שחקנים עם מספר טלפון תקין לשליחה</Typography>
                    </Box>
                  ) : smsInfo.smsBalance <= 0 && smsInfo.smsBalance !== -1 ? (
                    <Box sx={{ textAlign: 'center' }}>
                      <Box sx={{ p: 1, borderRadius: 2, background: '#fef2f2', border: '1px solid #fecaca', mb: 1 }}>
                        <Typography sx={{ fontWeight: 600, color: '#dc2626', fontSize: '.75rem' }}>אין יתרת SMS</Typography>
                      </Box>
                      <Button variant="contained" size="small" fullWidth
                        onClick={() => { setShowPreActivateDialog(false); setPkgTab('sms'); setShowPackageDialog(true); }}
                        sx={{ borderRadius: 2, py: .7, fontWeight: 700, fontSize: '.75rem', background: 'linear-gradient(135deg, #7c3aed, #9333ea)', '&:hover': { background: 'linear-gradient(135deg, #6d28d9, #7c3aed)' } }}>
                        🛒 רכוש חבילת הודעות
                      </Button>
                    </Box>
                  ) : smsInfo.smsBalance !== -1 && smsInfo.smsBalance < smsInfo.validPlayers ? (
                    <Box sx={{ textAlign: 'center' }}>
                      <Box sx={{ p: 1, borderRadius: 2, background: '#fef2f2', border: '1px solid #fecaca', mb: 1 }}>
                        <Typography sx={{ fontWeight: 600, color: '#dc2626', fontSize: '.75rem' }}>אין מספיק יתרה ({smsInfo.smsBalance}) ל-{smsInfo.validPlayers} שחקנים</Typography>
                      </Box>
                      <Button variant="contained" size="small" fullWidth
                        onClick={() => { setShowPreActivateDialog(false); setPkgTab('sms'); setShowPackageDialog(true); }}
                        sx={{ borderRadius: 2, py: .7, fontWeight: 700, fontSize: '.75rem', background: 'linear-gradient(135deg, #7c3aed, #9333ea)', '&:hover': { background: 'linear-gradient(135deg, #6d28d9, #7c3aed)' } }}>
                        🛒 רכוש עוד הודעות
                      </Button>
                    </Box>
                  ) : (
                    <Button
                      fullWidth variant="contained" size="small"
                      disabled={smsLoading}
                      startIcon={smsLoading ? <CircularProgress size={16} color="inherit" /> : <SmsIcon />}
                      onClick={async () => {
                        setSmsLoading(true);
                        try {
                          const r = await fetch(`${API_URL}/send_sms/${preActivateInfo.gameId}`, {
                            method: 'POST',
                            headers: getAuthHeaders(),
                            body: JSON.stringify({ mobileLink: preActivateInfo.mobileLink }),
                          });
                          const d = await r.json();
                          if (d.status) {
                            setSmsSent(true);
                            setSmsInfo(prev => ({ ...prev, smsBalance: d.newBalance }));
                            showSnack(`✅ נשלחו ${d.sent} הודעות${d.kosherSkipped ? ` (דולגו ${d.kosherSkipped} כשר)` : ''}`);
                          } else {
                            showSnack(d.error || 'שגיאה בשליחת SMS', 'error');
                          }
                        } catch {
                          showSnack('שגיאה בשליחת SMS', 'error');
                        }
                        setSmsLoading(false);
                      }}
                      sx={{
                        borderRadius: 2, py: .8, fontWeight: 700, fontSize: '.8rem',
                        background: 'linear-gradient(135deg, #7c3aed, #9333ea)',
                        boxShadow: '0 3px 12px rgba(124,58,237,.3)',
                        '&:hover': { background: 'linear-gradient(135deg, #6d28d9, #7c3aed)' },
                      }}>
                      📩 שלח SMS ל-{smsInfo.validPlayers} שחקנים
                    </Button>
                  )}
                </Box>
              ) : (
                <Typography sx={{ fontSize: '.75rem', color: '#94a3b8', textAlign: 'center' }}>טוען מידע SMS...</Typography>
              )}
            </Box>
          )}
        </Box>

        {/* שחקנים + מכסה */}
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
          {/* מכסת הפעלות */}
          <Box sx={{
            flex: 1, p: 1.5, borderRadius: 2.5, textAlign: 'center',
            background: preActivateInfo.remaining === -1
              ? 'linear-gradient(135deg, #f0fdf4, #dcfce7)'
              : preActivateInfo.remaining <= 2
                ? 'linear-gradient(135deg, #fef2f2, #fecaca)'
                : 'linear-gradient(135deg, #fffbeb, #fef3c7)',
            border: `1px solid ${preActivateInfo.remaining === -1 ? '#bbf7d0' : preActivateInfo.remaining <= 2 ? '#fca5a5' : '#fde68a'}`,
          }}>
            <Typography sx={{ fontSize: '.62rem', color: '#94a3b8', fontWeight: 600, mb: .3 }}>⚡ הפעלות</Typography>
            <Typography sx={{
              fontWeight: 800, fontSize: '1rem',
              color: preActivateInfo.remaining === -1 ? '#15803d' : preActivateInfo.remaining <= 2 ? '#dc2626' : '#d97706',
            }}>
              {preActivateInfo.remaining === -1 ? '♾️' : preActivateInfo.remaining}
            </Typography>
            <Typography sx={{ fontSize: '.6rem', color: '#94a3b8', fontWeight: 500 }}>
              {preActivateInfo.remaining === -1 ? 'ללא הגבלה' : 'נותרו'}
            </Typography>
          </Box>

          {/* מכסת שחקנים */}
          {globalPlayerInfo && (
            <Box sx={{
              flex: 1, p: 1.5, borderRadius: 2.5, textAlign: 'center',
              background: globalPlayerInfo.maxPlayers > 0 && globalPlayerInfo.maxPlayers !== 9999 && globalPlayerInfo.totalPlayers > globalPlayerInfo.maxPlayers * 0.8
                ? 'linear-gradient(135deg, #fef2f2, #fecaca)'
                : 'linear-gradient(135deg, #eff6ff, #dbeafe)',
              border: `1px solid ${globalPlayerInfo.maxPlayers > 0 && globalPlayerInfo.maxPlayers !== 9999 && globalPlayerInfo.totalPlayers > globalPlayerInfo.maxPlayers * 0.8 ? '#fca5a5' : '#bfdbfe'}`,
            }}>
              <Typography sx={{ fontSize: '.62rem', color: '#94a3b8', fontWeight: 600, mb: .3 }}>👥 שחקנים</Typography>
              <Typography sx={{
                fontWeight: 800, fontSize: '1rem',
                color: globalPlayerInfo.maxPlayers === 0 || globalPlayerInfo.maxPlayers === 9999
                  ? '#15803d'
                  : globalPlayerInfo.totalPlayers > globalPlayerInfo.maxPlayers * 0.8
                    ? '#dc2626'
                    : '#2563eb',
              }}>
                {globalPlayerInfo.totalPlayers} / {globalPlayerInfo.maxPlayers === 0 || globalPlayerInfo.maxPlayers === 9999 ? '♾️' : globalPlayerInfo.maxPlayers}
              </Typography>
              <Typography sx={{ fontSize: '.6rem', color: '#94a3b8', fontWeight: 500 }}>
                בחבילה שלך
              </Typography>
            </Box>
          )}
        </Box>

        {/* כפתור ניהול שחקנים */}
        {globalPlayerInfo && globalPlayerInfo.maxPlayers > 0 && globalPlayerInfo.maxPlayers !== 9999 && globalPlayerInfo.totalPlayers > globalPlayerInfo.maxPlayers * 0.5 && (
          <Box
            onClick={() => { setShowPreActivateDialog(false); setViewMode('players'); }}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1, p: 1.2, borderRadius: 2.5, mb: 1.5,
              cursor: 'pointer', transition: 'all .2s',
              background: '#f8fafc', border: '1px solid #e2e8f0',
              '&:hover': { background: '#f1f5f9', borderColor: '#cbd5e1' },
            }}>
            <Box sx={{ width: 28, height: 28, borderRadius: '8px', background: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Typography sx={{ fontSize: '.85rem' }}>👥</Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: '.78rem', fontWeight: 700, color: '#1e293b' }}>ניהול שחקנים</Typography>
              <Typography sx={{ fontSize: '.62rem', color: '#94a3b8' }}>
                השביתו שחקנים שלא צריכים לשחק כדי לפנות מקום
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '.85rem', color: '#94a3b8' }}>›</Typography>
          </Box>
        )}
      </Box>

      {/* Actions */}
      <Box sx={{ px: 3, pb: 3, pt: 1, display: 'flex', gap: 1.5 }}>
        <Button
          onClick={() => setShowPreActivateDialog(false)}
          sx={{
            flex: 1, borderRadius: 3, py: 1.3, fontWeight: 700,
            color: '#64748b', background: '#f1f5f9',
            '&:hover': { background: '#e2e8f0' },
          }}>
          ביטול
        </Button>
        <Button
          variant="contained"
          disabled={preActivateLoading}
          onClick={async () => {
            const gId = preActivateInfo?.gameId || selectedGame?.id;
            try {
              await fetch(`${API_URL}/reset_players/${gId}`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` },
              });
            } catch (e) { console.error('reset_players error:', e); }
            setShowPreActivateDialog(false);
            navigate(`/toplay/${gId}`);
          }}
          sx={{
            flex: 2, borderRadius: 3, py: 1.3, fontWeight: 800, fontSize: '.95rem',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            boxShadow: '0 4px 20px rgba(16,185,129,.35)',
            '&:hover': {
              background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
              boxShadow: '0 6px 28px rgba(16,185,129,.45)',
            },
          }}>
          ▶️ המשך לדף ההמתנה
        </Button>
      </Box>

      {/* Footer hint */}
      <Box sx={{ px: 3, pb: 2.5 }}>
        <Typography sx={{
          fontSize: '.72rem', color: '#94a3b8', textAlign: 'center',
          background: '#f8fafc', borderRadius: 2, py: 1, px: 2,
        }}>
          💡 לחיצה על "המשך" תעביר לדף ההמתנה — המשחק לא יופעל מיד
        </Typography>
      </Box>
    </>
  )}
</Dialog>

{/* דיאלוג מגבלת שחקנים — נפתח לפני דיאלוג הפעלה */}
<Dialog open={showPlayerLimitDialog} onClose={() => setShowPlayerLimitDialog(false)} maxWidth="xs" fullWidth
  PaperProps={{ sx: { borderRadius: 5, overflow: 'hidden', background: '#fff' } }}>
  <Box sx={{ textAlign: 'center', pt: 4, pb: 2, px: 3, background: 'linear-gradient(135deg, #fef9f0, #fef3c7)', borderBottom: '1px solid #fde68a' }}>
    <Box sx={{ width: 72, height: 72, borderRadius: '50%', mx: 'auto', mb: 2, background: 'linear-gradient(135deg, #f59e0b, #d97706)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 24px rgba(245,158,11,.3)' }}>
      <Typography sx={{ fontSize: 36 }}>🚀</Typography>
    </Box>
    <Typography sx={{ fontWeight: 900, fontSize: '1.3rem', color: '#92400e', mb: .5 }}>
      הגיע הזמן לשדרג!
    </Typography>
    <Typography sx={{ fontSize: '.9rem', color: '#b45309', lineHeight: 1.7 }}>
      יש לך משחקים מדהימים — בואו נתאים את החבילה למספר השחקנים שלכם!
    </Typography>
  </Box>

  <Box sx={{ px: 3, pt: 3, pb: 1 }}>
    {/* כרטיסי מידע */}
    <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5 }}>
      <Box sx={{ flex: 1, p: 2, borderRadius: 3, textAlign: 'center', background: 'linear-gradient(135deg, #fef2f2, #fecaca)', border: '1px solid #fca5a5' }}>
        <Typography sx={{ fontSize: '.65rem', color: '#92400e', fontWeight: 600, mb: .3 }}>👥 שחקנים שמורים</Typography>
        <Typography sx={{ fontWeight: 900, fontSize: '1.6rem', color: '#dc2626' }}>
          {globalPlayerInfo?.totalPlayers || 0}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, p: 2, borderRadius: 3, textAlign: 'center', background: 'linear-gradient(135deg, #fffbeb, #fef3c7)', border: '1px solid #fde68a' }}>
        <Typography sx={{ fontSize: '.65rem', color: '#92400e', fontWeight: 600, mb: .3 }}>📦 מותר בחבילה</Typography>
        <Typography sx={{ fontWeight: 900, fontSize: '1.6rem', color: '#d97706' }}>
          {globalPlayerInfo?.maxPlayers || 0}
        </Typography>
      </Box>
    </Box>

    {/* הודעה משכנעת */}
    <Box sx={{ p: 2, borderRadius: 3, background: '#fffbeb', border: '1px solid #fde68a', mb: 2 }}>
      <Typography sx={{ fontSize: '.85rem', color: '#92400e', textAlign: 'center', lineHeight: 1.8, fontWeight: 500 }}>
        🌟 עם <strong>שדרוג חבילה</strong> תוכלו להפעיל משחקים עם כל השחקנים
        שלכם, לקבל יותר הפעלות וליהנות מכל התכונות המתקדמות!
      </Typography>
    </Box>

    {/* טיפ — ניהול שחקנים */}
    <Box sx={{ p: 1.5, borderRadius: 2.5, background: '#f8fafc', border: '1px solid #e2e8f0', mb: 2 }}>
      <Typography sx={{ fontSize: '.78rem', color: '#475569', textAlign: 'center', lineHeight: 1.7 }}>
        💡 <strong>טיפ:</strong> אפשר גם להשבית שחקנים שלא צריכים לשחק כדי לפנות מקום — בלי לשדרג חבילה
      </Typography>
    </Box>
  </Box>

  {/* כפתורים */}
  <Box sx={{ px: 3, pb: 3, display: 'flex', flexDirection: 'column', gap: 1.2 }}>
    <Button
      fullWidth
      onClick={() => { setShowPlayerLimitDialog(false); setShowPackageDialog(true); }}
      sx={{
        borderRadius: 50, py: 1.5, fontWeight: 800, fontSize: '.95rem',
        background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: '#fff',
        boxShadow: '0 4px 16px rgba(245,158,11,.35)',
        '&:hover': { background: 'linear-gradient(135deg, #d97706, #b45309)', boxShadow: '0 6px 24px rgba(245,158,11,.45)' },
      }}>
      ⬆️ שדרגו חבילה עכשיו
    </Button>
    <Button
      fullWidth
      onClick={() => { setShowPlayerLimitDialog(false); setViewMode('players'); }}
      sx={{
        borderRadius: 50, py: 1.2, fontWeight: 700, fontSize: '.88rem',
        color: '#64748b', background: '#f1f5f9', border: '1px solid #e2e8f0',
        '&:hover': { background: '#e2e8f0' },
      }}>
      👥 ניהול שחקנים — הסרת שחקנים מהרשימה
    </Button>
    <Button
      fullWidth
      onClick={() => setShowPlayerLimitDialog(false)}
      sx={{
        borderRadius: 50, py: 1, fontWeight: 600, fontSize: '.82rem',
        color: '#94a3b8',
        '&:hover': { background: '#f8fafc' },
      }}>
      חזרה
    </Button>
  </Box>
</Dialog>

                </Box>

{/* ═══════════ GUIDED TOUR — SPOTLIGHT OVERLAY ═══════════ */}
{showTour && (() => {
  const step = tourSteps[tourStep] || tourSteps[0];
  const isLast = tourStep >= tourSteps.length - 1;
  const pad = 10;
  const hasSpot = spotRect && step.target;
  const W = window.innerWidth;
  const H = window.innerHeight;
  const tooltipW = Math.min(380, W - 24);

  // Smart position: place tooltip near the element, on the same side as the button
  let tooltipStyle = {};
  let arrowSide = 'none'; // which side of tooltip points to element
  let tooltipLeftPx = null;
  if (hasSpot) {
    const elCenterX = spotRect.left + spotRect.width / 2;
    const spaceAbove = spotRect.top;
    const spaceBelow = H - (spotRect.top + spotRect.height);

    // Vertical: place above or below based on space
    const placeAbove = spaceAbove > spaceBelow && spaceAbove > 200;
    if (placeAbove) {
      tooltipStyle.position = 'fixed';
      tooltipStyle.bottom = H - spotRect.top + pad + 8;
      arrowSide = 'bottom';
    } else {
      tooltipStyle.position = 'fixed';
      tooltipStyle.top = spotRect.top + spotRect.height + pad + 8;
      arrowSide = 'top';
    }

    // Horizontal: center tooltip on target so arrow and click area stay aligned
    tooltipStyle.width = tooltipW;
    const idealLeft = elCenterX - tooltipW / 2;
    const clampedLeft = Math.max(12, Math.min(idealLeft, W - tooltipW - 12));
    tooltipStyle.left = clampedLeft;
    tooltipStyle.right = 'auto';
    tooltipLeftPx = clampedLeft;
  } else {
    tooltipStyle = { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: tooltipW };
    tooltipLeftPx = (W - tooltipW) / 2;
  }

  // Arrow position (points to element center) — use pixel offset from left edge of tooltip
  const elCenterXForArrow = hasSpot ? (spotRect.left + spotRect.width / 2) : 0;
  const tooltipLeft = tooltipLeftPx ?? 0;
  const arrowLeft = hasSpot ? Math.max(20, Math.min(elCenterXForArrow - tooltipLeft, tooltipW - 20)) : tooltipW / 2;

  return (
    <>
      {/* Dark overlay with rounded cutout — pointerEvents:none so user can click highlighted element */}
      <Box style={{ position: 'fixed', inset: 0, zIndex: 9998, pointerEvents: 'none' }}>
        <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0 }}>
          <defs>
            <mask id="tour-mask">
              <rect width="100%" height="100%" fill="white" />
              {hasSpot && (
                <rect
                  x={spotRect.left - pad} y={spotRect.top - pad}
                  width={spotRect.width + pad * 2} height={spotRect.height + pad * 2}
                  rx="24" ry="24" fill="black"
                />
              )}
            </mask>
          </defs>
          <rect width="100%" height="100%" fill="rgba(0,0,0,.55)" mask="url(#tour-mask)" />
        </svg>
      </Box>

      {/* Spotlight ring with matching border-radius */}
      {hasSpot && (
        <Box
          style={{
            position: 'fixed',
            top: spotRect.top - pad,
            left: spotRect.left - pad,
            width: spotRect.width + pad * 2,
            height: spotRect.height + pad * 2,
          }}
          sx={{
            borderRadius: '24px',
            border: `3px solid ${step.color}`,
            boxShadow: `0 0 24px ${step.color}66`,
            zIndex: 9999,
            pointerEvents: 'none',
            transition: 'all .4s ease',
            animation: 'tourPulse 2s ease-in-out infinite',
            '@keyframes tourPulse': { '0%,100%': { boxShadow: `0 0 24px ${step.color}66` }, '50%': { boxShadow: `0 0 40px ${step.color}99` } },
          }} />
      )}

      {/* Tooltip card */}
      <Box onClick={e => e.stopPropagation()}
        style={tooltipStyle}
        sx={{
          zIndex: 10000,
          background: '#fff',
          borderRadius: 4,
          boxShadow: `0 12px 48px rgba(0,0,0,.25), 0 0 0 1px ${step.color}33`,
          overflow: 'visible',
          animation: 'tourFadeIn .35s ease',
          '@keyframes tourFadeIn': { from: { opacity: 0, transform: 'translateY(8px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
        }}>
        {/* Arrow pointing to element */}
        {hasSpot && (
          <Box
            style={{
              position: 'absolute',
              ...(arrowSide === 'top' ? { top: -8 } : { bottom: -8 }),
              left: arrowLeft,
            }}
            sx={{
              width: 16, height: 16,
              background: step.color,
              transform: 'translateX(-50%) rotate(45deg)',
              borderRadius: 2,
              zIndex: -1,
            }} />
        )}

        {/* Color header */}
        <Box sx={{ background: `linear-gradient(135deg, ${step.color}, ${step.color}bb)`, color: '#fff', py: 1.8, px: 3, textAlign: 'center', borderRadius: '16px 16px 0 0' }}>
          <Typography sx={{ fontSize: '1.8rem', mb: .3 }}>{step.emoji}</Typography>
          <Typography sx={{ fontWeight: 800, fontSize: '1.05rem' }}>{step.title}</Typography>
          <Typography sx={{ fontSize: '.7rem', opacity: .7, mt: .2 }}>
            שלב {tourStep + 1} מתוך {tourSteps.length}
          </Typography>
        </Box>

        {/* Description */}
        <Box sx={{ px: 2.5, pt: 1.8, pb: 1.2 }}>
          <Typography sx={{ fontSize: '.85rem', color: '#475569', lineHeight: 1.85, fontWeight: 500, whiteSpace: 'pre-line', textAlign: 'center' }}>
            {step.desc}
          </Typography>
        </Box>

        {/* Progress dots */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: .6, pb: 1.2 }}>
          {tourSteps.map((_, i) => (
            <Box key={i} sx={{
              width: i === tourStep ? 20 : 7, height: 7, borderRadius: 4,
              background: i === tourStep ? step.color : '#e2e8f0',
              transition: 'all .3s',
            }} />
          ))}
        </Box>

        {/* Buttons */}
        <Box sx={{ px: 2, pb: 2, display: 'flex', gap: 1, justifyContent: 'center' }}>
          <Button size="small" onClick={async (e) => { e.stopPropagation(); if (isLast) setShowTour(false); else await handleTourNext(); }}
            sx={{
              fontWeight: 700, borderRadius: 50, px: 3, fontSize: '.85rem',
              background: `linear-gradient(135deg, ${step.color}, ${step.color}cc)`, color: '#fff',
              boxShadow: `0 3px 12px ${step.color}44`,
              '&:hover': { boxShadow: `0 5px 18px ${step.color}55` },
            }}>
            {isLast ? '🚀 בואו נתחיל!' : (step.nextLabel || 'הבא ←')}
          </Button>
          {!isLast && (
            <Button size="small" onClick={(e) => { e.stopPropagation(); setShowTour(false); }} sx={{
              color: '#94a3b8', fontWeight: 600, borderRadius: 50, px: 2, fontSize: '.75rem', minWidth: 0,
              '&:hover': { background: '#f8fafc' },
            }}>
              דלג ✕
            </Button>
          )}
        </Box>
      </Box>
    </>
  );
})()}

            </div>
        </ThemeProvider>
    );
}

export default App;
